<template>
    <div class="chips-container"
        :class="{ 'drag-over': isDragging, 'chips-bare': inputOff }"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
        v-uiHelp="{ key: attr?.uiHelp || (mode === 'Page' ? 'chips.strip.page' : 'chips.strip.grid') }"
    >
        <!-- IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): комментарий внутри корня — на корневом уровне шаблона он делает компонент фрагментом в отладочной сборке ($el — текст, ⌄ падал на getBoundingClientRect); inputOff — полоса только показывает чипсы (поле ввода, лупа и ⌄ — у хоста: строка поиска MenuPanel) -->
        <!-- IE 2026 09 04 / ODOO-336291 (mobile UX): B-42 — лупа стоит ВНЕ .input-container, её тап не доходил
             до focusInput; теперь это та же цель, что и полоса чипсов -->
        <!-- webscm 2026 11 02 / ODOO-350849 (заказ Андрея: поле «Message content» чата непонятно): поле с другим смыслом, не поиск — attr.uiHelp (ключ объяснения по Alt полосы, лупы и поля ввода) и attr.inputIcon (значок вместо лупы); без них всё как было -->
        <div class="chips-icon" v-if="visibleChips.length === 0 && !inputOff" @click.stop="focusInput($event)" v-uiHelp="{ key: attr?.uiHelp || 'chips.search-icon', state: () => (mode === 'Page' ? 'header' : 'table') }">
            <i :class="['pi', attr?.inputIcon || 'pi-search']"></i>
        </div>

        <div class="input-container" @click="focusInput" @mousedown="onInputContainerMouseDown">
            <!-- IE 2026 10 12 / ODOO-343342 (заказ Игоря: правая кнопка на остальных элементах — рейл / чипс / стол): правая кнопка по чипсу — его форма (то же, что наведение / клик), chipContextMenu -->
            <div v-for="(chip, index) in visibleChips" :key="index"
                :class="['chip', chip.meta?.cssClass || '', { 'chip-selected': selectedIndex === index, 'chip-drag-over': dragOverChipIndex === index }]"
                :draggable="!chip._isVirtual"
                @click.stop="selectChip(index)"
                @dragstart="onChipDragStart(index, $event)"
                @dragover.prevent="onChipDragOver(index)"
                @dragleave="onChipDragLeave(index)"
                @drop.stop="onChipDrop(index, $event)"
                @dragend="onChipDragEnd"
                @mouseover="onChipMouseOver(index)"
                @mouseleave="onChipMouseLeave"
                @contextmenu="chipContextMenu(index, $event)"
                v-uiHelp="{ key: uiHelp_chipHelpKey(chip), state: () => (selectedIndex === index ? 'selected' : 'normal'), vars: () => uiHelp_chipVars(chip) }"
            >
                <div v-if="hoverIndex === index && chip.segment !== 'CHIP-WidgetsPage'" class="chip-hover-popup"><!-- IE 2026 09 15 / ODOO-…: у чипса пейджинга формы нет -->
                    <div class="chip-menu" v-if="getChipMenuTypes(chip)" :ref="`chipMenu-${index}`">
                        <div
                            v-for="(type, id) in getChipMenuTypes(chip)"
                            :key="id"
                            :class="['chip-menu-item', { 'chip-menu-item-active': (chip._raw || chip).segment === id }]"
                            @click.stop="selectChipType(index, id, type)"
                            v-uiHelp="'chips.form.type'"
                        >
                            <i :class="['pi', type.pi]"></i>
                            <span>{{ type.title }}</span>
                        </div>
                    </div>
                    <!-- IE 2026 11 02 / ODOO-349799 (group-header-value) (заказ Игоря, скрин «Конфігурація» по чипу «Вид, Підсистема»): опция чипа группировки «Field and count» — заголовок строки-группы с именем поля
                         и счётчиком («Вид: Довідники (88 елементи)»); выключена (по умолчанию) — только значение («Довідники»). Хранится в самом чипе стола (meta.fieldAndCount), как переключение Group ↔ Hierarchy -->
                    <div class="chip-menu" v-if="groupHeaderOption_show(chip)">
                        <div
                            :class="['chip-menu-item', { 'chip-menu-item-active': groupHeader_fieldAndCount(chip._raw || chip) }]"
                            @click.stop="groupHeaderOption_toggle(index)"
                            v-uiHelp="'chips.form.groupHeader'"
                        >
                            <i :class="['pi', groupHeader_fieldAndCount(chip._raw || chip) ? 'pi-check-square' : 'pi-stop']"></i>
                            <span>{{ t('Field and count') }}</span>
                        </div>
                    </div>
                    <!-- IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): меню модели чипса — строки pmChipRows (архивы модели — папкой «Archive (N)», свежие сверху; выключатель fe.ux.pmArchiveSubmenu) -->
                    <div class="chip-menu" v-if="(chip._raw || chip).segment === 'CHIP-ProcessModel'">
                        <div v-for="r in pmChipRows('pm')" :key="r.key"
                            :class="['chip-menu-item', { 'chip-menu-item-active': r.active, 'chip-menu-folder': r.type === 'folder', 'chip-menu-depth1': r.depth === 1 }]"
                            @click.stop="pmChipRow_click('pm', r)"
                            v-uiHelp="r.type === 'folder' ? 'chips.form.pm.archive' : 'chips.form.pm'"
                        >
                            <i v-if="r.type === 'folder'" :class="['pi', 'chip-menu-fold', r.open ? 'pi-angle-down' : 'pi-angle-right']"></i>
                            <i :class="['pi', r.icon]"></i>
                            <span class="chip-menu-text">{{ r.title }}</span>
                            <span v-if="r.aside" class="chip-menu-aside">{{ r.aside }}</span>
                        </div>
                    </div>
                    <!-- IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): чипс «Δ vs model» — выбор модели сравнения (модели, ветки, архивы); по умолчанию — «From settings» (как было: модель из настроек отчёта / стола / ПМ);
                         выбор — параметр отчёта prev_process_model (тот же, что Compare → Previous model), форма помечается изменённой, отчёт перечитывается; выключатель fe.ux.compareModelPicker -->
                    <div class="chip-menu" v-if="chip.segment === 'CHIP-CompareModel' && cmpPickerOn">
                        <div class="chip-menu-sub">{{ t('Compare with') }}</div>
                        <div v-for="r in pmChipRows('cmp')" :key="r.key"
                            :class="['chip-menu-item', { 'chip-menu-item-active': r.active, 'chip-menu-folder': r.type === 'folder', 'chip-menu-depth1': r.depth === 1 }]"
                            @click.stop="pmChipRow_click('cmp', r)"
                            v-uiHelp="r.type === 'folder' ? 'chips.form.pm.archive' : (r.id ? 'chips.form.compare.model' : 'chips.form.compare.default')"
                        >
                            <i v-if="r.type === 'folder'" :class="['pi', 'chip-menu-fold', r.open ? 'pi-angle-down' : 'pi-angle-right']"></i>
                            <i :class="['pi', r.icon]"></i>
                            <span class="chip-menu-text">{{ r.title }}</span>
                            <span v-if="r.aside" class="chip-menu-aside">{{ r.aside }}</span>
                        </div>
                    </div>
                    <!-- IE 2026 09 15 / ODOO-… (заказ Игоря): чипс периода — быстрая правка границ «с … по» (применяется сразу; поле в фокусе держит форму) -->
                    <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                    <div v-if="mode === 'Page' && chipPeriod(chip)" class="chip-period" @click.stop>
                        <label class="chip-period-lbl" v-uiHelp="'chips.form.period.from'">{{ t('From') }}<input type="date" class="chip-pick-input chip-period-input" :value="chipPeriod(chip).from" @change="chipPeriod_change(chip, 'from', $event.target.value)"></label>
                        <label class="chip-period-lbl" v-uiHelp="'chips.form.period.to'">{{ t('To') }}<input type="date" class="chip-pick-input chip-period-input" :value="chipPeriod(chip).to" @change="chipPeriod_change(chip, 'to', $event.target.value)"></label>
                    </div>
                    <div :class="['chip-tooltip', { 'chip-tooltip-clip': chipClipOn, 'chip-tooltip-metric': metricChip_is(chip) || formatChip_is(chip) }]" @mouseover="chipTip_over" @mouseleave="chipTip_hide" @scroll.capture.passive="chipTip_hide" v-uiHelp="'chips.form'"><!-- IE 2026 09 14 / ODOO-5800 (замечание Игоря): обрезка длинных названий + «продолжение» при наведении (chipTip_over) -->
                        <!-- IE 2026 09 09 / ODOO-5800 (заказ Игоря): у чипса с несколькими значениями — чекбокс перед каждым;
                             снятие флага убирает значение из отбора и сразу применяет чипсы (виджеты обновляются),
                             список в открытом поповере не меняется — при следующем открытии остаются только отмеченные -->
                        <!-- IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): чипс метрик списка (CHIP-Metric) — выбранные значения с чекбоксами: снять флаг =
                             убрать значение и сразу применить отбор; щелчок по названию / Alt — оставить только это значение -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <template v-if="metricChip_is(chip)">
                            <div class="chip-metric-head" v-uiHelp="'chips.form.metric.head'"><b>{{ t('Metrics') }}</b><span class="chip-metric-hint">{{ metricChip_ruleHint(chip) }}</span></div>
                            <!-- IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): зеркало глобального чипса над таблицей — отбор задаётся в шапке стола -->
                            <div v-if="chip._mirror" class="chip-metric-hint chip-metric-hint-row" v-uiHelp="'chips.form.metric.mirror-hint'">{{ t('Workspace metrics chip shown above this table — change the selection in the header of the workspace. Metrics of this table under the selection: {known}', { known: chip.known }) }}</div>
                            <!-- IE 2026 10 04 / ODOO-5800 (fe-76): комбинация / горизонт чипса шапки, которых словарь этой таблицы не знает, сопоставлены по основе сигнала (metricChip_localize) -->
                            <div v-if="chip._mirror && chip.fallback" class="chip-metric-hint chip-metric-hint-row chip-metric-fallback">≈ {{ t('Matched by signal: the horizon differs at this level') }}</div>
                            <!-- IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): чипс шапки стола режет выборку отчётов на входе -->
                            <div v-if="chip._global" class="chip-metric-hint chip-metric-hint-row" v-uiHelp="'chips.form.metric.global-hint'">{{ t('Workspace filter: reports take only the calculation nodes that carry these metrics') }}</div>
                            <div v-for="v in metricChip_selected(chip)" :key="v.kind + '|' + v.id" class="chip-value-row"
                                @click.stop="metricChip_toggle(index, chip, v.kind, v.id, $event, true)"
                                v-uiHelp="{ key: 'chips.form.metric.selected', state: () => (chip._mirror ? 'readonly' : 'edit') }"
                            >
                                <i class="pi chip-value-check pi-check-square" @click.stop="metricChip_toggle(index, chip, v.kind, v.id, $event)" v-uiHelp="{ key: 'chips.form.metric.selected-check', state: () => (chip._mirror ? 'readonly' : 'edit') }" />
                                <span v-if="v.sample" class="chip-metric-swatch" :style="v.sample" v-uiHelp="'chips.form.swatch'"></span>
                                <span class="chip-clip" :title="v.tip || null">{{ v.title }}</span><!-- IE 2026 10 04 / ODOO-5800 (fe-74): исходные названия метрик сигнала — в подсказке -->
                                <span class="chip-metric-kind" v-uiHelp="'chips.form.kind'">{{ v.kindTitle }}</span>
                            </div>
                            <div v-if="!metricChip_selected(chip).length" class="chip-pick-empty" v-uiHelp="'chips.form.empty'">{{ chip._global ? t('Nothing selected — the workspace is not filtered by metrics') : t('Nothing selected — all rows are shown') }}</div>
                        </template>
                        <!-- IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): чипс условного оформления — выбранные правила с
                             чекбоксами: снять флаг = убрать правило и сразу применить отбор; щелчок по названию / Alt — оставить только это правило -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <template v-else-if="formatChip_is(chip)">
                            <div class="chip-metric-head"><b>{{ t('Formats') }}</b><span class="chip-metric-hint">{{ t('rows and cells painted by the selected formatting rules') }}</span></div>
                            <div v-for="v in formatChip_selected(chip)" :key="v.id" :class="['chip-value-row', { 'chip-value-off': !v.known }]"
                                @click.stop="formatChip_toggle(index, chip, v.id, $event, true)"
                                v-uiHelp="'chips.form.format.selected'"
                            >
                                <i class="pi chip-value-check pi-check-square" @click.stop="formatChip_toggle(index, chip, v.id, $event)" v-uiHelp="'chips.form.format.selected-check'" />
                                <span v-if="v.sample" class="chip-metric-swatch" :style="v.sample" v-uiHelp="'chips.form.swatch'"></span>
                                <span class="chip-clip">{{ v.title }}</span>
                                <span v-if="!v.known" class="chip-metric-kind" v-uiHelp="'chips.form.format.unknown'">{{ t('not among the rules with Selector') }}</span>
                            </div>
                            <div v-if="!formatChip_selected(chip).length" class="chip-pick-empty">{{ t('Nothing selected — all rows are shown') }}</div>
                        </template>
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <template v-else-if="chipValueRows(chip, index).length > 1">
                            <div v-if="getTooltipHTML(chip, { headerOnly: true })" v-html="getTooltipHTML(chip, { headerOnly: true })" v-uiHelp="'chips.form.condition'" />
                            <!-- IE 2026 09 09 / ODOO-5800 (уточнение Игоря): щелчок по названию — только это значение;
                                 чекбокс — добавить/убрать (Alt+щелчок по чекбоксу — тоже только это) -->
                            <div v-for="v in chipValueRows(chip, index)" :key="v.key"
                                :class="['chip-value-row', { 'chip-value-off': !v.on }]"
                                @click.stop="onChipValueTitleClick(index, chip, v, $event)"
                                v-uiHelp="'chips.form.value.row'"
                            >
                                <i :class="['pi', 'chip-value-check', v.on ? 'pi-check-square' : 'pi-stop']"
                                    @click.stop="toggleChipValue(index, chip, v, $event)"
                                    v-uiHelp="{ key: 'chips.form.value.check', state: () => (v.on ? 'on' : 'off') }" />
                                <!-- IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): иконка сегмента между чекбоксом и названием — открыть элемент -->
                                <i v-if="chipPick_segment(chip)" :class="['pi', 'chip-item-open', chipPick_itemPi(chip, v)]"
                                    :title="t('Open {title}', { title: v.title })" @click.stop="chipPick_openItem(chip, v)" v-uiHelp="'chips.form.open'" />
                                <span class="chip-clip">{{ v.title }}</span>
                            </div>
                        </template>
                        <!-- IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса) (замечание Игоря: «когда один элемент выбран — не хватает иконки для его открытия»):
                             одно ссылочное значение — заголовок + строка значения с иконкой сегмента (без чекбокса: снимать нечего) -->
                        <template v-else-if="chipSingleValue(chip)">
                            <div v-if="getTooltipHTML(chip, { headerOnly: true })" v-html="getTooltipHTML(chip, { headerOnly: true })" v-uiHelp="'chips.form.condition'" />
                            <div class="chip-value-row chip-value-single" v-uiHelp="'chips.form.value.single'">
                                <i :class="['pi', 'chip-item-open', chipPick_itemPi(chip, chipSingleValue(chip))]"
                                    :title="t('Open {title}', { title: chipSingleValue(chip).title })" @click.stop="chipPick_openItem(chip, chipSingleValue(chip))" v-uiHelp="'chips.form.open'" />
                                <span class="chip-clip">{{ chipSingleValue(chip).title }}</span>
                            </div>
                        </template>
                        <div v-else-if="getTooltipHTML(chip)" v-html="getTooltipHTML(chip)" v-uiHelp="'chips.form.condition'" @click="apprLink_click" /><!-- IE 2026 10 10 / ODOO-345618 FE1: ссылка подсказки чипса -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div :class="['chip-tooltip-actions', { 'has-content': !!getTooltipHTML(chip) || metricChip_is(chip) || formatChip_is(chip) }]">
                            <span v-if="!apprChip_is(chip)" class="chip-tooltip-index" v-uiHelp="'chips.form.index'">#{{ index + 1 }}</span><!-- IE 2026 10 11 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE2 (UX1 S-06): у чипса сводки Approval номер ничего не значит -->
                            <button class="chip-copy-btn" @click.stop="copyChip(index)" v-uiHelp="'chips.form.copy'"><i class="pi pi-copy"></i> {{ t('Copy') }}</button>
                            <button class="chip-copy-btn" @click.stop="downloadChip(index)" v-uiHelp="'chips.form.download'"><i class="pi pi-download"></i> {{ t('Download') }}</button>
                            <!-- открыть виджет селектора — та же команда, что dblclick
                                 по тексту чипса (editChip, ветка CHIP-Selector) -->
                            <button v-if="chip.segment === 'CHIP-Selector'"
                                class="chip-copy-btn" @click.stop="openChipSelector(index)" v-uiHelp="'chips.form.open-selector'"
                            ><i class="pi pi-external-link"></i> {{ t('Open') }}</button>
                            <!-- IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): отчёт метрик, через который резолвится чипс -->
                            <button v-if="chip._global" class="chip-copy-btn" :title="t('Open the metrics report')" @click.stop="metricChipG_openReport(chip)" v-uiHelp="'chips.form.open-report'"
                            ><i class="pi pi-external-link"></i> {{ t('Open') }}</button>
                        </div>
                        <!-- у ВХОДНОГО чипса поиска строки и так не прячутся (отбор сделал бэк) — флажок не нужен -->
                        <div v-if="chipNav_on(chip) && !chipPlace_input(chip)" class="chip-check-row" @click.stop="toggleShowAllLines(index)" v-uiHelp="{ key: 'chips.form.show-all-rows', state: () => ((chip._raw || chip).showAllLines ? 'on' : 'off') }">
                            <i :class="['pi', (chip._raw || chip).showAllLines ? 'pi-check-square' : 'pi-stop']" />
                            <span>{{ t('Show rows without matches') }}</span>
                        </div>
                        <!-- IE 2026 09 25 / ODOO-… (замечание Игоря: поиск в таблице по названиям на других языках): подпись «Matches in English names» (язык словами, если один; иначе other-language) -->
                        <div v-if="chipAltOn(chip)" class="chip-alt-note" :title="chipAltTitle(chip)" v-uiHelp="{ key: 'chips.form.other-lang', state: () => String((chip._raw || chip)._alt || '') }"><i class="pi pi-language"></i> {{ chipAltLabel(chip) }}</div>
                        <!-- IE 2026 09 19 / ODOO-5800 (Igor: «добавь еще рядом флаг "показать другие форматирования" … без флага будут показаны только форматирования по тек; и тоже по умолчанию вкл») -->
                        <div v-if="metricFmtFlag_show(chip)" class="chip-check-row" @click.stop="toggleShowOtherFormats(index)" v-uiHelp="{ key: 'chips.form.show-other-formats', state: () => ((chip._raw || chip).showOtherFormats !== false ? 'on' : 'off') }">
                            <i :class="['pi', (chip._raw || chip).showOtherFormats !== false ? 'pi-check-square' : 'pi-stop']" />
                            <span>{{ t('Show other formatting') }}</span>
                        </div>
                        <!-- IE 2026 10 07 / ODOO-5800 (Igor 07.10: «добавь в чипсе флаг о том что нужно приглушить другие оформления … по умолчанию пускай он будет выключенным»): приглушение ячеек вне отбора чипса сигналов — только с флагом (нет поля = выключен) -->
                        <div v-if="metricDimFlag_show(chip)" class="chip-check-row" @click.stop="toggleDimOtherFormats(index)" v-uiHelp="{ key: 'chips.form.dim-other-formats', state: () => ((chip._raw || chip).dimOtherFormats === true ? 'on' : 'off') }">
                            <i :class="['pi', (chip._raw || chip).dimOtherFormats === true ? 'pi-check-square' : 'pi-stop']" />
                            <span>{{ t('Dim other formatting') }}</span>
                        </div>
                        <!-- IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): место действия чипса — Table (по загруженным строкам) | Input (бэк режет выборку) -->
                        <div v-if="chipPlace_show(chip)" class="chip-place-row" @click.stop v-uiHelp="{ key: 'chips.form.place', state: () => chipPlace_get(chip) }">
                            <span class="chip-place-label">{{ t('Acts on') }}</span>
                            <span v-for="p in chipPlace_places()" :key="p.id" :title="p.hint"
                                :class="['chip-place-opt', { 'chip-place-opt-on': chipPlace_get(chip) === p.id }]"
                                @click.stop="chipPlace_set(index, p.id)"
                                v-uiHelp="{ key: 'chips.form.place.' + p.id, state: () => (chipPlace_get(chip) === p.id ? 'current' : 'other') }"
                            >{{ p.title }}</span>
                        </div>
                        <div v-if="chipPlace_notes(chip).length" class="chip-place-notes" v-uiHelp="'chips.form.place.notes'">
                            <div v-for="(note, ni) in chipPlace_notes(chip)" :key="ni"><i class="pi pi-exclamation-triangle" /> {{ note }}</div>
                        </div>
                        <!-- подбор значения на место текущего (как в ячеечном dropdown):
                             поиск по словарю сегмента чипса, клик — заменить значение -->
                        <!-- IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): поиск и три списка выбора из словаря метрик виджета (attr.metric_rules):
                             Grade, Metric и Grade + Metric + Reason; чекбокс — добавить / убрать, название / Alt — только это значение -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div v-if="metricChip_is(chip) && !chip._mirror" class="chip-pick chip-metric-pick">
                            <div class="chip-pick-input-row">
                                <i class="pi pi-search"></i>
                                <input class="chip-pick-input" :placeholder="t('Find a grade, a metric or a reason...')" v-model="metricPickText" v-uiHelp="'chips.form.metric.search'"
                                    @click.stop @mousedown.stop @keydown.stop
                                    @keydown.esc.stop="metricPickText ? (metricPickText = '') : chipPick_close()"
                                    @blur="onChipPickBlur"
                                />
                            </div>
                            <div class="chip-pick-list chip-metric-list">
                                <template v-for="grp in metricChip_groups(chip)" :key="grp.kind">
                                    <div class="chip-history-title chip-metric-group" v-uiHelp="{ key: 'chips.form.metric.group', state: () => grp.kind }">{{ grp.title }}</div>
                                    <div v-for="it in grp.items" :key="grp.kind + '|' + it.id"
                                        :class="['chip-pick-item', { 'chip-pick-current': it.on }]" :title="it.tip || null"
                                        @click.stop="metricChip_toggle(index, chip, grp.kind, it.ids || it.id, $event, true)"
                                        v-uiHelp="'chips.form.metric.item'"
                                    >
                                        <i :class="['pi', 'chip-value-check', it.on ? 'pi-check-square' : 'pi-stop']"
                                            @click.stop="metricChip_toggle(index, chip, grp.kind, it.ids || it.id, $event)" v-uiHelp="{ key: 'chips.form.metric.check', state: () => (it.on ? 'on' : 'off') }"></i>
                                        <span v-if="it.sample" class="chip-metric-swatch" :style="it.sample" v-uiHelp="'chips.form.swatch'"></span>
                                        <span class="chip-clip" v-html="it.titleHTML"></span>
                                        <span class="chip-metric-rows" v-uiHelp="{ key: 'chips.form.metric.count', state: () => ((chip._global ? 'global-' : 'table-') + (grp.kind === 'combos' ? 'combos' : 'other')) }">{{ it.count }}</span>
                                    </div>
                                    <div v-if="!grp.items.length" class="chip-pick-empty">{{ chip._global && metricG.loading ? t('Loading metrics...') : t('No matches') }}</div>
                                </template>
                            </div>
                        </div>
                        <!-- IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): поиск и список правил словаря (attr.format_rules):
                             образец стиля, название, счётчики «строк · ячеек»; чекбокс — добавить / убрать, название / Alt — только это правило -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div v-if="formatChip_is(chip)" class="chip-pick chip-metric-pick">
                            <div class="chip-pick-input-row">
                                <i class="pi pi-search"></i>
                                <input class="chip-pick-input" :placeholder="t('Find a formatting rule...')" v-model="formatPickText" v-uiHelp="'chips.form.format.search'"
                                    @click.stop @mousedown.stop @keydown.stop
                                    @keydown.esc.stop="formatPickText ? (formatPickText = '') : chipPick_close()"
                                    @blur="onChipPickBlur"
                                />
                            </div>
                            <div class="chip-pick-list chip-metric-list">
                                <div class="chip-history-title chip-metric-group" v-uiHelp="'chips.form.format.group'">{{ t('Formatting rules with Selector') }}</div>
                                <div v-for="it in formatChip_list(chip)" :key="it.id"
                                    :class="['chip-pick-item', { 'chip-pick-current': it.on }]"
                                    @click.stop="formatChip_toggle(index, chip, it.id, $event, true)"
                                    v-uiHelp="'chips.form.format.item'"
                                >
                                    <i :class="['pi', 'chip-value-check', it.on ? 'pi-check-square' : 'pi-stop']"
                                        @click.stop="formatChip_toggle(index, chip, it.id, $event)" v-uiHelp="{ key: 'chips.form.format.check', state: () => (it.on ? 'on' : 'off') }"></i>
                                    <span v-if="it.sample" class="chip-metric-swatch" :style="it.sample" v-uiHelp="'chips.form.swatch'"></span>
                                    <span class="chip-clip" v-html="it.titleHTML"></span>
                                    <span class="chip-metric-rows" v-uiHelp="'chips.form.format.count'">{{ it.count }}</span>
                                </div>
                                <div v-if="!formatChip_list(chip).length" class="chip-pick-empty">{{ t('No matches') }}</div>
                            </div>
                        </div>
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div v-if="mode === 'Page' && !metricChip_is(chip) && chipPick_segment(chip)" class="chip-pick">
                            <div class="chip-pick-input-row">
                                <i class="pi pi-search"></i>
                                <input
                                    class="chip-pick-input"
                                    :placeholder="t('Pick another value...')"
                                    v-model="chipPickText"
                                    v-uiHelp="'chips.form.pick.input'"
                                    @focus="chipPick_load(chip)"
                                    @click.stop
                                    @mousedown.stop
                                    @keydown.stop
                                    @keydown.enter.stop="chipPick_applyFirst(index, chip)"
                                    @keydown.esc.stop="chipPickText ? (chipPickText = '') : chipPick_close()"
                                    @blur="onChipPickBlur"
                                /><!-- IE 2026 09 12 / ODOO-…: Esc в пустом поле закрывает форму; blur — см. onChipPickBlur -->
                            </div>
                            <div class="chip-pick-list" v-if="chipPickFocused || chipPickText">
                                <!-- IE 2026 09 30 / ODOO-5800 (Igor: «если при подборе не найдены строки, то попробовать расширить поиск, ища под подсегментом, а не под виджетом
                                     селектора; при этом сообщай об этом»): в строках виджета-селектора совпадений нет — список ниже найден по всему каталогу подсегмента грани -->
                                <div v-if="chipPickResult.wide && chipPickItems(chip).length" class="chip-pick-wide" v-uiHelp="'chips.form.pick.wide'"
                                    :title="t('The selector widget, filtered by the other chips, has no values matching the text or only looser ones, so the list shows the same search over the whole catalog of this field without the other chips.')"
                                ><i class="pi pi-search-plus"></i>{{ t('No matches in the current selection — showing matches from the whole catalog “{catalog}”', { catalog: chipPick_wideTitle(chipPickResult.wide) }) }}</div><!-- IE 2026 09 30 / ODOO-5800 -->
                                <!-- IE 2026 09 09 / ODOO-5800 (заказ Игоря): чекбокс = добавить/убрать значение к текущему отбору,
                                     щелчок по тайтлу или Alt+щелчок = только это значение (как в виджетах-селекторах) -->
                                <div v-for="it in chipPickItems(chip)" :key="it.id"
                                    :class="['chip-pick-item', { 'chip-pick-current': chipPick_isCurrent(chip, it) }]"
                                    @click.stop="onChipPickTitleClick(index, chip, it, $event)"
                                    v-uiHelp="'chips.form.pick.item'"
                                >
                                    <i :class="['pi', 'chip-value-check', chipPick_isCurrent(chip, it) ? 'pi-check-square' : 'pi-stop']"
                                        @click.stop="onChipPickCheckClick(index, chip, it, $event)" v-uiHelp="{ key: 'chips.form.pick.check', state: () => (chipPick_isCurrent(chip, it) ? 'on' : 'off') }"></i>
                                    <!-- IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): иконка сегмента — открыть элемент списка -->
                                    <i :class="['pi', 'chip-item-open', chipPick_itemPi(chip, it)]"
                                        :title="t('Open {title}', { title: it.title })" @click.stop="chipPick_openItem(chip, it)" v-uiHelp="'chips.form.open'" />
                                    <span class="chip-clip" v-html="chipPick_titleHTML(it)"></span>
                                </div>
                                <div v-if="chipPickLoading(chip)" class="chip-pick-empty" v-uiHelp="'chips.form.pick.status'">{{ t('Loading values...') }}</div>
                                <div v-else-if="chipPick_widgetLoading(chip) && !chipPickItems(chip).length" class="chip-pick-empty" v-uiHelp="'chips.form.pick.status'">{{ t('Loading the selector widget...') }}</div><!-- IE 2026 09 22 / ODOO-5800 (Igor): список — из виджета-селектора, словарь не подставляем -->
                                <div v-else-if="chipPickResult.pending && !chipPickItems(chip).length" class="chip-pick-empty" v-uiHelp="'chips.form.pick.status'">{{ t('Searching...') }}</div><!-- IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска) -->
                                <div v-else-if="!chipPickItems(chip).length" class="chip-pick-empty" v-uiHelp="'chips.form.pick.status'">{{ t('No matches') }}</div>
                                <div v-else-if="chipPickConv || chipPickFuzzy" class="chip-pick-conv" :class="{ 'chip-pick-conv-fuzzy': chipPickFuzzy, 'chip-pick-conv-extra': chipPick_isExtra(chipPickFuzzy) }" v-uiHelp="{ key: 'chips.form.pick.conv', state: () => (chipPick_isExtra(chipPickFuzzy) ? 'extra' : (chipPickConv && chipPickFuzzy ? 'both' : (chipPickFuzzy ? 'fuzzy' : 'layout'))) }" :title="chipPick_isExtra(chipPickFuzzy) ? t('Nothing matches the text as typed — {what}', { what: chipPick_fuzzyText(chipPickFuzzy) }) : (chipPickFuzzy ? t('Nothing matches exactly — showing values that differ by one or two letters (typos allowed)') : t('The text looks typed in another keyboard layout — showing matches for the converted text'))">{{ chipPickConv ? (chipPickFuzzy ? t('Approximate matches for “{conv}”', { conv: chipPickConv }) : t('Matches for “{conv}”', { conv: chipPickConv })) : (chipPickFuzzy ? t('Approximate matches') : t('Matches')) }}{{ chipPickFuzzy ? ' (' + chipPick_fuzzyText(chipPickFuzzy) + ')' : '' }}</div><!-- IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): последняя ступень (7 / 8) — состояние extra, своя подсказка и перенос подписи (chip-pick-conv-extra) --><!-- IE 2026 09 10 / ODOO-…; IE 2026 09 12 / ODOO-…: режим опечаток; IE 2026 09 23 / ODOO-341407: ревью i18n — подпись целой фразой (ключ общий с MenuPanel), не склейкой «Matches» + «for …» -->
                            </div>
                        </div>
                        <!-- история значений этого чипса (per пользователь+база, localStorage);
                             клик — применить прошлый отбор. Тот же блок тултипа, разделитель линией -->
                        <!-- IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): история отборов этого чипса (пользователь + база + виджет, localStorage) -->
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <div v-if="metricChip_is(chip) && !chip._mirror && metricChip_history(chip).length" class="chip-history">
                            <div class="chip-history-title" v-uiHelp="'chips.form.recent'">{{ t('Recent') }}</div>
                            <div v-for="h in metricChip_history(chip)" :key="h.sig"
                                :class="['chip-history-item', { 'chip-history-current': h.current }]"
                                @click.stop="metricChip_historyApply(index, chip, h, $event, true)"
                                v-uiHelp="'chips.form.recent.metric.item'"
                            >
                                <i :class="['pi', 'chip-value-check', h.current ? 'pi-check-square' : 'pi-stop']"
                                    @click.stop="metricChip_historyApply(index, chip, h, $event)" v-uiHelp="{ key: 'chips.form.recent.metric.check', state: () => (h.current ? 'current' : 'other') }"></i>
                                <span class="chip-clip">{{ h.title }}</span>
                            </div>
                        </div>
                        <!-- IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): история отборов чипса оформления (пользователь + база + виджет) -->
                        <div v-if="formatChip_is(chip) && formatChip_history(chip).length" class="chip-history">
                            <div class="chip-history-title" v-uiHelp="'chips.form.recent'">{{ t('Recent') }}</div>
                            <div v-for="h in formatChip_history(chip)" :key="h.sig"
                                :class="['chip-history-item', { 'chip-history-current': h.current }]"
                                @click.stop="formatChip_historyApply(index, chip, h, $event, true)"
                                v-uiHelp="'chips.form.recent.format.item'"
                            >
                                <i :class="['pi', 'chip-value-check', h.current ? 'pi-check-square' : 'pi-stop']"
                                    @click.stop="formatChip_historyApply(index, chip, h, $event)" v-uiHelp="{ key: 'chips.form.recent.format.check', state: () => (h.current ? 'current' : 'other') }"></i>
                                <span class="chip-clip">{{ h.title }}</span>
                            </div>
                        </div>
                        <div v-if="mode === 'Page' && !metricChip_is(chip) && chipHistoryItems(chip).length" class="chip-history">
                            <div class="chip-history-title" v-uiHelp="'chips.form.recent'">{{ t('Recent') }}</div>
                            <div v-for="h in chipHistoryItems(chip)" :key="h.sig"
                                :class="['chip-history-item', { 'chip-history-current': chipHistoryIsCurrent(chip, h) }]"
                                @click.stop="onChipHistoryTitleClick(index, h, $event)"
                                v-uiHelp="'chips.form.recent.selector.item'"
                            >
                                <!-- IE 2026 09 09 / ODOO-5800: чекбокс — добавить значения записи истории к текущему отбору;
                                     тайтл / Alt+щелчок — применить запись целиком (как раньше) -->
                                <i :class="['pi', 'chip-value-check', chipHistoryIsCurrent(chip, h) ? 'pi-check-square' : 'pi-stop']"
                                    @click.stop="onChipHistoryCheckClick(index, chip, h, $event)" v-uiHelp="{ key: 'chips.form.recent.selector.check', state: () => (chipHistoryIsCurrent(chip, h) ? 'current' : 'other') }"></i>
                                <!-- IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): запись истории с одним значением — иконка сегмента открывает элемент; с несколькими — нет иконки -->
                                <i v-if="chipHistory_single(h)" :class="['pi', 'chip-item-open', chipPick_itemPi(chip, chipHistory_single(h), chipHistory_single(h).relation_segment)]"
                                    :title="t('Open {title}', { title: h.title })" @click.stop="chipPick_openItem(chip, chipHistory_single(h), chipHistory_single(h).relation_segment)" v-uiHelp="'chips.form.open'" />
                                <span class="chip-clip">{{ h.title }}</span><!-- IE 2026 09 14 / ODOO-5800: полный текст, обрезка — CSS -->
                            </div>
                        </div>
                    </div>
                </div>
                <!-- IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): у чипса «Regimen paused until …» (doc._schedule) значок и текст в справке по Alt — состояние status («только сообщает о состоянии»), а не «у фильтра нет виджета-селектора» -->
                <span :class="['chip-icon', 'pi', hoverIndex === index ? 'pi-cog' : chip.meta?.pi]"
                    @click.stop="toggleChipMenu(index, $event)"
                    v-uiHelp="{ key: 'chips.chip-icon', state: () => (chip._mirror ? 'mirror' : chip._global ? 'metric-global' : mode === 'Page' ? (chip.doc?.widget ? 'header-widget' : (chip.doc?._schedule ? 'status' : 'header-none')) : 'table') }"
                />

                <span v-if="!chip.isEditing" class="chip-text" @dblclick="editChip(index)"
                    v-html="getChipTitleHTML(chip)"
                    v-uiHelp="{ key: 'chips.chip-text', state: () => (['selector', 'period'].includes(uiHelp_chipKind(chip)) && chip.doc?.widget ? 'open' : (['search', 'style'].includes(uiHelp_chipKind(chip)) ? 'edit' : (uiHelp_chipKind(chip) === 'schedule-paused' ? 'status' : 'none'))) }"
                />
                <input v-else
                    v-model="chip.title"
                    ref="chipInputs"
                    class="chip-input"
                    v-uiHelp="'chips.chip-edit'"
                    @blur="saveChip(index)"
                    @keydown.enter="saveChip(index)"
                />
                <!-- IE 2026 08 28 / ODOO-5828 (заказ Игоря): значение чипса вне периметра расчёта текущей версии
                     (нет ни одного документа — например, двойник каталога из глобального поиска) -->
                <!-- IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): входной чипс виден без наведения; предупреждения бэка (record._doc.chips_input) — знаком на чипсе -->
                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                <i v-if="chipPlace_input(chip)" class="pi pi-sign-in chip-place-ico" v-uiHelp="'chips.badge.input'" :title="t('Input chip: restricts the selection on the server')" />
                <span v-if="chipPlace_notes(chip).length" class="chip-badge chip-badge-warn chip-badge-place-notes" v-uiHelp="'chips.badge.notes'" :title="chipPlace_notes(chip).join('\n')">⚠</span>
                <span v-if="(chip._raw || chip).out_of_scope?.length" class="chip-badge chip-badge-warn chip-badge-scope" v-uiHelp="'chips.badge.scope'"
                    :title="tn((chip._raw || chip).out_of_scope.length, 'No calculation documents in the current version for {n} selected value — possibly a catalog duplicate. Widgets filtered by this chip show empty results.', 'No calculation documents in the current version for {n} selected values — possibly a catalog duplicate. Widgets filtered by this chip show empty results.')"
                >⚠</span>
                <span v-if="['CHIP-Rows', 'CHIP-Cell', 'CHIP-CellFilters'].includes((chip._raw || chip).segment) && (chip._raw || chip).data?.length"
                    class="chip-badge"
                    v-uiHelp="'chips.badge.cells'"
                >{{ (chip._raw || chip).data.length }}</span>
                <template v-if="chip.segment === 'CHIP-Limit' && chip.doc?.limit">
                    <i :class="['chip-nav', 'pi', 'pi-chevron-left', {'chip-nav-disabled': !chipLimit_canPrev(chip)}]"
                        @click.stop="chipLimit_prev(index)"
                        v-uiHelp="{ key: 'chips.nav.limit.prev', state: () => (chipLimit_canPrev(chip) ? 'ok' : 'edge') }"
                    />
                    <i :class="['chip-nav', 'pi', 'pi-chevron-right', {'chip-nav-disabled': !chipLimit_canNext(chip)}]"
                        @click.stop="chipLimit_next(index)"
                        v-uiHelp="{ key: 'chips.nav.limit.next', state: () => (chipLimit_canNext(chip) ? 'ok' : 'edge') }"
                    />
                </template>
                <!-- задача 14 ч.1: счётчик найденных + ←/→ переход между совпадениями
                     (matchCount/matchIndex проставляет хост; грид их не ставит — блок скрыт) -->
                <span v-if="(chip._raw || chip).segment === 'CHIP-Search' && ((chip._raw || chip)._fuzzy || chipExtraOn(chip))" class="chip-fuzzy" v-uiHelp="{ key: 'chips.badge.fuzzy', state: () => String(chipExtraOn(chip) || (chip._raw || chip)._fuzzy || '') }" :title="chipFuzzyTitle(chip)">≈</span><!-- IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): и чипс, найденный последней ступенью (_extra) --><!-- IE 2026 09 12 / ODOO-… -->
                <!-- IE 2026 09 21 / ODOO-… (замечание Игоря: фраза поиска целиком главнее частей): чипс нашёл фразу целиком — отдельные слова не ищутся -->
                <span v-if="chipPhraseOn(chip)" class="chip-phrase" v-uiHelp="'chips.badge.phrase'" :title="t('The whole phrase is found — its words are not searched separately')">“ ”</span>
                <!-- IE 2026 09 25 / ODOO-… (замечание Игоря: поиск в таблице по названиям на других языках): чипс нашёл строки по названиям записей на другом языке (решение грида — AttrGrid.searchAlt_decide) — значок языка -->
                <span v-if="chipAltOn(chip)" class="chip-alt" v-uiHelp="{ key: 'chips.badge.otherLang', state: () => String((chip._raw || chip)._alt || '') }" :title="chipAltTitle(chip)"><i class="pi pi-language"></i>{{ chipAltBadge(chip) }}</span>
                <template v-if="chipNav_on(chip) && chipMatch_show(chip).badge"><!-- IE 2026 10 05 / ODOO-5800 (fe-82) -->
                    <!-- единственное совпадение — ни бейджа, ни стрелок (навигировать некуда,
                         «1/1» только шумит); 0 — показываем бейдж как сигнал «не найдено» -->
                    <!-- IE 2026 10 05 / ODOO-5800 (fe-82) (Igor 05.10: «ожидал, что чипс селектора будет иметь переход и ходить по … ячейкам»): у чипса метрик
                         единственная остановка показывается — «1/1» и стрелки ведут к ячейке сигнала (chipMatch_show, fe.ux.metricNavSingle) -->
                    <span class="chip-badge chip-badge-matches" v-uiHelp="{ key: 'chips.badge.matches', state: () => ((chip._raw || chip).matchCount === 0 ? 'zero' : ((chip._raw || chip).segment === 'CHIP-Metric' ? 'metric' : (chip._raw || chip).segment === 'CHIP-Format' ? 'format' : 'search')), vars: () => chipMore_vars(chip) }"><span class="chip-badge-count">{{ chipMatch_text(chip) }}</span><span v-if="chipMore_text(chip)" class="chip-badge-more">{{ chipMore_text(chip) }}</span></span><!-- IE 2026 10 05 / ODOO-5800 (fe-86): хвост «+N» — ячейки сигнала в строках выключенных опций отчёта (chipMore_text), строка о них в подсказке счётчика (chipMore_vars) --><!-- IE 2026 10 05 / ODOO-5800 (fe-88): текст счётчика — chipMatch_text (у чипса метрик до первого перехода ‹ › — только число остановок) -->
                    <template v-if="chipMatch_show(chip).arrows"><!-- IE 2026 10 05 / ODOO-5800 (fe-82) -->
                        <i :class="['chip-nav', 'pi', 'pi-chevron-left']"
                            @click.stop="$emit('chip-search-nav', { index, dir: -1 })"
                            v-uiHelp="'chips.nav.matches.prev'"
                        />
                        <i :class="['chip-nav', 'pi', 'pi-chevron-right']"
                            @click.stop="$emit('chip-search-nav', { index, dir: 1 })"
                            v-uiHelp="'chips.nav.matches.next'"
                        />
                    </template>
                </template>
                <!-- IE 2026 10 07 / ODOO-5800 (Igor 05.10: «когда чип поиска находит одно значение, то нужна кнопка ">", по которой будет произведён переход к найденной строке или ячейке»):
                     ровно одно совпадение — одна кнопка › на месте «следующего», то же событие chip-search-nav (dir: 1 при одной остановке ведёт к ней же); ≥ 2 — пара ‹ › выше, 0 — кнопок нет (chipMatch_show.go) -->
                <i v-if="chipNav_on(chip) && chipMatch_show(chip).go" :class="['chip-nav', 'chip-nav-go', 'pi', 'pi-chevron-right']"
                    @click.stop="$emit('chip-search-nav', { index, dir: 1 })"
                    v-uiHelp="'chips.nav.matches.go'"
                />
                <!-- IE 2026 08 19 / ODOO-5512 (заказ Игоря): информационный чипс со счётчиком
                     и навигацией (Corrections: бейдж = число правленных ячеек, ←/→ — переход
                     между ними; ⊗ — откат всех несохранённых корректировок) -->
                <template v-if="(chip.segment === 'CHIP-Info' || chip.segment === 'CHIP-CompareModel') && chip.doc?.count != null">
                    <span class="chip-badge chip-badge-info" v-uiHelp="{ key: 'chips.badge.info', state: () => (chip.segment === 'CHIP-CompareModel' ? 'compare' : (chip.doc?._corr ? 'corrections' : 'info')) }">{{ chip.doc.count }}</span>
                    <template v-if="chip.doc.nav">
                        <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                        <i class="chip-nav pi pi-chevron-left" v-uiHelp="{ key: 'chips.nav.info.prev', state: () => (chip.segment === 'CHIP-CompareModel' ? 'compare' : (chip.doc?._corr ? 'corrections' : 'info')) }" :title="t('Previous corrected cell')" @click.stop="chip.doc.onNav && chip.doc.onNav(-1)" />
                        <i class="chip-nav pi pi-chevron-right" v-uiHelp="{ key: 'chips.nav.info.next', state: () => (chip.segment === 'CHIP-CompareModel' ? 'compare' : (chip.doc?._corr ? 'corrections' : 'info')) }" :title="t('Next corrected cell')" @click.stop="chip.doc.onNav && chip.doc.onNav(1)" />
                    </template>
                </template>
                <!-- IE 2026 09 15 / ODOO-… (заказ Игоря): чипс пейджинга тиража виджетов — «с–по / всего» и ‹ › -->
                <template v-if="chip.segment === 'CHIP-WidgetsPage'">
                    <span class="chip-badge chip-badge-pages" v-uiHelp="'chips.badge.pages'" :title="chip.doc.tooltip">{{ chip.doc.from }}–{{ chip.doc.to }} / {{ chip.doc.total }}</span>
                    <i :class="['chip-nav', 'pi', 'pi-chevron-left', { 'chip-nav-disabled': chip.doc.pageNo <= 0 }]" v-uiHelp="{ key: 'chips.nav.pages.prev', state: () => (chip.doc.pageNo <= 0 ? 'edge' : 'ok') }" :title="t('Previous widgets')" @click.stop="store.multiply_nav(chip.doc.key, -1)" />
                    <i :class="['chip-nav', 'pi', 'pi-chevron-right', { 'chip-nav-disabled': chip.doc.pageNo >= chip.doc.pages - 1 }]" v-uiHelp="{ key: 'chips.nav.pages.next', state: () => (chip.doc.pageNo >= chip.doc.pages - 1 ? 'edge' : 'ok') }" :title="t('Next widgets')" @click.stop="store.multiply_nav(chip.doc.key, 1)" />
                </template>
                <!-- ⊗ — зона БЕЗ ховера: наведение сюда не открывает попап чипса и
                     закрывает открытый (попап накрывал саму кнопку закрытия) -->
                <!-- IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): у статичного информационного чипса (doc.static — «Regimen paused until …») крестика нет -->
                <i v-if="chip.segment !== 'CHIP-WidgetsPage' && !chip.doc?.static" class="chip-remove pi pi-times-circle" @click.stop="removeChip(index)"
                    @mouseover.stop="onChipRemoveMouseOver()"
                    v-uiHelp="{ key: 'chips.remove', state: () => uiHelp_chipKind(chip) }"
                />
            </div>

            <span v-if="attr?.showPlaceholder && !editable" class="chips-placeholder-text">{{ attr?.placeholder || placeholder }}</span>
            <input
                v-if="editable && mode !== 'Page' && !inputOff"
                ref="input"
                type="text"
                v-model="newChipText"
                class="chips-input"
                :placeholder="attr?.placeholder || placeholder"
                @keydown="onChipsInputKeydown"
                @keydown.enter="addChip"
                @keydown.backspace="removeLastChip"
                @keydown.esc.stop.prevent="onChipsInputEsc"
                @blur="onChipsInputBlur"
                @paste.prevent="onPaste"
                v-uiHelp="attr?.uiHelp || 'chips.input'"
            />
            <!-- IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): поле поиска полосы чипсов стола (mode Page, десктоп) — один запрос по словарям всех селекторов стола (barSearch_*), выбор ставит чипс тем же путём, что форма чипса (applyChipPick). Флаг fe.ux.chipsBarInput -->
            <!-- IE 2026 11 04 / ODOO-350449 (заказ Игоря: верхняя полоса в один ряд — иконки вместо подписей, «⋯» вместо поиска): при чипсах в полосе (fe.ux.topBarCompact) поле — узкое «⋯» без второго ряда; что это — по Alt (chips.bar.input); фокус / текст — прежняя ширина -->
            <input
                v-if="barInput_on()"
                ref="barInput"
                type="text"
                v-model="barText"
                class="chips-input chips-bar-input"
                :class="{ 'chips-bar-dots': barDots_on(), 'chips-bar-typed': barDots_on() && !!barText }"
                :placeholder="barDots_on() ? '⋯' : t('Search by selectors…')"
                @focus="barSearch_focus"
                @blur="barSearch_blur"
                @keydown.down.prevent="barSearch_move(1)"
                @keydown.up.prevent="barSearch_move(-1)"
                @keydown.enter.prevent="barSearch_enter"
                @keydown.esc.stop.prevent="barSearch_esc"
                @paste="barPaste"
                v-uiHelp="'chips.bar.input'"
            />
        </div>

        <div v-if="!inputOff" class="dropdown-button" @click.stop="toggleDropdown" v-uiHelp="{ key: 'chips.dropdown-button', state: () => (showDropdown ? 'open' : 'closed') }">
            <i :class="['pi', showDropdown ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
        </div>

        <!-- IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): совпадения поля поиска полосы — группы «селектор: значения»; mousedown не уводит фокус из поля -->
        <div v-if="barOpen && barQuery.length >= 2 && barInput_on()" class="chips-dropdown chips-bar-list" :style="barListStyle" @mousedown.prevent @click.stop>
            <div v-if="barLoading" class="chip-pick-empty">{{ t('Loading values...') }}</div>
            <template v-for="g in barGroups" :key="g.key">
                <div class="chips-bar-group">
                    <i :class="['pi', chipPick_segmentPi(g.chip)]"></i>
                    <span class="chips-bar-group-title">{{ g.title }}</span>
                    <span v-if="g.tier || g.conv" :class="['chips-bar-tier', { 'chip-pick-conv-fuzzy': g.tier }]">{{ barSearch_tierText(g) }}</span>
                </div>
                <div v-for="(it, ii) in g.items" :key="g.key + '|' + it.id"
                    :class="['chip-pick-item', 'chips-bar-item', { 'chips-bar-active': barActive === g.offset + ii }]"
                    @click="barSearch_pick(g, it)" @mousemove="barActive = g.offset + ii"
                    v-uiHelp="'chips.bar.item'"
                >
                    <i :class="['pi', 'chip-item-open', chipPick_itemPi(g.chip, it)]"></i>
                    <span class="chip-clip" v-html="barSearch_titleHTML(g, it)"></span>
                </div>
            </template>
            <div v-if="!barGroups.length && !barLoading" class="chip-pick-empty">{{ barPending ? t('Searching...') : t('No matches') }}</div>
        </div>

        <div class="chips-dropdown" v-if="showDropdown" :style="dropdownStyle" @click.stop v-uiHelp="'chips.dd.panel'">
            <div class="dropdown-tab" v-for="(tab, tabIndex) in dropdownTabs" :key="tabIndex + '-' + resolveIndex">
                <div :class="['dropdown-tab-header', { 'dropdown-tab-header-action': tab.meta?.action }]"
                    @click="tab.meta?.action && onDropdownTabAction(tab.meta.action)"
                    v-uiHelp="{ key: uiHelp_ddTabKey(tab), state: () => (mode === 'Embedded' ? 'table' : 'page') }"
                >
                    <i :class="tab.meta?.pi"></i>
                    <span>{{ tab.title }}</span>
                    <i v-if="tab.meta?.add_action" class="pi pi-plus dropdown-tab-header-add"
                        @click.stop="onDropdownTabAddAction(tab.meta.add_action)"
                    />
                </div>
                <div class="dropdown-tab-items">
                    <template v-for="(item, itemIndex) in tab.attrs" :key="itemIndex">
                        <!-- nested tab -->
                        <template v-if="item.segment === 'AttrTab'">
                            <!-- IE 2026 09 15 / ODOO-… (замечание Игоря): группа Filters — сразу список вариантов с колонками флагов, без второго подзаголовка «Filters variants» -->
                            <!-- IE 2026 09 19 / ODOO-… (замечание Игоря): команды группы — не строками под списком, а кнопками-пиктограммами над списком вариантов
                                 (в строке заголовка группы); названия и пояснения — стандартной подсказкой платформы (зажатый Alt / Show tooltips) -->
                            <div class="dropdown-tab-subtitle" :class="{ 'chip-variant-head': item.cols || item.buttons }" v-uiHelp="{ key: 'chips.cmd.group', state: () => (itemIndex === 0 ? 'filters' : 'page') }">
                                <span class="chip-cmd-head"><span>{{ item.title }}</span>
                                    <span v-if="item.buttons?.length" class="chip-cmd-buttons">
                                        <template v-for="(b, bi) in item.buttons" :key="'b' + bi">
                                            <span v-if="b._gap" class="chip-cmd-gap"></span>
                                            <!-- IE 2026 09 26 / ODOO-… (замечание Игоря: «+» на новой странице): недоступная кнопка (⚙ временной страницы) — приглушена, клик ничего
                                                 не делает; не disabled — у disabled-кнопки нет событий мыши, и подсказка по Alt (почему недоступна) не показалась бы -->
                                            <!-- demo-webscm 2026 11 02 / ODOO-350612: data-help = ключ объяснения команды (uiHelp_cmdKey) — цель тура навигатора (chips.cmd.btn.*) -->
                                            <button v-else type="button" :class="['chip-cmd-btn', { 'is-disabled': b.disabled }]" :aria-label="b.title" :aria-disabled="b.disabled ? 'true' : null" :data-cmd="b.name || b.title" :data-help="uiHelp_cmdKey(b) || null"
                                                v-wjTooltipFn="cmdTooltipFn(b)" @click.stop="!b.disabled && onDropdownTabItemClick(b)"><i :class="b.meta?.pi"></i></button>
                                        </template>
                                    </span>
                                </span>
                                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                                <span v-if="item.cols" class="chip-variant-cols" v-uiHelp="'chips.cmd.cols'" :title="t('Default for all · Default for me')">{{ t('All · Me') }}</span>
                            </div>
                            <template v-for="(sub, subIndex) in item.attrs" :key="'n' + subIndex">
                                <!-- IE 2026 08 28 / ODOO-5828: разделитель и пункты-команды таба Commands -->
                                <div v-if="sub._divider" class="dropdown-tab-divider" />
                                <!-- IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): подзаголовок списка с колонками флагов и строка варианта / страницы -->
                                <div v-else-if="sub.segment === 'CHIP-VariantHead'" class="dropdown-tab-subtitle chip-variant-head">
                                    <span>{{ sub.title }}</span><span class="chip-variant-cols" :title="t('Default for all · Default for me')">{{ t('All · Me') }}</span>
                                </div>
                                <div v-else-if="sub.segment === 'CHIP-VariantRow'" :class="['dropdown-tab-item', 'dropdown-tab-item-nested', 'chip-variant-row', { 'chip-variant-current': sub.current }]"
                                    :style="sub.depth ? { paddingLeft: (12 + sub.depth * 14) + 'px' } : null" :title="sub.tooltip || null" @click.stop="sub.onTitle && sub.onTitle()"
                                    v-uiHelp="{ key: 'chips.cmd.row', state: () => ((sub.pi === 'pi-filter' ? 'filters-' : 'page-') + (('onDelete' in sub) ? 'variant' : 'main')) }">
                                    <i :class="['dropdown-tab-item-cmd-pi', 'pi', sub.pi || 'pi-file']" v-uiHelp="'chips.cmd.row-icon'" />
                                    <!-- IE 2026 09 19 / ODOO-… (замечание Игоря: «сделай бейдж current как на скрине» — как в списке вариантов виджета) -->
                                    <span v-if="sub.current" class="chip-variant-badge" v-uiHelp="'chips.cmd.current'" :title="t('This one is applied now')">{{ t('Current') }}</span>
                                    <span class="chip-variant-title">{{ sub.title }}</span>
                                    <!-- IE 2026 09 19 / ODOO-… (замечание Игоря: удаление вариантов — не основных): корзина у строки ВАРИАНТА, видна по наведению;
                                         у «(main)» её нет; право — владелец варианта или админ (can_edit с бэка) -->
                                    <i v-if="sub.onDelete" class="pi pi-trash chip-variant-del" v-uiHelp="'chips.cmd.delete'" :title="t('Delete this variant (it is moved to the archive)')" @click.stop="sub.onDelete()" />
                                    <i v-if="sub.flags" :class="['pi', 'chip-variant-flag', 'chip-variant-flag-all', sub.default_all ? 'pi-check-square' : 'pi-stop', { 'chip-variant-flag-off': !sub.canAll }]"
                                        v-uiHelp="{ key: 'chips.cmd.all', state: () => (!sub.canAll ? 'locked' : (('onDelete' in sub ? 'variant-' : 'main-') + (sub.default_all ? 'on' : 'off'))) }"
                                        :title="sub.canAll ? t('Default for all — opens for everyone') : t('Default for all (admins)')" @click.stop="sub.onAll && sub.onAll()" />
                                    <i v-if="sub.flags" :class="['pi', 'chip-variant-flag', 'chip-variant-flag-me', sub.default_me ? 'pi-check-square' : 'pi-stop']"
                                        v-uiHelp="{ key: 'chips.cmd.me', state: () => (('onDelete' in sub ? 'variant-' : 'main-') + (sub.default_me ? 'on' : 'off')) }"
                                        :title="t('Default for me — opens for me (wins over \'for all\')')" @click.stop="sub.onMe && sub.onMe()" />
                                </div>
                                <div v-else class="dropdown-tab-item dropdown-tab-item-nested"
                                    :title="sub.tooltip || null"
                                    @click="onDropdownTabItemClick(sub)"
                                >
                                    <i v-if="sub.segment === 'CHIP-Command' && sub.meta?.pi" :class="['dropdown-tab-item-cmd-pi', sub.meta.pi]" />
                                    <span v-if="sub.segment === 'CHIP-Where' || sub.segment === 'CHIP-Selector' || sub.segment === 'CHIP-Param' || sub.segment === 'CHIP-Widget' || sub.segment === 'CHIP-Record'"
                                        class="dropdown-tab-item-title chip-text"
                                        v-html="resolveDropdownItemHTML(sub)"
                                    />
                                    <span v-else>{{ resolveDropdownItemTitle(sub) }}</span>
                                </div>
                            </template>
                        </template>
                        <!-- regular item -->
                        <template v-else>
                            <div class="dropdown-tab-item"
                                :style="item.meta?.depth ? { paddingLeft: (12 + item.meta?.depth * 16) + 'px' } : {}"
                                @click="onDropdownTabItemClick(item)"
                                v-uiHelp="uiHelp_ddItemKey(item)"
                            >
                                <i v-if="item._template"
                                    class="dropdown-tab-item-check pi pi-plus"
                                />
                                <i v-else-if="item.segment === 'CHIP-Where' || item.segment === 'CHIP-Selector' || item._global"
                                    :class="['dropdown-tab-item-check', 'pi', item.meta?.use !== false ? 'pi-check-square' : 'pi-stop']"
                                    @click.stop="check_chip_dropdown(item); emitChips()"
                                    v-uiHelp="{ key: 'chips.dd.item.check', state: () => (item.meta?.use !== false ? 'on' : 'off') }"
                                />
                                <span v-if="item.segment === 'CHIP-Where' || item.segment === 'CHIP-Selector' || item.segment === 'CHIP-Param' || item.segment === 'CHIP-Widget' || item.segment === 'CHIP-Record' || item._global"
                                    class="dropdown-tab-item-title chip-text"
                                    v-html="resolveDropdownItemHTML(item)"
                                />
                                <span v-else>{{ resolveDropdownItemTitle(item) }}</span>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick, defineComponent } from "vue";
import { useMainStore } from '@/stores/mainStore'
import { gridEditMode_ddChips } from '@/utils/gridEditMode'   // IE 2026 10 14 / ODOO-5800: чипс Corrections per-binding (dd-чипсы грида — gridEditMode_ddChips)
import { debugLog } from '@/utils/debugLog'   // IE 2026 10 02 / ODOO-345042 (заказ Игоря: сообщения в консоли): [chip-pick] — только при localStorage webscm.debug = 1
import { liveSearch_text, liveSearch_delay } from '@/utils/gridLiveSearch'   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): ввод в полосе чипсов таблицы — живой поиск (без Enter)
import { pmMenu_chipRows, pmMenu_fmtDate, pmMenu_cmpApply } from '@/utils/pmMenuGroups'   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ)
import { chipsLegacy_afterRemove } from '@/utils/menuSearchChips'   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): ⊗ в режиме Legacy — список без снятого чипса
import { t, getLanguage } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n — словарь для data()/props (там нет this); в методах — this.t / this.tn
import { GROUP_HEADER_OPTION, groupHeaderValue_on, groupHeader_fieldAndCount } from '@/utils/gridGroupHeader'   // IE 2026 11 02 / ODOO-349799 (group-header-value): опция чипа группировки «Field and count» — полный заголовок строки-группы (поле, значение, счётчик); хранится в самом чипе (meta.fieldAndCount)
import { pageCommandGroups_build } from '@/utils/pageCommandGroups'   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p214 єдине меню робочої області — вкладки Меню / Сторінки / Завдання)
import { layoutCandidates } from '@/utils/keyboardLayout'   // IE 2026 09 10 / ODOO-…: поиск не в той раскладке
import { metricChip_lists, metricChip_parts, metricChip_titleParts, metricChip_text, metricChip_sig, metricChip_copy, metricChip_isEmpty, metricChip_histRead, metricChip_histPush, METRIC_CHIP_KINDS, metricRules_h4, metricRules_s5, metricKey_parse, metricChip_newFlags /* IE 2026 10 04 / ODOO-5800 (fe-73) */ } from '@/utils/metricChip'   // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»)
import { metricChip_toggleIds, metricSignals_learn, metricRules_sig, metricChip_labels } from '@/utils/metricChip'   // IE 2026 10 04 / ODOO-5800 (fe-74): чипс сигналов v2
import { signalsV2_on } from '@/utils/signalNames'   // IE 2026 10 04 / ODOO-5800 (fe-74)
import { metricNav_show } from '@/utils/metricChip'   // IE 2026 10 05 / ODOO-5800 (fe-82): счётчик и стрелки чипса метрик при единственной остановке
import { metricNav_badge } from '@/utils/metricChip'   // IE 2026 10 05 / ODOO-5800 (fe-88): текст счётчика — до первого перехода ‹ › только число остановок
import { metricNav_moreBadge, metricNav_moreText } from '@/utils/metricChip'   // IE 2026 10 05 / ODOO-5800 (fe-86): хвост «+N» счётчика чипса метрик — ячейки сигнала в строках выключенных опций
import { metricChipG_offer } from '@/utils/metricChip'   // IE 2026 10 04 / ODOO-5800 (fe-78): пункт «Signals» списка ⌄ после снятого ⊗
import { modelV2_on, sigRules_isV2 } from '@/utils/signalModel'   // IE 2026 10 04 / ODOO-5800 (fe-78): модель сигналов v2 — секции формы по записям справочников
import { CHIP_PLACES, chipPlace_supported, chipPlace_of, chipPlace_isInput, chipPlace_write, chipPlace_sig, chipPlace_info } from '@/utils/chipPlace'   // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки)
import { formatChip_isEmpty, formatChip_copy, formatChip_sig, formatChip_parts, formatChip_titleParts, formatChip_text, formatChip_toggle, formatChip_items, formatChip_histRead, formatChip_histPush } from '@/utils/formatChip'   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format)
import { cfClassDeltaCss } from '@/utils/cfEval'   // образец цвета правила в форме и подписи чипса метрик
import { clipOverlay_show } from '@/utils/clipOverlay'   // IE 2026 09 18 / ODOO-… (замечание Игоря): «продолжение» обрезанного текста — пиксель в пиксель
import { chipsBar_segKind, chipsBar_dataBytes, chipsBar_segState, chipsBar_plan, chipsBar_num, CHIPS_BAR_MAX_ROWS, CHIPS_BAR_MAX_SEGMENTS, CHIPS_BAR_SERVER_LIMIT, CHIPS_BAR_MAX_BYTES } from '@/utils/chipsBarLoad'   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): вид / размер сегмента, бюджет байт   // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів)
import { searchExtraSets, matchExtraChar, lowerCached, extraTierText, EXTRA_TIER_SYMBOL, EXTRA_TIER_CHAR } from '@/utils/searchMatch'   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): последняя ступень поиска
import { searchTerms, fuzzyMaxTier, matchTerms, matchTermsBest, prepareTerms, highlightRanges, FUZZY_TIERS, FUZZY_TIER_TEXT } from '@/utils/searchMatch'   // IE 2026 09 12 / ODOO-… (заказ Игоря): поиск по частям + опечатки (ступени)
import { gridSearchAlt_label, gridSearchAlt_hint, gridSearchAlt_shownLang } from '@/utils/gridSearchAlt'   // IE 2026 09 25 / ODOO-… (замечание Игоря: поиск в таблице по названиям на других языках): подпись чипса в режиме «на другом языке»; IE 2026 09 28 / ODOO-341407: + gridSearchAlt_shownLang
import { chipWidget_open, chipWidget_isSource, chipWidgetExplicit_on } from '@/utils/chipWidgetOpen'   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
import { uiHelp_rowsHtml, uiHelp_enabled } from '@/utils/uiHelp'   // IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U4: строки объяснения дописываются в подсказку кнопок-команд (cmdTooltipFn), ключи-помощники uiHelp_chipKind / uiHelp_chipHelpKey / uiHelp_ddTabKey / uiHelp_ddItemKey / uiHelp_cmdKey
import { compareModelBadge_text, compareModelBadge_helpVars } from '@/utils/compareModelBadge'   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114897: плашка «Порівняння з» — _compare_info.compare_model)
import { approvalChipTitle, approvalChipLabel, approvalChipLink, approvalOpenRef, approvalLinksOn, approvalChipIs } from '@/utils/approval'   // IE 2026 10 10 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE1-kimp FE1: ссылки подсказки чипса   // IE 2026 10 09 / ODOO-345618 (F2): перевод чипса сводки согласования (бэк шлёт английский текст)
import { approvalChipOnTab } from '@/utils/approval'   // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса)
import { scheduleChip_on, scheduleChip_virtual } from '@/utils/scheduleChip'   // IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): чипс шапки стола «Regimen paused until …»
import { ctxMenu_allowed, ctxMenu_count } from '@/utils/ctxMenuGuard'   // IE 2026 10 12 / ODOO-343342 (заказ Игоря: правая кнопка на остальных элементах — рейл / чипс / стол)
import { orderChip_title } from '@/utils/orderChipTitle'   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #115652: подписи чипса сортировки на языке пользователя)
import { chip_titleLang, chipTitleLang_on } from '@/utils/chipTitleLang'   // IE 2026 10 12 / ODOO-5800: подпись чипса на языке пользователя (title_uk / title_ru), чипс «Mine» / «Мої»
import { chipsList_on, chipsTabs_hasFilters, chipsTab_attrs } from '@/utils/chipsList'   // IE 2026 10 13 / ODOO-5800: вкладка «Chips» панели ⌄ перечисляет чипсы виджета в любом режиме (флаг fe.ux.chipsListAllModes)
import { chipPickKeepsSelector_on, chipRow_values, chipRow_inToList, chipRow_isWidgetSelector, chipRow_setSingle } from '@/utils/chipPickRow'   // IE 2026 10 13 / ODOO-5800: значения строки «in» стола — в attrs (флаг fe.ux.chipPickKeepsSelector)
import { chipRow_portable, chipPortable_parse, chipPortable_findRow, chipsBarFlag_on } from '@/utils/chipsBarCopy'   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): переносимый текст чипса стола
import { pageGotoChip_applyMany } from '@/utils/pageGotoChip'   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): вставка чипса в стол без виджета-селектора
import * as wjcCore from '@mescius/wijmo';
import { re } from "mathjs";

const CHIP_PICK_WIDGET_WAIT_MS = 20000   // IE 2026 09 22 / ODOO-5800: сколько ждём данных скрыто открытого виджета-селектора, прежде чем показать словарь

const LAYOUT_MAP = {
    KeyQ:{en:'q',ru:'й'}, KeyW:{en:'w',ru:'ц'}, KeyE:{en:'e',ru:'у'},
    KeyR:{en:'r',ru:'к'}, KeyT:{en:'t',ru:'е'}, KeyY:{en:'y',ru:'н'},
    KeyU:{en:'u',ru:'г'}, KeyI:{en:'i',ru:'ш'}, KeyO:{en:'o',ru:'щ'},
    KeyP:{en:'p',ru:'з'}, BracketLeft:{en:'[',ru:'х'}, BracketRight:{en:']',ru:'ъ'},
    KeyA:{en:'a',ru:'ф'}, KeyS:{en:'s',ru:'ы'}, KeyD:{en:'d',ru:'в'},
    KeyF:{en:'f',ru:'а'}, KeyG:{en:'g',ru:'п'}, KeyH:{en:'h',ru:'р'},
    KeyJ:{en:'j',ru:'о'}, KeyK:{en:'k',ru:'л'}, KeyL:{en:'l',ru:'д'},
    Semicolon:{en:';',ru:'ж'}, Quote:{en:"'",ru:'э'},
    KeyZ:{en:'z',ru:'я'}, KeyX:{en:'x',ru:'ч'}, KeyC:{en:'c',ru:'с'},
    KeyV:{en:'v',ru:'м'}, KeyB:{en:'b',ru:'и'}, KeyN:{en:'n',ru:'т'},
    KeyM:{en:'m',ru:'ь'}, Comma:{en:',',ru:'б'}, Period:{en:'.',ru:'ю'},
}

// IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів): размеры полных словарей сегментов, узнанные полем полосы за сеанс страницы ({ seg: строк }; Infinity — обрезан бэком)
const CHIPS_BAR_SIZES = Object.create(null)
const CHIPS_BAR_BYTES = Object.create(null)   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): замер байт загруженных словарей ({ seg: байт })

export default defineComponent({
    props: {
        widget_id: [String, Number],
        attr_id: String,
        initialAttr: {
            type: Object,
            default: null,
        },
        modelValue: Array, // Legacy: for components without record binding (e.g. Search.vue)
        record: Object, // for mode='Page': external record (e.g. WORKSPACE.record)
        mode: { type: String, default: 'Standalone' }, // 'Standalone' | 'Embedded' | 'Legacy' | 'Page'
        placeholder: { type: String, default: () => t('Search...') },   // IE 2026 09 23 / ODOO-341407: i18n
        inputOff: { type: Boolean, default: false },   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): без своего поля ввода, лупы и ⌄ (ввод — у хоста; чипсы приходят modelValue)
    },
    data: () => ({
        WIDGET: {},
        attr: {},

        editable: false,
        isDragging: false,
        showChipMenuIndex: null,
        hoverIndex: null,
        pmArchOpen: {},   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): папка «Archive (N)» меню чипса модели раскрыта: { pm, cmp } — true / false после щелчка
        // IE 2026 09 09 / ODOO-5800: снимок значений чипса на время открытого поповера ({ index, items })
        chipValueSnap: null,
        // подбор значения чипса (блок Pick value в выпадашке)
        chipPickText: '',
        chipPickQuery: '',
        formatPickText: '',   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): текст поиска формы чипса оформления
        metricPickText: '',   // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): текст поиска формы чипса метрик
        // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): чипс метрик шапки стола (mode 'Page'). keys — словарь витрины (metric_chip_keys бэка), sig — отпечаток прочих
        // чипсов стола, под которыми он посчитан; localDoc — отбор, который правит пользователь, пока бэк его не принял (форма и подпись читают
        // его, а не строку стола: ответы бэка заменяют строку целиком); draft — чипс добавлен из ⌄, строки стола ещё нет (её создаёт бэк)
        metricG: { ws: '', report: '', keys: null, sig: '', loading: false, draft: false, localDoc: null },
        variantsPanel: null,   // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): списки панели (pages / page / variants / filters / workspace)
        variantsPanelLoading: false,
        variantsUnavailable: false,   // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): бэк без методов вариантов (dev02 до слияния) — прежние команды
        chipPickResult: { out: [], tier: 0, conv: '', pending: false, key: '', seg: null, wide: null },   // IE 2026 09 30 / ODOO-5800: wide — { segs, seg, label } поиска, расширенного на подсегмент грани;   // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): результат порционного прохода   // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): текст подбора с задержкой — фильтр (ступени опечаток) не на каждую букву
        chipPickConv: '',   // IE 2026 09 10 / ODOO-…: текст подбора, сконвертированный из другой раскладки (подсказка)
        chipPickFuzzy: 0,   // IE 2026 09 12 / ODOO-…: ступень опечаток показанных совпадений (0 — точные)
        chipPickInner: 0,   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): ступень опечаток внутри последней ступени (шаг 7: очищенный текст найден с опечаткой) — в подписи списка
        chipPickFocused: false,
        selectedIndex: null,
        dragOverChipIndex: null,
        newChipText: '',
        langBuffer: [],
        resolveIndex: 0,
        showDropdown: false,
        dropdownStyle: {},
        // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): поле поиска полосы чипсов стола
        barText: '',
        barQuery: '',
        barOpen: false,
        barGroups: [],
        barActive: -1,
        barPending: false,
        barLoading: false,
        barListStyle: {},

        // IE 2026 09 23 / ODOO-341407: i18n — подписи типов чипсов (ключи сегментов не переводятся)
        chipTypes: {
            'CHIP-Search':                { title: t('Search'),                  pi: 'pi-search' },
            'CHIP-Selector':              { title: t('Selector'),                pi: 'pi-check-square' },   // IE 2026 09 13 / ODOO-335246 (Igor): прежняя иконка чипса селектора (просьба Igor 13.09)   // IE 2026 09 13 / ODOO-… (иконки)
            'CHIP-Where':                 { title: t('Filter'),                  pi: 'pi-filter' },
            'CHIP-Group':                 { title: t('Group', null, 'chip'),                   pi: 'ie-ico-group' },   // IE 2026 09 13 / ODOO-… (иконки)   // IE 2026 09 24 / ODOO-341407: контекстный ключ словаря (ревизия омонимов)
            'CHIP-Tree':                  { title: t('Hierarchy'),               pi: 'pi-sitemap' },
            'CHIP-Tag':                   { title: t('Tag'),                     pi: 'pi-tag' }, // IE 2026 08 19 / ODOO-5512
            'CHIP-TreeRoot':              { title: t('Tree'),                    pi: 'pi-sitemap' }, // IE 2026 08 20: отбор «текущее дерево» (Open in list)
            'CHIP-Param':                 { title: t('Param'),                   pi: 'pi-cog' },
            'CHIP-Node':                  { title: t('Node'),                    pi: 'ie-ico-node' },   // IE 2026 09 13 / ODOO-… (иконки)
            'CHIP-Order':                 { title: t('Order', null, 'chip'),                   pi: 'pi-sort' },   // IE 2026 09 24 / ODOO-341407: контекстный ключ словаря (ревизия омонимов)

            'CHIP-Widget':                { title: t('Widget'),                  pi: 'ie-ico-widget' },   // IE 2026 09 13 / ODOO-… (иконки)
            'CHIP-Record':                { title: t('Record'),                  pi: 'pi-file' },
            'CHIP-JSON':                  { title: t('JSON'),                    pi: 'pi-code' },
            'CHIP-Image':                 { title: t('Image'),                   pi: 'pi-image' },
            'CHIP-File':                  { title: t('File'),                    pi: 'pi-file' },

            'CHIP-Limit':                 { title: t('Limit'),                   pi: 'ie-ico-limit' },   // IE 2026 09 13 / ODOO-… (иконки)
            'CHIP-WidgetsPage':           { title: t('Widgets'),                 pi: 'pi-th-large' },
            'CHIP-Format':                { title: t('Formats'),                 pi: 'pi-palette' },   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format)
            'CHIP-Metric':                { title: t('Metrics'),                 pi: 'pi-bullseye' },   // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»)   // IE 2026 09 15 / ODOO-… (заказ Игоря): пейджинг тиража виджетов

            // виртуальные PM-чипы отчёта (визуализация настроек records.doc.params):
            // Process model (override) и режим Compare with previous model
            'CHIP-ProcessModel':          { title: t('Process model'),           pi: 'pi-sitemap' },
            'CHIP-CompareModel':          { title: t('Compare with model'),      pi: 'pi-arrow-right-arrow-left' },

            'CHIP-Rows':                  { title: t('Rows'),                    pi: 'pi-list' },
            'CHIP-Cell':                  { title: t('Cell'),                    pi: 'pi-table' },
            'CHIP-CellFilters':           { title: t('Cell Filters'),            pi: 'pi-filter' },
            'CHIP-CellAndWidgetsFilters': { title: t('Cell & Widget Filters'),   pi: 'pi-filter' },
            'CHIP-CellValues':            { title: t('Cell Values'),             pi: 'pi-link' },
            // TODO convert to filter rows
        },

        // Группы взаимозаменяемых типов: чип можно переключить только внутри своей группы.
        chipTypeSwitchGroups: [
            ['CHIP-Group', 'CHIP-Tree'],
            ['CHIP-Where', 'CHIP-Selector'],
            ['CHIP-Image', 'CHIP-File'],
            ['CHIP-Rows', 'CHIP-Cell', 'CHIP-CellFilters', 'CHIP-CellAndWidgetsFilters', 'CHIP-CellValues'],
        ],

        dropdown_data_custom: null, // override from outside if needed
    }),
    computed: {
        // IE 2026 09 14 / ODOO-5800 (замечание Игоря): выключатель обрезки названий в форме под чипсом (fe.ux.chipClipTitles=false — прежний вид)
        chipClipOn() { return this.store?.setting?.('fe.ux.chipClipTitles', true) !== false },
        chipsRaw() {
            if (this.mode === 'Page') {
                return this.record?.doc?.params?.WHERE || [] // this.record?.doc?.params?.CHIPS
            } else if (this.mode === 'Standalone') {
                if (!this.WIDGET?.record || !this.attr?.binding) return []
                return this.store.get_value(this.WIDGET.record, this.attr.binding, [], true)
            } else if (this.mode === 'Embedded') {
                if (!this.WIDGET?.record || !this.attr?.name) return []
                return this.store.get_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.CHIPS`, [], true)
            } else {
                // Legacy (Search.vue)
                return this.modelValue || []
            }
        },
        chips() {
            const chips = this.chipsRaw
                .map((chip, originalIndex) => {
                    if (this.mode === 'Page' && chip && chip.chip_segment === 'CHIP-Metric' && this.metricChipG_on()) {
                        // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): строка канала selector_map::metric_chip — чипс метрик шапки; подпись — из отбора по словарю
                        // витрины (до его прихода — по id значений)
                        // IE 2026 10 04 / ODOO-5800 (fe-78): выключенная строка (снята ⊗) при новом черновике из ⌄ остаётся в списке СО СВОИМ отбором, черновик — отдельный чипс
                        const rowM = chip, docM = (rowM.use === false && this.metricG.draft && modelV2_on() ? rowM.doc : (this.metricG.localDoc || rowM.doc)) || {}
                        return {
                            _raw: rowM, _index: originalIndex, _global: true,
                            segment: 'CHIP-Metric',
                            title: metricChip_text(docM, this.metricChip_rules(true)) || this.t('Metrics'),   // IE 2026 09 23 / ODOO-341407: i18n
                            meta: { use: rowM.use, hidden: rowM.hidden, pi: 'pi-bullseye', cssClass: rowM.cssClass || '', titleHTML: this.metricChip_titleHTML(docM, true) },
                            doc: docM,
                        }
                    }
                    if (this.mode === 'Page') {
                        const rowWHERE = chip
                        return {
                            _raw: rowWHERE,
                            _index: originalIndex,

                            segment: 'CHIP-Selector',
                            title: rowWHERE.title,
                            meta: {
                                use: rowWHERE.use,
                                hidden: rowWHERE.hidden,
                                pi: rowWHERE.pi,
                                cssClass: rowWHERE.cssClass || '',
                            },
                            doc: {
                                WHERE: [
                                    rowWHERE,
                                ],
                                widget: rowWHERE.widget,
                            },
                        }
                    }
                    chip._index = originalIndex
                    return chip
                })
                .filter(chip => chip.meta?.hidden !== true)
                .map(chip => this.check_chip(chip))

            return chips
        },
        // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): действующая строка чипса метрик стола (отпечаток отбора) — вотчер подтягивает словарь для подписи
        metricG_rowSig() {
            if (this.mode !== 'Page') return ''
            const row = (this.chipsRaw || []).find(r => r && r.chip_segment === 'CHIP-Metric')
            return row && row.use !== false && !metricChip_isEmpty(row.doc || {}) ? (this.store.activeWORKSPACE?.workspace || '') + '|' + (row.widget || '') + '|' + metricChip_sig(row.doc) : ''
        },
        visibleChips() {
            this.resolveIndex // dependency trigger for itemsSource resolve
            const base = this.chips.filter(chip => chip.meta?.use !== false)
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): черновик из ⌄ — виртуальный чипс в конце полосы, пока бэк не создал строку стола
            if (this.mode === 'Page' && this.metricG.draft && !base.some(c => c._global)) {
                const docD = this.metricG.localDoc || {}
                base.push({ segment: 'CHIP-Metric', _global: true, _draft: true, _isVirtual: true, title: '',
                    meta: { use: true, pi: 'pi-bullseye', titleHTML: this.metricChip_titleHTML(docD, true) }, doc: docD })
            }
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): зеркало глобального чипса метрик над таблицей (виджет принимает чипс как table / both)
            if (this.mode === 'Embedded') { const mir = this.metricMirror_now(); if (mir) base.push(mir) }
            if (this.attr?.['showCHIP-Limit'] && this.WIDGET?.record?.doc != null) {
                const doc = this.WIDGET.record.doc
                const _total = doc._limit_total_count ?? null
                const _isOnePage = doc.limit != null && _total != null && _total <= doc.limit && (doc.offset ?? 0) === 0
                if (!_isOnePage && (doc.limit || doc.offset)) { // || doc.dataLimit
                    base.unshift({
                        segment: 'CHIP-Limit',
                        _isVirtual: true,
                        title: this.t('Limit'),   // IE 2026 09 23 / ODOO-341407: i18n
                        meta: { use: true, pi: 'pi-book' },
                        doc: {
                            limit: doc.limit ?? null,
                            offset: doc.offset ?? 0,
                            dataLimit: doc.dataLimit ?? null,
                            totalCount: doc._limit_total_count ?? null,
                        },
                    })
                }
            }
            // PM-чипы: настройка «выполнять по указанной процессной модели» и режим
            // сравнения с моделью показываются чипами над таблицей; удаление чипа
            // очищает настройку (removeChip), выбор в hover-меню — меняет её
            const _pmChipsHost = (this.mode === 'Standalone' && String(this.attr?.binding || '').endsWith('CHIPS'))
                || (this.mode === 'Embedded' && this.attr?.name === 'dataSheet')
            // IE 2026 10 14 / ODOO-5800: виртуальный info-чипс из записи info (doc._info_chips бэка / WIDGET._dd_info_chips фронта) — общий для основной таблицы и гридов вкладок
            const _infoChip = (info) => ({
                segment: 'CHIP-Info',
                _isVirtual: true,
                _index: -1,
                title: approvalChipTitle(info),   // IE 2026 10 09 / ODOO-345618 (F2): чипс «Approval: …» переводится, прочие — как были
                meta: { use: true, pi: info.pi || 'pi-info-circle', cssClass: info.cssClass || '' },
                doc: info,
            })
            if (_pmChipsHost) {
                const params = this.WIDGET?.record?.doc?.params
                if (params) {
                    // IE 2026 08 28 / ODOO-5800 (замечание Игоря «мелькание изменения размеров чипса»): обычный
                    // select стирает _compare_info — чип сравнения с эталоном исчезал на время перечита и
                    // «моргал» бейджем при возврате. При params.compare_reference держим последний info на WIDGET
                    // (кладёт вотчер _compare_info — в computed писать нельзя, lint no-side-effects)
                    const cmpInfo = this.WIDGET?.record?.doc?._compare_info
                        || (params.compare_reference ? this.WIDGET._cmpInfo_keep : null) || null
                    if (params.compare_with_prev || cmpInfo || params.compare_reference) {
                        // IE 2026 08 23 / ODOO-5884 (заказ Игоря): бейдж с числом строк-отклонений и ←/→ переход
                        // между ними — как у чипса Corrections; навигацию делает грид (compareNav)
                        // IE 2026 08 24 / ODOO-5884 (замечание Игоря): в СПИСКАХ грид не называется dataSheet —
                        // ищем любой грид с навигацией сравнения
                        const _cmpGrid = Object.values(this.WIDGET?.attrs_vueObj || {}).find(vo => typeof vo?.compareNav === 'function')
                        const _nDev = cmpInfo?.rows_dev ?? null
                        const cmpBadge = params.compare_reference ? '' : compareModelBadge_text(cmpInfo, this.t)   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114897: плашка «Порівняння з» — _compare_info.compare_model)
                        // IE 2026 09 23 / ODOO-341407: i18n — подпись и подсказка чипса сравнения
                        base.unshift({
                            segment: 'CHIP-CompareModel',
                            _isVirtual: true,
                            // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114897: плашка «Порівняння з» — _compare_info.compare_model): с чем сравнивается отчёт сейчас — вместо «Δ vs …» (в режиме эталона плашки нет)
                            title: cmpBadge || this.t('Δ vs {model}', { model: cmpInfo?.prev_title || (params.compare_reference ? this.t('reference (data test)')
                                : this.pmTitle(cmpInfo?.prev_process_model
                                || params.prev_process_model || 'previous-pm')) }),  // IE 2026 08 24 / ODOO-5884: имя модели
                            meta: { use: true, pi: 'pi-arrow-right-arrow-left' },
                            doc: (_nDev != null && cmpInfo) ? {
                                count: _nDev,
                                nav: _nDev > 0 && !!_cmpGrid, // IE 2026 08 28 / ODOO-5800 (заказ Игоря): и при одном отклонении есть переход
                                onNav: (dir) => _cmpGrid?.compareNav?.(dir),
                                tooltip: [[this.t('Rows with deviations'), String(_nDev)],
                                          [this.t('Rows matched'), String(cmpInfo.rows_matched ?? '')],
                                          [this.t('Current / previous rows'), `${cmpInfo.rows_current ?? ''} / ${cmpInfo.rows_prev ?? ''}`]],
                                cmpInfo: cmpBadge ? cmpInfo : null,   // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114897): для объяснения по Alt (uiHelp_chipVars)
                            } : { cmpInfo: cmpBadge ? cmpInfo : null },
                        })
                    }
                    if (params.process_model_override) {
                        base.unshift({
                            segment: 'CHIP-ProcessModel',
                            _isVirtual: true,
                            title: this.pmTitle(params.process_model_override),
                            meta: { use: true, pi: 'pi-sitemap' },
                            doc: {},
                        })
                    }
                }
                // информационные чипсы прохода (сводка утверждения планов,
                // метрики): бэк кладёт их в doc._info_chips вместе с данными.
                // _index: -1 — у виртуальных чипсов нет позиции в списке
                // пользовательских, иначе перетаскивание и меню правят чужой чипс
                // IE 2026 08 18 / ODOO-5512: + чипсы расшифровки с фронта (несохранённые
                // правки ячеек-источника) — живут на WIDGET, переживают select
                // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса): чипс сводки согласования показывает бейдж вкладки Approval (fe.ux.approvalTabBadge) — в ряду его нет; в данных он остаётся (источник бейджа)
                const _apprOnTab = approvalChipOnTab(this.WIDGET, (k, d) => this.store.setting(k, d))
                // IE 2026 10 14 / ODOO-5800: чипс Corrections динамического списка (_binding) — только на своём гриде (utils/gridEditMode.gridEditMode_chipVisible)
                const _infoChips = [...(this.WIDGET?.record?.doc?._info_chips || []), ...gridEditMode_ddChips(this.WIDGET?._dd_info_chips, this.attr?.binding, true)].filter(info => !(_apprOnTab && approvalChipIs(info))).map(_infoChip)   // IE 2026 10 12 / ODOO-345618 (заказ Игоря: бейдж согласования на вкладке вместо чипса) · IE 2026 10 14 / ODOO-5800: dd-чипсы — gridEditMode_ddChips
                if (_infoChips.length) base.unshift(..._infoChips)
            } else if (this.mode === 'Embedded') {
                // IE 2026 10 14 / ODOO-5800 (бот dev02, п.4: чипс «Corrections» динамического списка на вкладке не рендерился): info-чипсы собирались только у основной таблицы (_pmChipsHost —
                // attr.name === 'dataSheet'); у грида ленивой вкладки (responsibles-grid …) — свои чипсы WIDGET._dd_info_chips с _binding этого грида (utils/gridEditMode.gridEditMode_ddChips)
                const _ddChips = gridEditMode_ddChips(this.WIDGET?._dd_info_chips, this.attr?.binding, false).map(_infoChip)
                if (_ddChips.length) base.unshift(..._ddChips)
            }
            // IE 2026 09 15 / ODOO-… (заказ Игоря): чипс пейджинга тиража виджетов страницы — в глобальном контейнере чипсов
            // (PAGE._multiplyChips ставит mainStore.multiply_chips; ‹ › — multiply_nav)
            if (this.mode === 'Page') {
                // IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): чипс шапки стола «Regimen paused until 05:00 UTC (3 nodes)» — первым в полосе, крестика нет (doc.static);
                // список кладёт mainStore.apply_header_chips (workspace_open / метод schedule_header_chip), тексты строит бэк; выключатель fe.ux.scheduleHeaderChip
                if (scheduleChip_on(this.store)) base.unshift(...scheduleChip_virtual(this.store.activeWORKSPACE?.header_chips))
                for (const c of (this.store.activeWORKSPACE?.activePAGE?._multiplyChips || [])) {
                    base.push({ segment: 'CHIP-WidgetsPage', _isVirtual: true, _index: -1, title: c.title, meta: { use: true, pi: 'pi-th-large' }, doc: { ...c } })
                }
            }
            return base
        },
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): выбор модели в чипсе «Δ vs model» — выключатель fe.ux.compareModelPicker (по умолчанию вкл.); у копии-эталона (compare_reference) выбора нет
        cmpPickerOn() {
            void this.store._settingsEpoch
            if (this.WIDGET?.record?.doc?.params?.compare_reference) return false
            return this.store.setting('fe.ux.compareModelPicker', true) !== false
        },
        pmMenuItems() {
            const items = [   // IE 2026 09 23 / ODOO-341407: i18n
                { process_model: 'current-pm', title: this.t('Current process model') },
                { process_model: 'previous-pm', title: this.t('Previous process model') },
            ]
            for (const it of (this.store.process_model_menu || [])) {
                if (it.process_model) {
                    items.push({ process_model: it.process_model, title: it.title })
                }
            }
            return items
        },
        // chipMapping() {
        //     return this.attr?.chipMapping || null
        // },

        // IE 2026 08 28 / ODOO-5828 (заказ Игоря, уточнение дизайна): таб Commands — только
        // Filters/Widgets с короткими именами; полные команды (+Page, +Copy) — в меню пользователя
        // IE 2026 08 28 / ODOO-5828 (заказ Игоря, уточнение дизайна): таб Commands — короткие имена; полные команды — в меню пользователя
        // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): группы Filters / Page / Widgets — варианты с флагами Default for all · for me
        // (вариант страницы = набор виджетов; вариант фильтров = снимок чипсов), команды Save as new variant / Save / Use current
        // IE 2026 09 29 / ODOO-342781: роль «только чтение» — вкладка Commands без записи (roCommandGroups); без роли — группы как были (pageCommandGroups_all)
        pageCommandGroups() {
            const groups = this.pageCommandGroups_all
            return this.store.readonly_on() ? this.roCommandGroups(groups) : groups
        },
        pageCommandGroups_all() {
            const store = this.store, WS = store.activeWORKSPACE
            const admin = store.checkAccess({ viewAccess: 'access-admin' })
            if (this.variantsUnavailable) return this.pageCommandGroups_legacy(admin, WS?.active_page || '')
            return pageCommandGroups_build(store, this.t, this.variantsPanel)   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p214 єдине меню робочої області — вкладки Меню / Сторінки / Завдання): группы — общий билдер utils/pageCommandGroups.js (его же читает форма меню рабочей области)
        },
        dropdown_data() {
            if (this.dropdown_data_custom) return this.dropdown_data_custom

            // IE 2026 09 23 / ODOO-341407: i18n — заголовки вкладок панели ⌄
            if (this.mode === 'Page') {
                return [
                    { segment: 'AttrTab', title: this.t('Selectors'),
                        meta: {
                            name:'filters', pi: 'pi pi-filter',
                            action: {command: 'widget_open', widget: 'widget-selectors'},
                            default_segment: 'CHIP-Selector',
                        },
                        attrs: []
                    },
                    { segment: 'AttrTab', title: this.t('Params'),
                        meta: {
                            name:'params', pi: 'pi pi-cog',   // IE 2026 09 13 / ODOO-… (иконки)
                            default_segment: 'CHIP-Param',
                        },
                        attrs: []
                    },
                    // IE 2026 08 28 / ODOO-5828 (заказ Игоря): третий раздел — команды
                    // управления столом (перенесены из меню пользователя, подменю Page)
                    { segment: 'AttrTab', title: this.t('Commands'),
                        meta: { name: 'commands', pi: 'pi pi-play' },   // IE 2026 09 13 / ODOO-… (иконки)
                        attrs: this.pageCommandGroups,
                    },
                ]
            }
            if (this.mode === 'Embedded') {
                const tabs = [
                    { segment: 'AttrTab', title: this.t('Chips'),
                        attrs: []
                    },
                    { segment: 'AttrTab', title: this.t('Params'), 
                        meta: {
                            name:'params', pi: 'pi pi-cog',   // IE 2026 09 13 / ODOO-… (иконки)
                            action: { command: 'set_activeAttr', binding: 'doc.params.fieldsMapping' },
                            default_segment: 'CHIP-Param', 
                        },
                        attrs: [] 
                    },
                ]
                // IE 2026 08 19 (заказ Игоря): у встроенных списков (таблица настроек)
                // блок Params относится к ВИДЖЕТУ, а не к списку — скрываем по флагу
                if (this.attr?.chips_hideParams) return [tabs[0]]
                return tabs
            }
            if (this.mode === 'Standalone') {
                return [
                    { segment: 'AttrTab', title: this.t('Filters'), 
                        meta: {
                            name:'filters', pi: 'pi pi-filter',
                            action: { command: 'widget_open', widget: 'widget-selectors' },
                            default_segment: 'CHIP-Where', 
                        },
                        attrs: [] 
                    },
                    { segment: 'AttrTab', title: this.t('Params'), 
                        meta: {
                            name:'params', pi: 'pi pi-cog',   // IE 2026 09 13 / ODOO-… (иконки)
                            default_segment: 'CHIP-Param', 
                        },
                        attrs: [] 
                    },
                ]
            }
            // Legacy
            return []
        },
        dropdownTabs() {
            let params
            if (this.mode === 'Page') {
                params = this.record?.doc?.params
            } else if (this.mode === 'Standalone' || this.mode === 'Embedded') {
                params = this.WIDGET?.record?.doc?.params
            } else {
                // Legacy: chips tab + add tab with default templates
                const defaultChips = (this.attr?.default_chips?.length
                    ? this.attr.default_chips
                    : [{ segment: 'CHIP-Where', title: this.t('Filter'), meta: { pi: 'pi pi-filter', use: true }, doc: { WHERE: [] } }]   // IE 2026 09 23 / ODOO-341407: i18n
                ).map(c => ({ ...c, _template: true }))

                const tabs = []
                if (this.chips?.length) {
                    tabs.push({ segment: 'AttrTab', title: this.t('Chips'),
                        meta: { name: 'filters', pi: 'pi pi-filter' },
                        attrs: this.chips,
                    })
                }
                tabs.push({ segment: 'AttrTab', title: this.t('Add'),
                    meta: { name: 'add', pi: 'pi pi-plus' },
                    attrs: defaultChips,
                })
                return tabs
            }

            return this.dropdown_data.map((tab) => {
                if (this.chips && tab.meta?.name === 'filters') {
                    // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): снятый пустой чипс метрик в списке не показываем; выключенный с отбором — с флажком (вернуть),
                    // а когда чипса нет и стол знает отчёт метрик (либо он открыт) — пункт «Metrics»
                    if (this.mode === 'Page' && this.metricChipG_on()) {
                        const list = this.chips.filter(c => !(c._global && metricChip_isEmpty(c._raw?.doc || {})))
                        if (this.metricChipG_offered()) list.push({ segment: 'CHIP-Metric', title: this.t('Metrics'), _template: true, meta: { use: true, pi: 'pi-bullseye' } })   // IE 2026 09 23 / ODOO-341407: i18n
                        return { ...tab, attrs: list }
                    }
                    return { ...tab, attrs:this.chips }
                }
                // IE 2026 08 19 (заказ Игоря): блок Chips встроенного списка —
                // предложить установить чипс дерева «Parent», если он ещё не
                // выбран, а строки выборки несут колонку parent
                // IE 2026 10 13 / ODOO-5800: вкладка «Chips» без meta.name (Embedded; либо dropdown_data_custom любого режима) — сперва чипсы виджета (вкл. выключенные), затем предложения
                const _chipsListOn = chipsList_on((k, d) => this.store?.setting?.(k, d))   // IE 2026 10 13 / ODOO-5800: флаг fe.ux.chipsListAllModes
                if (_chipsListOn && this.mode !== 'Embedded' && tab.title === this.t('Chips') && !tab.meta?.name) {   // IE 2026 10 13 / ODOO-5800
                    return { ...tab, attrs: chipsTab_attrs(this.chips, tab.attrs, { hasFilters: chipsTabs_hasFilters(this.dropdown_data) }) }   // IE 2026 10 13 / ODOO-5800
                }   // IE 2026 10 13 / ODOO-5800
                if (this.mode === 'Embedded' && tab.title === this.t('Chips') && !tab.meta?.name) {   // IE 2026 09 23 / ODOO-341407: i18n — заголовок вкладки переведён выше (dropdown_data), сравниваем тем же ключом
                    const attrs = []
                    const hasTree = (this.chipsRaw || []).some(c => c && (c.segment === 'CHIP-Tree' || c.attr_type === 'CHIP-Tree'))
                    let hasParent = (this.attr?.attrs || []).some(a => a?.binding === 'parent')
                    if (!hasParent) {
                        const rows = this.store.get_value(this.WIDGET?.record, this.attr?.binding) || []
                        hasParent = Array.isArray(rows) && rows.some(r => r && typeof r === 'object' && 'parent' in r)
                    }
                    if (!hasTree && hasParent) {
                        attrs.push({ segment: 'CHIP-Tree', title: this.t('Parent'), _template: true, _noOpen: true,   // IE 2026 09 23 / ODOO-341407: i18n (groupDescriptions — данные чипса, не переводятся)
                            meta: { use: true, pi: 'pi-sitemap' },
                            doc: { groupDescriptions: [{ title: 'Parent', binding: 'parent' }] } })
                    }
                    // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): у списка с правилами-метриками (бэк отдал attr.metric_rules) — предложить чипс метрик
                    if (this.metricChip_offered()) {
                        attrs.push({ segment: 'CHIP-Metric', title: this.t('Metrics'), _template: true,   // IE 2026 09 23 / ODOO-341407: i18n
                            meta: { use: true, pi: 'pi-bullseye' }, doc: { grades: [], metrics: [], horizons: [], statuses: [], combos: [] } })
                    }
                    // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): у виджета с правилами оформления, отмеченными Selector
                    // (бэк отдал attr.format_rules), — предложить чипс условного оформления
                    if (this.formatChip_offered()) {
                        attrs.push({ segment: 'CHIP-Format', title: this.t('Formats'), _template: true, meta: { use: true, pi: 'pi-palette' }, doc: { rules: [] } })   // IE 2026 09 23 / ODOO-341407: i18n
                    }
                    return { ...tab, attrs: chipsTab_attrs(this.chips, attrs, { on: _chipsListOn, hasFilters: chipsTabs_hasFilters(this.dropdown_data) }) }   // IE 2026 10 13 / ODOO-5800: чипсы виджета первыми (выключенные — с флажком), затем Parent / Metrics / Formats
                }
                if (params && tab.meta?.name === 'params') {
                    const defaultType = tab.meta?.default_segment || 'CHIP-Param'
                    
                    // params.fieldsMapping >> chips2

                    const processAttrs = (items) => {
                        const result = []
                        for (const row of items) {
                            if (row.use === false) continue
                            if (row.hidden === true) continue

                            const segment = row.segment || defaultType
                            let chip = {
                                segment: segment,
                                meta: {
                                    use: row.use !== false,
                                    pi: row.pi || this.chipTypes[segment]?.pi || 'pi-cog',
                                    cssClass: row.cssClass || '',
                                },
                                doc: {
                                    ...row,
                                    attrs: null,
                                },
                                attrs: [],
                            }
                            if (!row.segment && defaultType) row.segment = defaultType
                            if (row.attrs?.length) chip.attrs = processAttrs(row.attrs)
                            result.push(chip)
                        }
                        return result
                    }

                    const chips2 = processAttrs(params.fieldsMapping || [])
                    return { ...tab, attrs: chips2 }
                }
                return tab
            })
        },
    },
    setup() {
        const store = useMainStore()
        return { store }
    },
    mounted() {
        this._chipPlaceSig = this.chipPlace_sigNow()   // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки)
        this._onKeydown = (e) => {
            if (this.selectedIndex === null) return
            if (document.activeElement === this.$refs.input) return
            if (this.$refs.barInput && document.activeElement === this.$refs.barInput) return   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): Ctrl+C / Ctrl+X в поле поиска полосы — текст поля
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                e.preventDefault()
                this.copyChip(this.selectedIndex)
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
                e.preventDefault()
                this.cutChip(this.selectedIndex)
            } else if (e.altKey && e.key === 'F1') {
                e.preventDefault()
                const index = this.selectedIndex
                const chip = this.visibleChips[index]
                const chipEl = this.$el.querySelectorAll('.chip')[index]
                const widgetEl = document.getElementById('WIDGET' + this.WIDGET.id)
                let x, y
                if (chipEl && widgetEl) {
                    const cr = chipEl.getBoundingClientRect()
                    const wr = widgetEl.getBoundingClientRect()
                    x = cr.left - wr.left + cr.width / 2
                    y = cr.top - wr.top + cr.height / 2
                }
                this._openWidgetRowForChip(chip, index, x, y)
            } else if (e.key === 'Escape') {
                this.selectedIndex = null
            }
        }
        this._onDocumentClickDeselect = (e) => {
            if (!this.$el.contains(e.target)) this.selectedIndex = null
        }
        document.addEventListener('keydown', this._onKeydown)
    },
    watch: {
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): набор в полосе чипсов таблицы — живой поиск с паузой fe.ux.gridLiveSearchDebounceMs
        // (дефолт 250 мс; очистка — сразу): хост (AttrGrid, @live-search) ищет текст как чипс с «Show rows without matches» — строки не прячутся, найденное подсвечено
        newChipText(v) { this.liveSearch_input(v) },
        barText(v) { this.barSearch_input(v) },   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): поиск по селекторам — с паузой fe.ux.chipsBarSearchDebounceMs (250 мс)
        // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): запись / набор чипсов пришли не нашей правкой (открытие, перечитывание, другая
        // настройка) — отпечаток входных чипсов подтягиваем БЕЗ перезапроса: данные уже получены под эти чипсы
        chipsRaw() { if (!this._chipPlaceTimer) this._chipPlaceSig = this.chipPlace_sigNow() },
        // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): набор в поле подбора — фильтр запускается с паузой 200 мс (короткий текст и очистка — сразу):
        // ступени 5–6 (свёртка знаков, расстояние редактирования) на большом словаре дороги на каждую букву
        chipPickText(v) {
            clearTimeout(this._chipPickTimer)
            const delay = (v || '').length < 3 ? 0 : 200
            this._chipPickTimer = setTimeout(() => { this.chipPickQuery = v }, delay)
        },
        // IE 2026 09 09 / ODOO-5800: поповер закрылся — снимок значений сбрасывается, следующее открытие покажет оставшиеся
        // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): у стола действует чипс метрик — словарь нужен для подписи (запрос один, дальше кэш)
        metricG_rowSig: { handler(v) { if (v && this.metricChipG_on()) this.metricChipG_ensureKeys(false, true) }, immediate: true },
        // IE 2026 10 04 / ODOO-5800 (fe-74): реестр основ ключей для видов / сигналов чипса (utils/metricChip.metricSignals_learn) — сразу, как пришёл словарь виджета либо стола:
        // отбор строк грида (AttrGrid: metricChip_matchRow) сопоставляет ключи без словаря
        'attr.metric_rules': { handler(v) { metricSignals_learn(v) }, immediate: true, flush: 'sync' },
        'metricG.keys': { handler(v) { metricSignals_learn(v) }, immediate: true, flush: 'sync' },
        // другой стол — другой словарь и свой отбор: несохранённое состояние формы не переносится
        'store.activeWORKSPACE.workspace'() { if (this.mode === 'Page') { this.metricChipG_localDrop(); this.metricG.keys = null; this.metricG.sig = ''; this._metricLists = null } },
        hoverIndex(v) {
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): форма чипса метрик шапки открылась — словарь (числа считаются под прочими чипсами стола);
            // закрылась — отложенный отбор уходит сразу, пустой черновик не висит
            if (v !== null && this.visibleChips?.[v]?._global) this.metricChipG_ensureKeys()
            if (v === null && this.mode === 'Page') {
                if (this._mgSendTimer) this.metricChipG_send()
                if (this.metricG.draft && metricChip_isEmpty(this.metricG.localDoc || {})) { this.metricG.draft = false; this.metricG.localDoc = null }
            }
            if (this._formatDirty) { this.formatChip_historyPush(this._formatDirty); this._formatDirty = null }   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format)
            if (this._metricDirty) { this.metricChip_historyPush(this._metricDirty); this._metricDirty = null }   // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»)
            this.chipTip_hide()   // IE 2026 09 14 / ODOO-5800: форма под чипсом сменилась/закрылась — «продолжение» текста убрать
            if (v === null) this.chipValueSnap = null
        },
        'WIDGET.record.doc._compare_info': {
            // IE 2026 08 28 / ODOO-5800: последний info сравнения — на WIDGET (чип с эталоном переживает перечит)
            handler(v) { if (v) this.WIDGET._cmpInfo_keep = v },
            immediate: true,
        },
        'WIDGET.record.doc.params.compare_reference': {
            // IE 2026 08 28 / ODOO-5800 (заказ Игоря): флаг бейджа «Reference view» — в store по WIDGET.id
            // (в Widget.vue цепочка record.doc.params не всегда реактивна, здесь — надёжно)
            handler(v) { const id = this.WIDGET?.id; if (id != null) this.store.refBadge_by_widget[id] = v || null },
            immediate: true,
        },
        selectedIndex(val) {
            if (val !== null) {
                document.addEventListener('click', this._onDocumentClickDeselect)
            } else {
                document.removeEventListener('click', this._onDocumentClickDeselect)
            }
        },
        'store.variantsEpoch'() { if (this.showDropdown && this.mode === 'Page') this.variantsPanel_load() },   // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола)
        showDropdown(val) {
            if (val && this.mode === 'Page') this.variantsPanel_load()   // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): списки панели вариантов
            if (val) {
                document.addEventListener('click', this._onDocumentClick)
                this.store.openChipsDropdown = this
            } else {
                document.removeEventListener('click', this._onDocumentClick)
                if (this.store.openChipsDropdown === this) this.store.openChipsDropdown = null
            }
        },
        'store.activeWIDGET'(newW) {
            if (this.showDropdown && this.WIDGET && newW !== this.WIDGET) {
                this.showDropdown = false
            }
        },
        'store.itemsSource_change_index'() {
            this._resolveChipValues()
        },
        chips: {
            deep: true,
            handler() {
                this._resolveChipValues_all()
            },
        },
    },
    beforeUnmount() {
        clearTimeout(this._liveT)   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска)
        clearTimeout(this._barT); this._barRun = (this._barRun || 0) + 1; this.barSearch_cancel()   /* IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів) */ // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл)
        clearTimeout(this._chipHoverTimer)
        clearTimeout(this._chipPlaceTimer)   // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки)
        this.chipTip_hide()   // IE 2026 09 14 / ODOO-5800
        document.removeEventListener('click', this._onDocumentClick)
        document.removeEventListener('click', this._onDocumentClickDeselect)
        document.removeEventListener('keydown', this._onKeydown)
        if (this.store.openChipsDropdown === this) this.store.openChipsDropdown = null
        if (this.attr?.binding && this.WIDGET?.attrs_vueObj?.[`${this.binding}-CHIPS`] === this) {
            delete this.WIDGET.attrs_vueObj[`${this.binding}-CHIPS`]
        }
    },
    created() {
        // IE 2026 10 02 / ODOO-345042 (заказ Игоря: сообщения в консоли): ленивые нереактивные поля заданы заранее — чтение `this._x` в render до первой записи давало Vue warn
        // «Property "_x" was accessed during render but is not defined on instance»; охранники `if (!this._x)` ниже прежние
        this._chipHistoryReq = null
        this._liveT = null   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска)
        this._liveSent = ''   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска)
        this._chipPickDataTok = null
        this._chipPickDataN = 0
        this._chipPickSched = null
        this._barT = null; this._barRun = 0; this._barUnwatch = null; this._barPickFirst = 0; this._barMissing = 0; this._barSet = null   /* IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів) */ // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл)
        if (this.mode === 'Page') {
            this.attr = this.initialAttr
            this._resolveChipValues_all()
            return
        }
        if (this.widget_id != null) {
            this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
            this.content_Changed()
        }
    },
    methods: {
        check_chip(chip) {
            chip.meta ||= {}
            chip.meta.use = chip.meta?.use !== false
            chip.meta.pi = chip.meta?.pi || this.chipTypes[chip.segment]?.pi || ''

            chip.doc ||= {}

            chip.title = chip.title || this._resolveTitle(chip).text || ''

            return chip
        },
        check_chip_dropdown(chip) {
            chip.meta ||= {}
            chip.meta.use = !(chip.meta?.use !== false)
            if (chip._global) this.metricChipG_localDrop()   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): флажок списка переключает строку стола как есть
            
            if (this.mode === 'Page') {
                const rowWHERE = chip
                rowWHERE._raw ||= {}
                rowWHERE._raw.use = chip.meta.use
            }
        },
        content_Changed() {
            if (this.initialAttr) {
                // attr передан объектом — id в поиске не участвует; присвоить до
                // регистрации `${binding}-CHIPS`, чтобы она шла по этому объекту
                this.attr = this.initialAttr
            }
            if (this.mode === 'Standalone' || this.mode === 'Embedded') {
                if (!this.initialAttr) {
                    this.store.widget_attr_set(this, false)
                }
                if (this.attr?.binding) {
                    if (this.attr.attr_type !== 'AttrChips') {
                        this.binding = `doc.viewParams.${this.attr.name}.CHIPS`
                    } else {
                        this.binding = this.attr.binding
                    }

                    // IE 2026 10 31 / ODOO-5800 (заказ Игоря: чіпси над тепловою картою, чіпс пошуку): полоса-зеркало (attr.chipsMirror — те же чипсы грида над вкладкой Heatmap)
                    // не перехватывает регистрацию полосы грида: форма строки чипса (widget-row-chip) после сохранения зовёт emitChips по этому ключу — у грида свой отбор
                    if (!this.attr.chipsMirror || !this.WIDGET.attrs_vueObj[`${this.binding}-CHIPS`]) this.WIDGET.attrs_vueObj[`${this.binding}-CHIPS`] = this
                }
            }
        },
        emitChips(opts, list) {   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): list — готовый список (⊗ в режиме Legacy), иначе this.chips
            this.$emit("update:modelValue", list || this.chips, opts);
            this.chipPlace_afterEmit()   // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): входной чипс сменился — перечитать выборку
            // Standalone: notify AttrGrid with same binding
            if (this.mode === 'Standalone' && this.attr?.binding) {
                for (const vueObj of Object.values(this.WIDGET.attrs_vueObj)) {
                    if (vueObj.attr?.binding === this.attr.binding && vueObj.attr?.attr_type === 'AttrGrid') {
                        vueObj.content_Changed()
                        break
                    }
                }
                if (this.WIDGET.widget === 'widget-selectors') {
                    this.store.sync_WORKSPACE(this.WIDGET, '>>WORKSPACE')
                }
            } else if (this.mode === 'Embedded') {
                if (this.attr?.name && this.WIDGET?.record) {
                    const action = this.store.get_value(this.WIDGET.record, `doc.viewParams.${this.attr.name}.action_afterChange`, null, true)
                    if (action) {
                        this.store.executeStoreMethod(this.WIDGET.vueObj, action, this.attr)
                    }
                }
            } else if (this.mode === 'Legacy') {
                const action = this.attr?.action_afterChange
                if (action && this.WIDGET) {
                    this.store.executeStoreMethod(this.WIDGET.vueObj, action, this.attr)
                }
            } else if (this.mode === 'Page') {
                // if (this.attr?.binding) {
                    const WORKSPACE = this.store.activeWORKSPACE
                    this.store.workspace_applySelector(WORKSPACE.workspace, WORKSPACE.active_page)
                // }
            }
        },
        // {M} (заказ Игоря): в редакторе кода (SQL / JSON, CodeMirror) поиск по умолчанию показывает и строки без совпадений —
        // как обычный поиск в редакторе: подсветка и переход к найденному, а не скрытие остального. Для таблиц умолчание прежнее
        // (строки без совпадений скрываются). Решается по видимому хосту виджета в момент создания чипса; чекбокс
        // «Show rows without matches» в меню чипса переключает как раньше
        // IE 2026 10 24 / ODOO-348769 (заказ Игоря: «при создании чипса поиска в таблице — ставь по умолчанию "показать строки без совпадений"»): чипс поиска,
        // набранный в полосе чипсов таблицы (хост — грид), создаётся с включённым «Show rows without matches»: строки не прячутся, найденное подсвечено,
        // ‹ › ходят по найденным; снять можно в меню чипса. Выключатель fe.ux.gridSearchShowAll (дефолт вкл). IE 2026 10 30 / ODOO-348769 (уточнение Игоря 30.10): умолчание
        // флажка у нового чипса таблицы — ВЫКЛ (fe.ux.gridSearchChipShowAll дефолт false; true — прежнее поведение 24.10). Чипсы стола (mode Page),
        // style:/= row.x/non-empty и выбор из дропдауна — без изменений
        searchChip_defaultShowAll() {
            if (this.mode === 'Page') return false
            // владелец полосы чипсов — ближайший хост вверх по $parent: AttrStrMulti / AttrJSON держат CodeMirror в this.editor;
            // грид (flex) — нет; между полосой и хостом могут быть обёртки
            let p = this.$parent
            for (let i = 0; i < 8 && p; i++, p = p.$parent) {
                if (p.editor && typeof p.editor.getWrapperElement === 'function') return true
                if (p.flex) return this.store.setting('fe.ux.gridSearchShowAll', true) !== false && this.store.setting('fe.ux.gridSearchChipShowAll', false) === true   // IE 2026 10 24 / ODOO-348769; IE 2026 10 27 / ODOO-5800 (решение Q1); IE 2026 10 30 / ODOO-348769 (уточнение Игоря 30.10, скрин «Реєстр відповідальних»): по умолчанию ВЫКЛ — Enter сужает таблицу; fe.ux.gridSearchChipShowAll = true — чипс создаётся с «Show rows without matches»
            }
            return false
        },
        addChip() {
            if (this.newChipText.trim() !== "") {
                const newChip = {
                    segment: "CHIP-Search",
                    title: this.newChipText.trim(),
                    meta: {
                        use: true,
                        pi: "pi-search",
                    }
                };
                if (this.searchChip_defaultShowAll()) newChip.showAllLines = true   // {M}
                this.chipsRaw.push(newChip);
                if (this.liveSearch_on()) this.liveSearch_emit('', false)   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): Enter — текст стал чипсом; живой поиск снимается без своего прохода (проход сделает emitChips ниже)
                this.emitChips();
                this.newChipText = "";
                nextTick(() => { this.$refs.input?.focus() })
            }
        },
        onPaste(event) {
            const fileItems = Array.from(event.clipboardData?.items || [])
                .filter(i => i.kind === 'file')
                .map(i => i.getAsFile())
                .filter(Boolean)

            if (fileItems.length) {
                let pending = fileItems.length
                for (const file of fileItems) {
                    const isJSON = file.type === 'application/json' || file.name?.endsWith('.json')
                    const isImage = !isJSON && file.type.startsWith('image/')
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        if (isJSON) {
                            try {
                                const chip = JSON.parse(e.target.result)
                                this.check_chip(chip)
                                this.chipsRaw.push(chip)
                            } catch { /* ignore malformed json */ }
                        } else {
                            const src = e.target.result
                            this.chipsRaw.push(isImage   // IE 2026 09 23 / ODOO-341407: i18n
                                ? { segment: 'CHIP-Image', title: file.name || this.t('Screenshot'), meta:{use: true, pi: 'pi-image'}, doc:{src, fileType:file.type} }
                                : { segment: 'CHIP-File', title: file.name || this.t('File'), meta:{use: true, pi: 'pi-file'}, doc:{src, fileType:file.type} }
                            )
                        }
                        if (--pending === 0) this.emitChips()
                    }
                    isJSON ? reader.readAsText(file) : reader.readAsDataURL(file)
                }
                return
            }

            const text = event.clipboardData?.getData('text') || ''
            if (!text) return
            let chip
            try { chip = this.store.parseClipboardWidget(text) } catch { return }
            // JSON-чип — только для структур (объект/массив); JSON-примитив
            // (строка в кавычках, число) — обычный текст поиска
            if (chip && typeof chip === 'object') {
                if (!chip.segment) {
                    chip = {
                        segment: 'CHIP-JSON',
                        title: this.t('JSON'),   // IE 2026 09 23 / ODOO-341407: i18n
                        doc: chip,
                    }
                }
            } else {
                this.newChipText = text.length > 150 ? text.slice(0, 150) : text
                nextTick(() => { this.$refs.input?.focus() })
                return
            }

            this.check_chip(chip)

            if (chip) {
                this.chipsRaw.push(chip)
                this.emitChips()
            }
        },
        copyChip(index) {
            const chip = this.visibleChips[index]
            if (this.mode === 'Page' && chip && !chip._global && this.chipsBar_flag('fe.ux.chipsBarCopy') && this.chipsBar_copy(chip)) return   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): чипс стола — переносимым текстом (вставка в поле полосы другого стола)
            // if ((chip.segment === 'CHIP-Image' || chip.segment === 'CHIP-File') && chip.doc.src) {
            //     const blob = this._dataURLtoBlob(chip.doc.src)
            //     navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]).catch(() => {
            //         navigator.clipboard.writeText(JSON.stringify(chip)).catch(() => {})
            //     })
            //     return
            // }
            if (!this.chipsBar_clipboardOk()) return   // IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою): нет буфера (insecure context / API нет) — молча, без ошибки в консоли
            navigator.clipboard.writeText(JSON.stringify(chip)).catch(() => {})
        },
        downloadChip(index) {
            const chip = this.visibleChips[index]
            const raw = '_raw' in chip ? chip._raw : chip
            const resolvedTitle = this._resolveTitle(raw).text || raw.title || 'chip'
            let blob, filename
            if ((raw.segment === 'CHIP-Image' || raw.segment === 'CHIP-File') && raw.doc?.src) {
                blob = this._dataURLtoBlob(raw.doc.src)
                filename = `${resolvedTitle}${this._mimeToExt(blob.type)}`
            } else {
                blob = new Blob([JSON.stringify(raw, null, 2)], { type: 'application/json' })
                filename = `${resolvedTitle}.json`
            }
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            a.click()
            URL.revokeObjectURL(url)
        },
        _mimeToExt(mime) {
            const map = {
                'image/png': '.png', 'image/jpeg': '.jpg', 'image/gif': '.gif',
                'image/webp': '.webp', 'image/svg+xml': '.svg', 'image/bmp': '.bmp',
                'application/pdf': '.pdf', 'text/plain': '.txt', 'text/csv': '.csv',
                'application/zip': '.zip',
            }
            return map[mime] || ''
        },
        _resolveChipValues() {
            const change_type = this.store.itemsSource.change_type
            if (!change_type) return

            let changed = false
            const resolveAttr = (V) => {
                if (V?.attr_type === 'AttrLink' && V.relation_segment === change_type) {
                    this.store.get_valueStr(this, V, V)
                    changed = true
                }
            }
            const resolveChip = (chip) => {
                resolveAttr(chip.doc?.leftValue)
                resolveAttr(chip.doc?.rightValue)
                resolveAttr(chip.doc?.attr)
                resolveAttr(chip.doc?.node)
                resolveAttr(chip.doc?.widget)
                resolveAttr(chip.doc?.record)
                if (chip.doc?.WHERE?.length) {
                    for (const row of chip.doc.WHERE) {
                        resolveAttr(row.leftValue)
                        resolveAttr(row.rightValue)
                        if (row.attrs?.length) for (const sub of row.attrs) {
                            if (sub.rightValue && !sub.rightValue.attr_type) sub.rightValue.attr_type = row.rightValue?.attr_type
                            if (sub.rightValue && !sub.rightValue.relation_segment) sub.rightValue.relation_segment = row.rightValue?.relation_segment
                            resolveAttr(sub.rightValue)
                        }
                    }
                }
                if (chip.attrs?.length) {
                    for (const chip2 of chip.attrs) {
                        resolveAttr(chip2.doc?.rightValue)
                        resolveChip(chip2)
                    }
                }
                if (chip.attrs?.length) {
                    for (const sub of chip.attrs) resolveChip(sub)
                }
            }
            for (const chip of this.chips) resolveChip(chip)
            for (const tab of this.dropdownTabs) {
                if (tab.attrs) for (const item of tab.attrs) resolveChip(item)
            }
            if (changed) {
                this.resolveIndex++
            }
        },
        _resolveChipValues_all() {
            if (!this.chips?.length) return
            for (const chip of this.chips) {
                this._resolveChipAttrLinks(chip)
            }
            this._resolveDropdownAttrLinks()
            this.resolveIndex++
        },
        _resolveChipAttrLinks(chip) {
            if (chip.doc?.leftValue?.attr_type === 'AttrLink' && chip.doc.leftValue.relation_segment) {
                this.store.get_valueStr(this, chip.doc.leftValue, chip.doc.leftValue)
            }
            if (chip.doc?.rightValue?.attr_type === 'AttrLink' && chip.doc.rightValue.relation_segment) {
                this.store.get_valueStr(this, chip.doc.rightValue, chip.doc.rightValue)
            }
            if (chip.doc?.attr?.attr_type === 'AttrLink' && chip.doc.attr.relation_segment) {
                const prev = chip.doc.attr.valueStr
                if (!this.store.get_valueStr(this, chip.doc.attr, chip.doc.attr) && prev) {
                    chip.doc.attr.valueStr = prev
                }
            }
            if (chip.doc?.node?.attr_type === 'AttrLink' && chip.doc.node.relation_segment) {
                const prev = chip.doc.node.valueStr
                if (!this.store.get_valueStr(this, chip.doc.node, chip.doc.node) && prev) {
                    chip.doc.node.valueStr = prev
                }
            }
            if (chip.doc?.widget?.attr_type === 'AttrLink' && chip.doc.widget.relation_segment) {
                const prev = chip.doc.widget.valueStr
                if (!this.store.get_valueStr(this, chip.doc.widget, chip.doc.widget) && prev) {
                    chip.doc.widget.valueStr = prev
                }
            }
            if (chip.doc?.record?.attr_type === 'AttrLink' && chip.doc.record.relation_segment) {
                const prev = chip.doc.record.valueStr
                if (!this.store.get_valueStr(this, chip.doc.record, chip.doc.record) && prev) {
                    chip.doc.record.valueStr = prev
                }
            }
            if (chip.doc?.WHERE?.length) {
                for (const row of chip.doc.WHERE) {
                    if (row.leftValue?.attr_type === 'AttrLink' && row.leftValue.relation_segment && row.leftValue?.value) {
                        this.store.get_valueStr(this, row.leftValue, row.leftValue, 'no', [row.leftValue.value])
                    }
                    if (row.rightValue?.attr_type === 'AttrLink' && row.rightValue.relation_segment && row.rightValue?.value) {
                        this.store.get_valueStr(this, row.rightValue, row.rightValue, {ids:[row.rightValue.value]})
                    }
                    if (row.attrs?.length) {
                        for (const sub of row.attrs) {
                            if (!sub.rightValue) continue
                            if (!sub.rightValue.attr_type) sub.rightValue.attr_type = row.rightValue?.attr_type
                            if (!sub.rightValue.relation_segment) sub.rightValue.relation_segment = row.rightValue?.relation_segment
                            if (sub.rightValue.attr_type === 'AttrLink' && sub.rightValue.relation_segment && sub.rightValue.value) {
                                this.store.get_valueStr(this, sub.rightValue, sub.rightValue)
                            }
                        }
                    }
                }
            }
            if (chip.doc?.attrs?.length) {
                for (const chip2 of chip.doc.attrs) {
                    if (chip2.doc?.rightValue?.attr_type === 'AttrLink' && chip2.doc.rightValue.relation_segment) {
                        this.store.get_valueStr(this, chip2.doc.rightValue, chip2.doc.rightValue)
                    }
                    this._resolveChipAttrLinks(chip2)
                }
            }
            if (chip.attrs?.length) {
                for (const sub of chip.attrs) {
                    this._resolveChipAttrLinks(sub)
                }
            }
        },
        _resolveDropdownAttrLinks() {
            for (const tab of this.dropdownTabs) {
                if (!tab.attrs?.length) continue
                for (const item of tab.attrs) {
                    this._resolveChipAttrLinks(item)
                }
            }
        },
        // ODOO-4505: строка-контейнер (набор пар selector_map_pairs) не несёт
        // rightValue — значения лежат уровнем глубже; разворачиваем рекурсивно,
        // иначе чип и тултип показывали «(1)»/«(0)» вместо списка комбинаций
        _flatWhereAttrs(rows) {
            const out = []
            for (const r of rows || []) {
                if (!r || r.use === false) continue
                if (!r.rightValue && r.attrs?.length) out.push(...this._flatWhereAttrs(r.attrs))
                else out.push(r)
            }
            return out
        },
        // IE 2026 09 23 / ODOO-341407: i18n — подпись оператора условия в заголовке чипса; КОДЫ условий (row.condition: 'between', 'in', 'NOT in',
        // 'LIKE', 'NOT LIKE', '=', '<>', …) остаются английскими — по ним сравнения (periodSel_row, chipPeriod, antClock, selector_text бэка)
        chipRow_isPeriod(row) {   // строка периода стола (mainStore.periodSel_row): флаг period либо английская база подписи 'Period' (valueStr_fix)
            const lv = row?.leftValue
            return !!row?.period || !!(lv && typeof lv === 'object' && lv.valueStr_fix && (lv.valueStr_en || lv.valueStr) === 'Period')
        },
        conditionLabel(condition) {
            switch (condition) {
                case 'between': return this.t('between')
                case 'in': return this.t('in')
                case 'NOT in': return this.t('NOT in')
                case 'LIKE': return this.t('LIKE')
                case 'NOT LIKE': return this.t('NOT LIKE')
                default: return condition
            }
        },
        _resolveTitle(chip) {
            if (chip.segment === 'CHIP-Where' || chip.segment === 'CHIP-Selector') {
                const WHERE = chip.doc.WHERE
                const activeWHERE = WHERE?.filter(r => r.use !== false)
                const row = activeWHERE?.[0] || WHERE?.[0] || chip.doc  // fallback to old flat structure
                // IE 2026 09 23 / ODOO-341407: i18n — строка периода (mainStore.periodSel_row: leftValue.valueStr 'Period' — английская база, ключ
                // словарей фронта и бэка, в данных не переводится) показывается переводом; оператор условия — подписью conditionLabel()
                const _lv = row.leftValue
                const _isPeriod = this.chipRow_isPeriod(row)
                const leftStr = _isPeriod ? this.t('Period') : (_lv?.valueStr || '')
                const leftHTML = _isPeriod ? leftStr : (this._formatAttrValueHTML(_lv) || leftStr)
                const condition = row.condition || ''
                const conditionText = this.conditionLabel(condition)
                if (row.attrs?.length) {
                    const activeAttrs = this._flatWhereAttrs(row.attrs)
                    const values = activeAttrs.map(r =>
                        r.rightValue?.valueStr || r.rightValue?.value || ''
                    ).filter(Boolean)
                    const valuesHTML = activeAttrs.map(r =>
                        this._formatAttrValueHTML(r.rightValue) || r.rightValue?.valueStr || r.rightValue?.value || ''
                    ).filter(Boolean)
                    const count = activeAttrs.length
                    const maxVisible = 2
                    if (values.length) {
                        const rest = values.length - maxVisible
                        const rightText = rest > 0
                            ? values.slice(0, maxVisible).join(', ') + ` +${rest}`
                            : values.join(', ')
                        const text = count > 1
                            ? `${leftStr} (${count}): ${rightText}`
                            : `${leftStr}: ${rightText}`
                        const rightHTML = rest > 0
                            ? valuesHTML.slice(0, maxVisible).join(', ') + ` <span class="chip-title-rest">+${rest}</span>`
                            : valuesHTML.join(', ')
                        const html = count > 1
                            ? `<span class="chip-title-left">${leftHTML}</span> <span class="chip-title-count">(${count})</span> ${rightHTML}`
                            : `<span class="chip-title-left">${leftHTML}</span> ${rightHTML}`
                        const extra = activeWHERE?.length > 1 ? ` +${activeWHERE.length - 1}` : ''
                        const extraHTML = activeWHERE?.length > 1 ? ` <span class="chip-title-count">+${activeWHERE.length - 1}</span>` : ''
                        return { text: text + extra, html: html + extraHTML }
                    }
                    const text = `${leftStr} (${count})`
                    const html = `<span class="chip-title-left">${leftHTML}</span> <span class="chip-title-count">(${count})</span>`
                    return { text, html }
                }
                const right = row.rightValue?.valueStr || ''
                const rightHTML = this._formatAttrValueHTML(row.rightValue) || right
                let text = right ? `${leftStr} ${conditionText} ${right}` : leftStr   // IE 2026 09 23 / ODOO-341407: i18n — подпись оператора
                let html = right
                    ? `<span class="chip-title-left">${leftHTML}</span> <span class="chip-title-condition">${conditionText}</span> ${rightHTML}`
                    : `<span class="chip-title-left">${leftHTML}</span>`
                if (activeWHERE?.length > 1) {
                    text += ` +${activeWHERE.length - 1}`
                    html += ` <span class="chip-title-count">+${activeWHERE.length - 1}</span>`
                }
                return { text, html }
            }
            if (chip.segment === 'CHIP-Param') {
                const title = chip.doc.attr?.title || ''
                const valueStr = chip.doc.attr?.valueStr || chip.doc.attr?.value || ''
                const text = valueStr ? `${title}: ${valueStr}` : title
                const html = `<span class="chip-title-left">${title}</span> ${this._formatAttrValueHTML(chip.doc.attr)}`
                return { text, html }
            }
            if (chip.segment === 'CHIP-Node') {
                const valueStr = chip.node?.valueStr || chip.node?.value || ''
                const text = valueStr
                const html = valueStr ? `<span class="chip-title-left">${valueStr}</span>` : ''
                return { text, html }
            }
            if (chip.segment === 'CHIP-Widget') {
                const valueStr = chip.doc.widget?.title || ''
                return { text: valueStr, html: valueStr ? `<span class="chip-title-left">${valueStr}</span>` : '' }
            }
            if (chip.segment === 'CHIP-Record') {
                const valueStr = chip.doc.record?.title || ''
                return { text: valueStr, html: valueStr ? `<span class="chip-title-left">${valueStr}</span>` : '' }
            }
            if (chip.segment === 'CHIP-Rows') {
                const cells = chip.doc?.cells || chip.meta?.source?.cells || []
                const titles = [...new Set(cells.map(c => c.title || c.binding))].join(', ')
                const text = titles || this.tn(cells.length, '{n} cell', '{n} cells')   // IE 2026 09 23 / ODOO-341407: i18n
                return { text, html: `<span class="chip-title-left">${text}</span>` }
            }
            if (chip.segment === 'CHIP-Cell') {
                const cells = chip.doc?.cells || chip.meta?.source?.cells || []
                const titles = [...new Set(cells.map(c => c.title || c.binding))].join(', ')
                const text = titles || this.tn(cells.length, '{n} cell', '{n} cells')   // IE 2026 09 23 / ODOO-341407: i18n
                return { text, html: `<span class="chip-title-left">${text}</span>` }
            }
            if (chip.segment === 'CHIP-CellFilters') {
                const cells = chip.doc?.cells || chip.meta?.source?.cells || []
                const titles = [...new Set(cells.map(c => c.title || c.binding))].join(', ')
                const text = titles || this.tn(cells.length, '{n} cell', '{n} cells')   // IE 2026 09 23 / ODOO-341407: i18n
                return { text, html: `<span class="chip-title-left">${text}</span>` }
            }
            return { text: '', html: '' }
        },
        _formatAttrValueHTML(attr) {
            if (!attr) return ''
            const valueStr = attr.valueStr || attr.value || ''
            if (!valueStr) return ''
            const attrType = attr.attr_type
            if (attrType === 'AttrLink' && attr.relation_segment) {
                const pi = this.store.get_value_segment(attr.relation_segment, 'pi', 'pi pi-arrow-right')
                return valueStr // `<i class="cell-link mr-1 ${pi}"></i>${valueStr}`
            }
            if (attrType === 'AttrDate') {
                return this.store.formatDateHTML(valueStr)
            }
            return valueStr
        },
        getChipTitleHTML(chip) {
            if (chip._mirror) return this.metricChip_titleHTML(chip.doc, this.metricMirror_rules())   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом)
                + (chip.fallback ? `<span class="chip-metric-approx" style="opacity:.6;margin-left:3px" title="${this.store.escapeHtml(this.t('Matched by signal: the horizon differs at this level'))}">≈</span>` : '')   // IE 2026 10 04 / ODOO-5800 (fe-76): сопоставлено по основе сигнала
            const raw = '_raw' in chip ? chip._raw : chip
            if (raw.segment === 'CHIP-Image' && raw.doc.src) {
                return `<img src="${raw.doc.src}" style="max-height:18px;vertical-align:middle;border-radius:2px;margin-right:2px;" />`
            }
            if (raw.segment === 'CHIP-Limit') {
                const limit = raw.doc?.limit
                const offset = raw.doc?.offset || 0
                const total = raw.doc?.totalCount
                const dataLimit = raw.doc?.dataLimit ?? null
                if (!limit) {
                    if (dataLimit != null) {
                        const bytesHTML = this.store.formatNumberHTML(dataLimit, { format: 'bytes' })
                        return `<span class="chip-title-left">${this.t('Data limit')}</span><span class="chip-title-condition"> ${bytesHTML}</span>`   // IE 2026 09 23 / ODOO-341407: i18n
                    }
                    if (offset > 0) {
                        return `<span class="chip-title-left">${this.t('Offset')}</span><span class="chip-title-condition"> ${offset}</span>`   // IE 2026 09 23 / ODOO-341407: i18n
                    }
                    return `<span class="chip-title-left">${this.t('Limit')}</span>`   // IE 2026 09 23 / ODOO-341407: i18n
                }
                const page = Math.max(offset > 0 ? 2 : 1, Math.floor(offset / limit) + 1)
                if (total != null) {
                    const totalPages = Math.max(page, Math.ceil(total / limit)) // total — полное число записей, как в chipLimit_canNext
                    return `<span class="chip-title-page">${page}</span><span class="chip-title-of"> / ${totalPages}</span>`
                }
                return `<span class="chip-title-page">${page}</span>`
            }
            if (raw.segment === 'CHIP-Info') {
                return this.store.escapeHtml(String(chip.title ?? ''))
            }
            // IE 2026 09 24 / ODOO-341407: чипсы дерева/порядка списков (title 'Parent' / 'Order' — литерал кода в viewParams записи) — перевод при показе
            // IE 2026 09 24 / ODOO-341407 (второй обход dev02: чипс «Type», «Level, Country», «Date to, Date from» — ключей нет): составной заголовок — по частям
            // IE 2026 10 29 / ODOO-5800 (ie-dm7 #115652): CHIP-Order — подписи полей как у заголовков колонок (attr_by_binding[…].title на языке пользователя), а не снимок chip.title
            if (!chip.meta?.titleHTML && raw.segment === 'CHIP-Order') {
                const ot = this.orderChip_title_live(raw)
                if (ot != null) return this.store.escapeHtml(ot)
            }
            if (!chip.meta?.titleHTML && typeof chip.title === 'string' && (raw.segment === 'CHIP-Tree' || raw.segment === 'CHIP-Order')) return this.store.escapeHtml(chip.title.split(', ').map(p => this.t(p, null, 'chip')).join(', '))
            return chip.meta?.titleHTML || this.chipTitle_lang(chip)   // IE 2026 10 12 / ODOO-5800: было chip.title — подпись на языке пользователя
        },
        // IE 2026 10 29 / ODOO-5800 (ie-dm7 #115652): грид-хост чипса (Embedded — родитель, иначе грид того же binding в виджете) → его attr_by_binding; без грида — null (прежний путь)
        orderChip_title_live(raw) {
            try {
                if (!chipTitleLang_on((k, d) => this.store.setting(k, d))) return null
                this.store.get_lang?.()   // зависимость рендера от языка (смена языка без reload)
                let grid = this.$parent?.attr_by_binding ? this.$parent : null
                if (!grid && this.attr?.binding) grid = Object.values(this.WIDGET?.attrs_vueObj || {}).find(vo => vo?.attr?.binding === this.attr.binding && vo.attr_by_binding) || null
                const abb = grid?.attr_by_binding
                if (!abb || !Object.keys(abb).length) return null
                return orderChip_title(raw, abb, p => this.t(p, null, 'chip'))
            } catch (e) { return null }
        },
        // IE 2026 10 12 / ODOO-5800: title_<язык> чипса (utils/chipTitleLang; флаг fe.ux.chipTitleLang, выкл — title)
        chipTitle_lang(chip) {
            try { return chipTitleLang_on((k, d) => this.store.setting(k, d)) ? chip_titleLang(chip, this.store.get_lang?.()) : chip.title } catch (e) { return chip?.title }
        },
        // ---- IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): чипс метрик списка (CHIP-Metric) ----
        // Словарь метрик виджета и отметки строк отдаёт бэк (attr.metric_rules, _DLRow.metrics); отбор строк — AttrGrid
        // (apply_CHIPS / check_CHIPS, ветка _metric), чистая логика — utils/metricChip.js. Выключатель fe.ux.metricChip
        metricChip_is(chip) { return !!chip && (chip.segment === 'CHIP-Metric' || (chip._raw || chip).segment === 'CHIP-Metric') },
        // счётчик, ‹ ›, «Show rows without matches» — у чипсов над таблицей; чипс метрик шапки режет выборку на входе, переходить не по чему
        // IE 2026 10 05 / ODOO-5800 (fe-82) (Igor 05.10, чипс от Selector строки грида сигналов: «ожидал, что чипс селектора будет иметь переход и ходить по … ячейкам»):
        // счётчик и стрелки ‹ › по числу остановок; у чипса метрик единственная остановка тоже показывается («1/1» и стрелки — переход раскрывает группы и выделяет
        // ячейку сигнала), у поиска и чипса оформления — как было (решение 21.07). Выключатель fe.ux.metricNavSingle (false — прежний вид)
        // IE 2026 10 07 / ODOO-5800 (Igor 05.10: «когда чип поиска находит одно значение, то нужна кнопка ">", по которой будет произведён переход к найденной строке или ячейке; то же для
        // чипса-метрики и других чипсов поиска, где при нескольких есть "<>"»): ровно одно совпадение — одна кнопка › (.go), то же действие, что у «следующего»; её видят и поиск таблицы,
        // и чипс метрик (вместо пары ‹ › fe-82), и чипс оформления, и поиск окна кода / записи (хосты AttrGrid / AttrJSON / AttrStrMulti). Выключатель fe.ux.chipNavSingleGo (false — прежний вид)
        chipMatch_show(chip) { const raw = chip._raw || chip; return metricNav_show(raw.matchCount, raw.segment === 'CHIP-Metric' && this.store?.setting?.('fe.ux.metricNavSingle', true) !== false, this.store?.setting?.('fe.ux.chipNavSingleGo', true) !== false) },
        // IE 2026 10 05 / ODOO-5800 (fe-88) (замер ботом dev02 fe-84: счётчик «1/5» сразу после флажка Selector, первый клик › — на «2/5»): текст счётчика —
        // utils/metricChip.metricNav_badge. У чипса метрик до первого перехода ‹ › номер остановки не показывается (matchIndex = -1, AttrGrid.metricNav_recompute,
        // выключатель fe.ux.metricNavFirstStop) — только число остановок; первый клик › ведёт на первую. У поиска и чипса оформления номер есть всегда — текст прежний
        chipMatch_text(chip) { const raw = chip._raw || chip; return metricNav_badge(raw.matchCount, raw.matchIndex) },
        // IE 2026 10 05 / ODOO-5800 (fe-86) (решение Игоря 05.10 по замеру selector_nav2 — «Комірок» грида сигналов 2, чипс «1/1»: «Показывать «1/1» с подсказкой «ещё 1 в выключенной
        // опции». Счёт честный, вид отчёта не меняется сам»): счётчик по-прежнему считает только достижимые ячейки, рядом — приглушённый хвост «+N»: столько ячеек
        // сигнала прибавится в обход, если включить выключенные опции отчёта (считает AttrGrid.metricNav_recompute → поля чипса matchMore / matchMoreIn); '' — таких
        // нет. Строка о них для подсказки счётчика — vars.more записи chips.badge.matches (utils/uiHelp/chips.js). Выключатель fe.ux.metricNavHiddenHint — в гриде.
        // vars.kind — вид чипса: у чипса метрик свой текст «What it is» (единственная ячейка показывается; прежний говорил «одно совпадение — бейджа нет»), и при «0»
        chipMore_text(chip) { const raw = chip._raw || chip; return raw.segment === 'CHIP-Metric' ? metricNav_moreBadge(raw.matchMore) : '' },
        // IE 2026 10 05 / ODOO-5800 (fe-86, доработка по ревью): карточка подсказки следует выключателям счётчика — их значения уходят в vars (single / first / hidden)
        chipMore_vars(chip) { const raw = chip._raw || chip, metric = raw.segment === 'CHIP-Metric', on = (k) => this.store?.setting?.(k, true) !== false; return { kind: metric ? 'metric' : '', more: metric ? metricNav_moreText(raw.matchMore, raw.matchMoreIn) : '', single: on('fe.ux.metricNavSingle'), first: on('fe.ux.metricNavFirstStop'), hidden: on('fe.ux.metricNavHiddenHint') } },
        chipNav_on(chip) { if (chip._global) return false; const seg = (chip._raw || chip).segment; return seg === 'CHIP-Search' || seg === 'CHIP-Metric' || (seg === 'CHIP-Format' && this.formatChip_on()) },   // IE 2026 09 20 / ODOO-5800: и чипс условного оформления
        // словарь метрик: над таблицей — виджета (attr.metric_rules), в шапке стола — всей витрины (metric_chip_keys бэка)
        metricChip_rules(global = this.mode === 'Page') {
            if (global) return this.metricG.keys || []
            return Array.isArray(this.attr?.metric_rules) ? this.attr.metric_rules : []
        },
        // IE 2026 09 19 / ODOO-5800 (Igor: измерение horizon в ключе метрики): словарь с горизонтами (ключи из четырёх частей) — в форме чипса список Horizon, а
        // комбинация читается «Grade + Metric + Reason + Horizon»; словарь без горизонтов (бэк без режима) — форма как была
        metricChip_h4(chip) { return metricRules_h4(chip?._mirror ? this.metricMirror_rules() : this.metricChip_rules()) },
        // IE 2026 09 21 / ODOO-5800 (Igor: измерение «статус документа» в ключе метрики): словарь со статусами документов — в форме чипса список Status, комбинация читается
        // «… + Horizon + Status»
        metricChip_s5(chip) { return metricRules_s5(chip?._mirror ? this.metricMirror_rules() : this.metricChip_rules()) },
        // IE 2026 09 23 / ODOO-341407: i18n — подписи формы чипса метрик (названия видов из METRIC_CHIP_KINDS — utils/metricChip)
        // IE 2026 10 04 / ODOO-5800 (fe-74) (Igor 04.10: чипс — словами грида сигналов): словарь несёт поля сигналов и флаг fe.metricChips.signalsV2 включён — секции Kind / Grade / Signal,
        // комбинация читается «Grade · Signal · Reason · Horizon · Status»; иначе форма прежняя
        metricChip_v2(chip) { return signalsV2_on() && metricRules_sig(chip?._mirror ? this.metricMirror_rules() : this.metricChip_rules()) },
        // IE 2026 10 04 / ODOO-5800 (fe-78): словарь новой модели сигналов (записи несут signal_kind_id) и флаг fe.signals.modelV2 — секции по записям справочников:
        // Kind / Grade / Signal / Cause / Horizon / Status, комбинация читается «Grade · Signal · Cause · Horizon · Status»
        metricChip_m2(chip) { return this.metricChip_v2(chip) && modelV2_on() && sigRules_isV2(chip?._mirror ? this.metricMirror_rules() : this.metricChip_rules()) },
        metricChip_comboTitleM2(chip) { return [this.t('Grade', null, 'signal'), this.t('Signal'), this.t('Cause'), ...(this.metricChip_h4(chip) ? [this.t('Horizon')] : []), ...(this.metricChip_s5(chip) ? [this.t('Status')] : [])].join(' · ') },
        metricChip_kindTitle(kind, chip) {
            if (kind === 'reasons') return this.t('Cause')   // IE 2026 10 04 / ODOO-5800 (fe-78)
            if (kind === 'combos' && this.metricChip_m2(chip)) return this.metricChip_comboTitleM2(chip)   // IE 2026 10 04 / ODOO-5800 (fe-78)
            if (this.metricChip_v2(chip)) {
                if (kind === 'combos') return this.metricChip_s5(chip) ? this.t('Grade · Signal · Reason · Horizon · Status') : (this.metricChip_h4(chip) ? this.t('Grade · Signal · Reason · Horizon') : this.t('Grade · Signal · Reason'))
                if (kind === 'grades') return this.t('Grade', null, 'signal')
            }
            if (kind === 'combos' && this.metricChip_h4(chip)) return this.metricChip_s5(chip) ? this.t('Grade + Metric + Reason + Horizon + Status') : this.t('Grade + Metric + Reason + Horizon')
            const _ttl = (METRIC_CHIP_KINDS.find(([k]) => k === kind) || [])[1]   // IE 2026 09 23 / ODOO-341407: i18n — [1] — ленивая подпись (utils/metricChip)
            return (typeof _ttl === 'function' ? _ttl() : _ttl) || kind
        },
        metricChip_ruleHint(chip) {
            if (this.metricChip_m2(chip)) {   // IE 2026 10 04 / ODOO-5800 (fe-78): (Kind and Grade and Signal and Cause and Horizon and Status) or Grade · Signal · Cause · Horizon · Status
                const dims = [this.t('Kind'), this.t('Grade', null, 'signal'), this.t('Signal'), this.t('Cause'), ...(this.metricChip_h4(chip) ? [this.t('Horizon')] : []), ...(this.metricChip_s5(chip) ? [this.t('Status')] : [])]
                return '(' + dims.join(' ' + this.t('and') + ' ') + ') ' + this.t('or') + ' ' + this.metricChip_comboTitleM2(chip)
            }
            if (this.metricChip_v2(chip)) {   // IE 2026 10 04 / ODOO-5800 (fe-74)
                if (this.metricChip_h4(chip) && this.metricChip_s5(chip)) return this.t('(Kind and Grade and Signal and Horizon and Status) or Grade · Signal · Reason · Horizon · Status')
                return this.metricChip_h4(chip) ? this.t('(Kind and Grade and Signal and Horizon) or Grade · Signal · Reason · Horizon') : this.t('(Kind and Grade and Signal) or Grade · Signal · Reason')
            }
            if (this.metricChip_h4(chip) && this.metricChip_s5(chip)) return this.t('(Grade and Metric and Horizon and Status) or Grade + Metric + Reason + Horizon + Status')
            return this.metricChip_h4(chip) ? this.t('(Grade and Metric and Horizon) or Grade + Metric + Reason + Horizon') : this.t('(Grade and Metric) or Grade + Metric + Reason')
        },
        metricChip_countTitle(chip, kind) {
            if (chip?._global) return kind === 'combos' ? this.t('Calculation nodes that carry this metric (under the other chips of the workspace)') : this.t('Calculation nodes by the rules of this value (a node with two reasons counts twice)')
            return kind === 'combos' ? this.t('Rows of the current selection under this rule') : this.t('Hits of the rules (a row with two reasons counts twice)')
        },
        // ---- IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): чипс метрик шапки стола ----
        // Выключатели: fe.ux.metricChip (чипс метрик вообще) и fe.ux.metricChipGlobal (чипс шапки; выключен — строка стола рисуется обычным чипсом)
        metricChipG_on() { return this.mode === 'Page' && this.store?.setting?.('fe.ux.metricChip', true) !== false && this.store?.setting?.('fe.ux.metricChipGlobal', true) !== false },
        metricChipG_rowNow() { return (this.chipsRaw || []).find(r => r && r.chip_segment === 'CHIP-Metric') || null },
        metricChipG_offered() {
            // IE 2026 10 04 / ODOO-5800 (fe-78; dev02_check28): после ⊗ строка стола выключена, но отбор в ней остаётся — пункт «Signals» прятался, новый чипс через меню
            // было не создать. Теперь выключенная строка пункт не прячет (её отбор — отдельной записью списка с флажком). Правило — utils/metricChip.metricChipG_offer;
            // выключатель fe.signals.modelV2 = false — как fe-77 (чипс уже есть: действующий — в полосе, выключенный — в списке с флажком, пункта нет)
            return metricChipG_offer({ on: this.metricChipG_on(), draft: this.metricG.draft, row: this.metricChipG_rowNow(), hasReport: !!this.store.metricChipG_report(), offDisabled: modelV2_on() })
        },
        // отпечаток прочих действующих чипсов стола: числа словаря считаются под ними
        metricChipG_sig() {
            return JSON.stringify((this.chipsRaw || []).filter(r => r && r.use !== false && r.chip_segment !== 'CHIP-Metric' && !String((r.leftValue || {}).value || '').startsWith('calc'))
                .map(r => [(r.leftValue || {}).value, (r.rightValue || {}).value || '', (r.rightValue || {}).valueTo || '', (r.attrs || []).filter(a => a && a.use !== false).map(a => (a.rightValue || {}).value)]))
        },
        // titlesOnly — словарь нужен только для подписи чипса (страница открывается, наборы calc ещё считаются): титулы годятся любые,
        // а числа такого словаря не считаются свежими — открытие формы перечитает его под действующими чипсами стола
        metricChipG_ensureKeys(force = false, titlesOnly = false) {
            const ws = this.store.activeWORKSPACE?.workspace || '', sig = this.metricChipG_sig(), report = String(this.store.metricChipG_report() || '')
            // другой стол или другой отчёт чипса (чипс поставлен из отчёта метрик страницы) — другой словарь
            if (this.metricG.ws !== ws || this.metricG.report !== report) { this.metricG.ws = ws; this.metricG.report = report; this.metricG.keys = null; this.metricG.sig = ''; this._metricLists = null }
            const cache = this.store._metricChipG_cache?.[ws]
            if (!this.metricG.keys && cache?.keys && (cache.report || '') === report) { this.metricG.keys = cache.keys; this.metricG.sig = cache.sig; this._metricLists = null }
            if (this.metricG.loading) { if (!titlesOnly) this._mgWantFresh = true; return }
            if (!force && this.metricG.keys && (titlesOnly || this.metricG.sig === sig)) return
            this.metricG.loading = true
            this._mgWantFresh = false
            this.store.metricChipG_keys((keys) => {
                this.metricG.loading = false
                if (keys && (this.store.activeWORKSPACE?.workspace || '') === ws && String(this.store.metricChipG_report() || '') === report) {
                    const sigNow = titlesOnly ? null : sig
                    this.metricG.keys = keys; this.metricG.sig = sigNow; this._metricLists = null
                    this.store._metricChipG_cache = { ...(this.store._metricChipG_cache || {}), [ws]: { sig: sigNow, keys, report } }
                }
                if (this._mgWantFresh) { this._mgWantFresh = false; this.metricChipG_ensureKeys() }
            })
        },
        metricChipG_localDrop() {
            clearTimeout(this._mgSendTimer); this._mgSendTimer = null; this._mgSentSig = null
            this.metricG.localDoc = null; this.metricG.draft = false
        },
        // состояние меняется — форма остаётся на чипсе метрик (черновик стоит в конце полосы, строка стола — на своём месте)
        metricChipG_keepForm(fn) {
            const open = this.hoverIndex != null && !!this.visibleChips?.[this.hoverIndex]?._global
            fn()
            if (open) { const i = this.visibleChips.findIndex(c => c._global); this.hoverIndex = i >= 0 ? i : null }
        },
        // отбор правится в localDoc и уходит на бэк с паузой: быстрые клики по флажкам — один запрос
        metricChipG_schedule() {
            this._metricDirty = { doc: metricChip_copy(this.metricG.localDoc || {}) }
            clearTimeout(this._mgSendTimer)
            this._mgSendTimer = setTimeout(() => this.metricChipG_send(), this.store?.setting?.('fe.ux.metricChipGlobalDelayMs', 450) ?? 450)
        },
        metricChipG_send() {
            clearTimeout(this._mgSendTimer); this._mgSendTimer = null
            const doc = this.metricG.localDoc
            if (!doc) return
            const row = this.metricChipG_rowNow()
            if (metricChip_isEmpty(doc)) {
                // пустой отбор снимает чипс: строка стола очищается на фронте, форма остаётся на черновике (закроется — черновик уйдёт)
                if (row && row.use !== false) {
                    this._mgSentSig = null
                    this.metricChipG_keepForm(() => { row.use = false; row.attrs = []; row.doc = {}; this.metricG.draft = true; this.resolveIndex++ })
                    this.emitChips()
                } else if (row) { row.attrs = []; row.doc = {} }
                return
            }
            const sig = metricChip_sig(doc)
            if (row && row.use !== false && metricChip_sig(row.doc || {}) === sig) { this.metricChipG_keepForm(() => { this.metricG.localDoc = null; this.metricG.draft = false }); return }
            this._mgSentSig = sig
            const settle = (ok, text) => {
                if (this._mgSentSig !== sig) return   // уже ушёл более новый отбор
                clearTimeout(this._mgSetTimer)
                this._mgSentSig = null
                this.metricChipG_keepForm(() => {
                    if (!ok || (this.metricG.localDoc && metricChip_sig(this.metricG.localDoc) === sig)) { this.metricG.localDoc = null; this.metricG.draft = false }
                    this.resolveIndex++
                })
                if (!ok) this.store.show_notification({ text, status: 'warning', timeout: 9000 })
            }
            clearTimeout(this._mgSetTimer)
            // IE 2026 09 23 / ODOO-341407: i18n — тексты нотификаций
            this._mgSetTimer = setTimeout(() => settle(false, this.t('Metrics chip: no answer from the server — the chip was not applied')), 45000)
            const sent = this.store.metricChipG_set(doc, (ok) => settle(ok, this.t('Metrics chip was not applied: the workspace has no metrics report to resolve it')))
            if (!sent) settle(false, this.t('Metrics chip: no active workspace'))
        },
        // ---- IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): зеркало глобального чипса над таблицей; объект ведёт грид (AttrGrid.metricMirror_sync) ----
        metricMirror_now() {
            const c = this.WIDGET?._metricMirror?.[this.attr?.name]
            if (!c || (c.dismissedSig && c.dismissedSig === c.sig) || this.store?.setting?.('fe.ux.metricChip', true) === false) return null
            if (!(c.known > 0) && this.store?.setting?.('fe.ux.metricMirrorKnownOnly', true) !== false) return null   // IE 2026 09 19 / ODOO-5800 (находка ie-dm7 #30452: зеркало чипса метрик прятало все строки): словарь этой таблицы отбора не знает — чипса над ней нет
            if ((this.chipsRaw || []).some(x => x && x.segment === 'CHIP-Metric' && x.meta?.use !== false && !metricChip_isEmpty(x.doc || {}))) return null   // свой действующий чипс главнее
            return c
        },
        // подписи зеркала: словарь виджета, а для значений, которых в нём нет, — словарь витрины (если форма чипса шапки его уже запрашивала)
        metricMirror_rules() {
            const own = this.metricChip_rules(false)
            const all = this.store._metricChipG_cache?.[this.WIDGET?.workspace]?.keys || []
            if (!all.length) return own
            const have = new Set(own.map(e => e && e.key))
            return [...own, ...all.filter(e => e && !have.has(e.key))]
        },
        metricChipG_openReport(chip) {
            const w = String(chip?._raw?.widget || this.store.metricChipG_report() || '')
            if (w) chipWidget_open(this.store, w)   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
        },
        metricChip_offered() {
            if (this.mode !== 'Embedded' || this.store?.setting?.('fe.ux.metricChip', true) === false) return false
            if (!this.metricChip_rules().length) return false
            return !(this.chipsRaw || []).some(c => c && c.segment === 'CHIP-Metric')
        },
        metricChip_doc(chip) {
            if (chip._global) {   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): чтение — без правки строки стола (её отбор может быть пустым объектом)
                const d = this.metricG.localDoc || chip._raw?.doc || {}
                return METRIC_CHIP_KINDS.every(([k]) => Array.isArray(d[k])) ? d : metricChip_copy(d)
            }
            const raw = chip._raw || chip
            raw.doc = raw.doc || {}
            for (const [k] of METRIC_CHIP_KINDS) if (!Array.isArray(raw.doc[k])) raw.doc[k] = []
            return raw.doc
        },
        metricChip_docEdit(chip) {
            if (!chip._global) return this.metricChip_doc(chip)
            if (!this.metricG.localDoc) this.metricG.localDoc = metricChip_copy(chip._raw?.doc || {})
            for (const [k] of METRIC_CHIP_KINDS) if (!Array.isArray(this.metricG.localDoc[k])) this.metricG.localDoc[k] = []   // IE 2026 10 04 / ODOO-5800 (fe-74): черновик без новых списков (kinds / signals)
            return this.metricG.localDoc
        },
        metricChip_listsNow() {
            const rules = this.metricChip_rules()
            if (this._metricLists?.rules !== rules) this._metricLists = { rules, lists: metricChip_lists(rules) }
            return this._metricLists.lists
        },
        // образец цвета правила: отличия классов правила от обычной ячейки (как образцы вкладки Styles и выбора стиля)
        metricChip_sample(css) {
            const names = String(css || '').split(/\s+/).filter(Boolean)
            if (!names.length) return ''
            const host = this.$el?.closest?.('.WIDGET')?.querySelector?.('.wj-flexgrid') || (this.mode === 'Page' && document.querySelector('.wj-flexgrid')) || document.body
            let out = ''
            for (const n of names) { try { out += cfClassDeltaCss(n, host) || '' } catch (e) { /* без образца */ } }
            return out
        },
        metricChip_selected(chip) {
            return metricChip_parts(this.metricChip_doc(chip), chip._mirror ? this.metricMirror_rules() : this.metricChip_rules()).map(p => ({ ...p, kindTitle: this.metricChip_kindTitle(p.kind, chip), sample: this.metricChip_sample(p.css) }))
        },
        metricChip_groups(chip) {
            const doc = this.metricChip_doc(chip), L = this.metricChip_listsNow()
            const terms = String(this.metricPickText || '').toLowerCase().split(/\s+/).filter(Boolean)
            const esc = (x) => this.store.escapeHtml(String(x))
            const mark = (title) => {
                let html = esc(title)
                for (const t of terms) { const i = html.toLowerCase().indexOf(esc(t)); if (i >= 0) html = html.slice(0, i) + '<b>' + html.slice(i, i + esc(t).length) + '</b>' + html.slice(i + esc(t).length) }
                return html
            }
            // IE 2026 09 19 / ODOO-5800 (Igor: измерение horizon в ключе метрики): список Horizon — только когда словарь знает горизонты
            // IE 2026 09 21 / ODOO-5800 (Igor: измерение «статус документа» в ключе метрики): список Status — только когда словарь знает статусы документов
            // IE 2026 10 04 / ODOO-5800 (fe-74): словарь сигналов — секции Kind и Signal вместо Metric (отбор по метрике прежнего чипса остаётся в выбранных значениях и действует);
            // пункт Signal — заголовок сигнала: включён, когда в отборе все его id; поиск смотрит и в подсказку (исходные названия метрик / полная причина)
            const v2 = !!L.v2
            const shown = ([kind]) => (kind !== 'horizons' || L.horizons.length) && (kind !== 'statuses' || L.statuses.length)
                && (v2 ? kind !== 'metrics' : (kind !== 'kinds' && kind !== 'signals'))
                && (kind !== 'reasons' || (!!L.m2 && L.reasons.length > 0))   // IE 2026 10 04 / ODOO-5800 (fe-78): секция Cause — только словарь новой модели сигналов
            const isOn = (kind, it) => it.ids ? it.ids.every(x => (doc[kind] || []).includes(x)) : (doc[kind] || []).includes(it.id)
            return METRIC_CHIP_KINDS.filter(shown).map(([kind]) => ({ kind, title: this.metricChip_kindTitle(kind, chip),
                items: (L[kind] || []).filter(it => !terms.length || terms.every(t => (String(it.title) + ' ' + String(it.tip || '')).toLowerCase().includes(t)))
                    .map(it => ({ id: it.id, ids: it.ids, tip: it.tip || '', on: isOn(kind, it), titleHTML: mark(it.title), sample: this.metricChip_sample(it.css), count: kind === 'combos' ? it.rows : it.hits })) }))
        },
        metricChip_titleHTML(doc, global = this.mode === 'Page') {
            const rules = Array.isArray(global) ? global : this.metricChip_rules(global)
            if (metricChip_isEmpty(doc)) return '<span style="opacity:.55">select…</span>'
            const esc = (x) => this.store.escapeHtml(String(x))
            // IE 2026 10 09 / ODOO-5800 (ie-growth7, Demo 6: подпись чипса повторена дважды и обрезана): одинаковые названия сливаются, обрезанные не совпадают между собой, полный текст — в title части
            // IE 2026 10 10 / ODOO-5800 (ревью fe-ui-1): выключатель fe.ux.metricChipLabels (дефолт ВКЛ; выкл — подпись как в fe-4: части не сливаются, без « ×N», различителя и подсказки с полным текстом)
            const merged = this.store.setting('fe.ux.metricChipLabels', true) !== false   // IE 2026 10 10 / ODOO-5800
            const labels = metricChip_labels(metricChip_parts(doc, rules), 34, merged), more = Math.max(0, labels.length - 3)   // IE 2026 10 10 / ODOO-5800: max = 34 как был; merged — выключатель
            const one = (p) => {
                const css = this.metricChip_sample(p.css).replace(/"/g, '&quot;')
                const sw = css ? `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;margin:0 3px 0 2px;vertical-align:-1px;box-shadow:0 0 0 1px rgba(0,0,0,.18);${css}"></span>` : ''
                if (!merged) return sw + esc(p.label)   // IE 2026 10 10 / ODOO-5800: как в fe-4 — без обёртки и title
                const cut = p.label !== p.full
                return `<span${cut ? ` title="${esc(p.full).replace(/"/g, '&quot;')}"` : ''}>${sw}${esc(p.label)}</span>`
            }
            return labels.slice(0, 3).map(one).join(', ') + (more ? ` +${more}` : '')
        },
        metricChip_commit(chip) {
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): условия над витриной и наборы calc считает бэк — отбор чипса шапки уходит методом workspace_metricChip
            if (chip._global) { this.metricChipG_schedule(); return }
            const raw = chip._raw || chip, doc = this.metricChip_doc(chip)
            raw.meta = { ...(raw.meta || {}), use: true, pi: 'pi-bullseye', titleHTML: this.metricChip_titleHTML(doc) }
            raw.title = metricChip_text(doc, this.metricChip_rules())
            this._metricDirty = raw   // в историю — когда форма закроется (промежуточные шаги выбора в Recent не нужны)
            this.emitChips()
        },
        metricChip_toggle(index, chip, kind, id, ev, titleClick = false) {
            if (chip._mirror) return   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): зеркало показывает отбор шапки стола, правится он там
            const doc = this.metricChip_docEdit(chip)
            metricChip_toggleIds(doc, kind, id, !!(titleClick || ev?.altKey))   // IE 2026 10 04 / ODOO-5800 (fe-74): id — значение либо массив (пункт Signal — все id сигнала под заголовком)
            this.metricChip_commit(chip)
        },
        metricChip_add() {
            this.showDropdown = false
            if (this.mode === 'Page') {
                // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): строки стола ещё нет — черновик в конце полосы, форма открывается сразу
                if (!this.metricChipG_offered()) return
                this.metricG.localDoc = metricChip_copy({})   // IE 2026 10 04 / ODOO-5800 (fe-74): все списки отбора (в т.ч. kinds / signals)
                this.metricG.draft = true
                nextTick(() => {
                    const i = this.visibleChips.findIndex(c => c._global)
                    if (i < 0) return
                    clearTimeout(this._chipHoverTimer); clearTimeout(this._chipOpenTimer)
                    this.metricPickText = ''
                    this.hoverIndex = i
                    this.$nextTick(() => this.chipPick_focusInput())
                })
                return
            }
            if ((this.chipsRaw || []).some(c => c && c.segment === 'CHIP-Metric')) return
            const chip = { segment: 'CHIP-Metric', title: '', meta: { use: true, pi: 'pi-bullseye', titleHTML: this.metricChip_titleHTML({}) }, doc: metricChip_copy({}) /* IE 2026 10 04 / ODOO-5800 (fe-74) */, ...this.metricChip_newFlags() }
            this.chipsRaw.push(chip)
            this.emitChips()
            nextTick(() => {
                const i = this.visibleChips.findIndex(c => this.metricChip_is(c))
                if (i < 0) return
                clearTimeout(this._chipHoverTimer); clearTimeout(this._chipOpenTimer)
                this.metricPickText = ''
                this.hoverIndex = i   // пустой чипс сразу открывает форму выбора
            })
        },
        // IE 2026 09 18 / ODOO-5800 (заказ Игоря: колонка use вкладки Metrics): отбор чипса задаётся извне (клики по use вкладки Metrics): нет чипса — создать,
        // пустой отбор — снять чипс; история — сразу (формы тут нет)
        metricChip_setDoc(doc, opts = {}) {   // IE 2026 10 07 / ODOO-5800: opts.fromSelector — отбор задан колонкой Selector списка сигналов (AttrGrid.metricUse_apply)
            const list = this.chipsRaw
            let i = list.findIndex(c => c && c.segment === 'CHIP-Metric')
            if (metricChip_isEmpty(doc)) {
                if (i >= 0) { list.splice(i, 1); this.emitChips() }
                return
            }
            if (i < 0) { list.push({ segment: 'CHIP-Metric', title: '', meta: { use: true, pi: 'pi-bullseye' }, doc: metricChip_copy(doc), ...this.metricChip_newFlags(true, !!opts.fromSelector) }); i = list.length - 1 }   // IE 2026 10 04 / ODOO-5800 (fe-73): чипс из строки грида сигналов / флажка формы ячейки отбирает строки отчёта   // IE 2026 10 07 / ODOO-5800: + fromSelector
            else list[i].doc = metricChip_copy(doc)
            this.metricChip_commit(list[i])
            this.metricChip_historyPush(list[i]); this._metricDirty = null
        },
        // IE 2026 09 19 / ODOO-5800 (Igor: «при вкл строк в "_doc.metrics_stats" по умолчанию вкл "Show rows without matches"» + флаг «Show other formatting»
        // «тоже по умолчанию вкл»): новый чипс метрик (из колонки Selector вкладки Metrics и из формы) строк не прячет и чужое оформление не гасит,
        // пока пользователь сам не снимет флаги. Под выключателем fe.ux.metricSelector — прежнее поведение (строки без совпадений прячутся)
        // IE 2026 10 04 / ODOO-5800 (fe-73) (Igor 04.10: строка грида сигналов снова фильтрует отчёт): fromSelection — чипс создан выбором строки (metricChip_setDoc):
        // «Show rows without matches» снят, отчёт сворачивается до строк сигнала; чипс из формы — как было. Выключатель fe.ux.metricSelectorShowAll (true — умолчание 19.09)
        // IE 2026 10 07 / ODOO-5800 (Igor 07.10, dev02, список сигналов → «Баланс постачання та попиту»: «при вкл селектора … добавляй чипс с флагом "Показати рядки без збігів"»):
        // чипс, созданный колонкой Selector списка сигналов (fromSelector), — с включённым «Show rows without matches»; флажок метрики формы ячейки — как в fe-73.
        // Выключатель fe.ux.signalSelectorShowAll (дефолт ВКЛ; false — чипс селектора снова отбирает строки, как fe-73). Чипс из формы «+ Metrics» не меняется
        metricChip_newFlags(fromSelection = false, fromSelector = false) {   // IE 2026 10 04 / ODOO-5800 (fe-73)
            const showAll = fromSelector && this.store.setting('fe.ux.signalSelectorShowAll', true) !== false   // IE 2026 10 07 / ODOO-5800
                ? true : this.store.setting('fe.ux.metricSelectorShowAll', false) === true
            return metricChip_newFlags(this.store.metricSelector_on(), fromSelection, showAll)
        },
        // ---- IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format) ----
        // Чипс условного оформления (CHIP-Format) — по образцу чипса метрик, только над таблицей виджета (mode Embedded): словарь attr.format_rules (правила с
        // Selector в гриде Conditional format), отбор doc.rules = [row_id]; форма — выбранные правила с чекбоксами, поиск, список правил с образцом стиля и
        // счётчиками «строк · ячеек», Recent; счётчик n/N, ‹ › по ячейкам, наведение подсвечивает ячейки, флаги «Show rows without matches» и «Show other
        // formatting» (оба по умолчанию вкл). Отбор строк, переходы и приглушение — в гриде (AttrGrid: apply_CHIPS / check_CHIPS ветка _format,
        // formatNav_*), чистая логика — utils/formatChip.js. Выключатель fe.ux.formatChip
        formatChip_is(chip) { return !!chip && (chip._raw || chip).segment === 'CHIP-Format' },
        formatChip_on() { return this.mode === 'Embedded' && this.store.metricSelector_on() && this.store?.setting?.('fe.ux.formatChip', true) !== false },
        formatChip_rules() { return Array.isArray(this.attr?.format_rules) ? this.attr.format_rules : [] },
        formatChip_offered() { return this.formatChip_on() && this.formatChip_rules().length > 0 && !(this.chipsRaw || []).some(c => c && c.segment === 'CHIP-Format') },
        formatChip_doc(chip) {
            const raw = chip._raw || chip
            if (!raw.doc || !Array.isArray(raw.doc.rules)) raw.doc = formatChip_copy(raw.doc)
            return raw.doc
        },
        formatChip_sample(p) { return this.metricChip_sample(p.css) + (p.style ? String(p.style).replace(/;?\s*$/, ';') : '') },
        formatChip_selected(chip) { return formatChip_parts(this.formatChip_doc(chip), this.formatChip_rules()).map(p => ({ ...p, sample: this.formatChip_sample(p) })) },
        formatChip_list(chip) {
            const terms = String(this.formatPickText || '').toLowerCase().split(/\s+/).filter(Boolean)
            const esc = (x) => this.store.escapeHtml(String(x))
            const mark = (title) => {
                let html = esc(title)
                for (const t of terms) { const i = html.toLowerCase().indexOf(esc(t)); if (i >= 0) html = html.slice(0, i) + '<b>' + html.slice(i, i + esc(t).length) + '</b>' + html.slice(i + esc(t).length) }
                return html
            }
            return formatChip_items(this.formatChip_doc(chip), this.formatChip_rules(), this.formatPickText)
                .map(it => ({ ...it, titleHTML: mark(it.title), sample: this.formatChip_sample(it), count: [it.rows, it.cells].filter(x => x !== undefined && x !== null).join(' · ') }))
        },
        formatChip_titleHTML(doc) {
            if (formatChip_isEmpty(doc)) return '<span style="opacity:.55">select…</span>'
            const esc = (x) => this.store.escapeHtml(String(x))
            const rules = this.formatChip_rules(), parts = formatChip_parts(doc, rules), { more } = formatChip_titleParts(doc, rules, 3)
            const one = (p) => {
                const css = this.formatChip_sample(p).replace(/"/g, '&quot;')
                const sw = css ? `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;margin:0 3px 0 2px;vertical-align:-1px;box-shadow:0 0 0 1px rgba(0,0,0,.18);${css}"></span>` : ''
                return sw + esc(p.title.length > 34 ? p.title.slice(0, 33).trimEnd() + '…' : p.title)
            }
            return parts.slice(0, 3).map(one).join(', ') + (more ? ` +${more}` : '')
        },
        formatChip_commit(chip) {
            const raw = chip._raw || chip, doc = this.formatChip_doc(chip)
            raw.meta = { ...(raw.meta || {}), use: true, pi: 'pi-palette', titleHTML: this.formatChip_titleHTML(doc) }
            raw.title = formatChip_text(doc, this.formatChip_rules())
            this._formatDirty = raw   // в историю — когда форма закроется
            this.emitChips()
        },
        // чекбокс — добавить / убрать правило; щелчок по названию / Alt — только это правило
        formatChip_toggle(index, chip, id, ev, titleClick = false) {
            const raw = chip._raw || chip
            raw.doc = formatChip_toggle(this.formatChip_doc(chip), id, titleClick || !!ev?.altKey)
            this.formatChip_commit(chip)
        },
        formatChip_add() {
            this.showDropdown = false
            if (!this.formatChip_offered()) return
            const chip = { segment: 'CHIP-Format', title: '', meta: { use: true, pi: 'pi-palette', titleHTML: this.formatChip_titleHTML({}) }, doc: { rules: [] }, ...this.metricChip_newFlags() }
            this.chipsRaw.push(chip)
            this.emitChips()
            nextTick(() => {
                const i = this.visibleChips.findIndex(c => this.formatChip_is(c))
                if (i < 0) return
                clearTimeout(this._chipHoverTimer); clearTimeout(this._chipOpenTimer)
                this.formatPickText = ''
                this.hoverIndex = i   // пустой чипс сразу открывает форму выбора
            })
        },
        // отбор задаётся извне (пробы, будущие команды): нет чипса — создать, пустой отбор — снять чипс; история — сразу
        formatChip_setDoc(doc) {
            const list = this.chipsRaw
            let i = list.findIndex(c => c && c.segment === 'CHIP-Format')
            if (formatChip_isEmpty(doc)) { if (i >= 0) { list.splice(i, 1); this.emitChips() } return }
            if (i < 0) { list.push({ segment: 'CHIP-Format', title: '', meta: { use: true, pi: 'pi-palette' }, doc: formatChip_copy(doc), ...this.metricChip_newFlags() }); i = list.length - 1 }
            else list[i].doc = formatChip_copy(doc)
            this.formatChip_commit(list[i])
            this.formatChip_historyPush(list[i]); this._formatDirty = null
        },
        formatChip_historyKey() {
            const u = this.store.user?.id || this.store.user?.email || 'user', db = this.store.user?.database?.dbid || ''
            return `formatChipHistory:${u}:${db}:${this.WIDGET?.record?.id || this.WIDGET?.widget || ''}`
        },
        formatChip_historyPush(raw) {
            try { formatChip_histPush(localStorage, this.formatChip_historyKey(), raw?.doc); this._formatHistTick = (this._formatHistTick || 0) + 1 } catch (e) { /* без истории */ }
        },
        formatChip_history(chip) {
            let list = []
            try { list = formatChip_histRead(localStorage, this.formatChip_historyKey()) } catch (e) { list = [] }
            const cur = formatChip_sig(this.formatChip_doc(chip)), rules = this.formatChip_rules()
            return list.map(h => ({ sig: h.sig, doc: h.doc, current: h.sig === cur, title: formatChip_text(h.doc, rules) || '—' }))
        },
        // название / Alt — применить запись истории целиком; чекбокс — добавить её правила к текущему отбору
        formatChip_historyApply(index, chip, h, ev, titleClick = false) {
            const raw = chip._raw || chip, cur = this.formatChip_doc(chip), src = formatChip_copy(h.doc)
            raw.doc = (titleClick || ev?.altKey) ? src : { rules: [...new Set([...cur.rules, ...src.rules])] }
            this.formatChip_commit(chip)
        },
        metricChip_historyKey() {
            const u = this.store.user?.id || this.store.user?.email || 'user', db = this.store.user?.database?.dbid || ''
            if (this.mode === 'Page') return this.store.metricChipG_historyKey()   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»)
            return `metricChipHistory:${u}:${db}:${this.WIDGET?.record?.id || this.WIDGET?.widget || ''}`
        },
        metricChip_historyPush(raw) {
            try { metricChip_histPush(localStorage, this.metricChip_historyKey(), raw?.doc); this._metricHistTick = (this._metricHistTick || 0) + 1 } catch (e) { /* без истории */ }
        },
        metricChip_history(chip) {
            let list = []
            try { list = metricChip_histRead(localStorage, this.metricChip_historyKey()) } catch (e) { list = [] }
            const cur = metricChip_sig(this.metricChip_doc(chip)), rules = this.metricChip_rules()
            return list.map(h => ({ sig: h.sig, doc: h.doc, current: h.sig === cur, title: metricChip_text(h.doc, rules) || '—' }))
        },
        // название / Alt — применить запись истории целиком; чекбокс — добавить её значения к текущему отбору
        metricChip_historyApply(index, chip, h, ev, titleClick = false) {
            const doc = this.metricChip_docEdit(chip), src = metricChip_copy(h.doc)
            for (const [k] of METRIC_CHIP_KINDS) doc[k] = (titleClick || ev?.altKey) ? (src[k] || []) : [...new Set([...(doc[k] || []), ...(src[k] || [])])]   // IE 2026 10 04 / ODOO-5800 (fe-78): списка reasons в записи истории может не быть
            this.metricChip_commit(chip)
        },
        chipHistoryItems(chip) {
            const raw = chip._raw || chip
            const list = this.store.chipHistory_forBinding(raw?.leftValue?.value)
            // IE 2026 08 26 / ODOO-5800 (замечание Игоря): тайтл записи истории считался один раз при захвате —
            // чипс, поставленный значением-id до загрузки словаря, оставался в Recent GUID'ом. Резолвим при показе
            // по словарю сегмента (реактивно), недостающие значения дозапрашиваем по id
            return list.map(h => ({ ...h, title: this.chipHistory_title(h) }))
        },
        chipHistory_title(h) {
            const values = []
            if (h.rightValue?.value || h.rightValue?.valueStr) values.push(h.rightValue)
            for (const r of h.attrs || []) {
                if (r?.rightValue?.value || r?.rightValue?.valueStr) values.push(r.rightValue)
            }
            if (!values.length) return h.title
            return values.map(v => {
                const seg = v.relation_segment
                const dict = (seg && v.value) ? this.store.itemsSource[seg]?.ids?.[v.value] : null
                if (dict?.title) return dict.title
                if (v.title) return v.title
                if (v.valueStr && v.valueStr !== v.value) return v.valueStr
                if (seg && v.value) this.chipHistory_requestTitle(seg, v.value)
                return v.valueStr || v.value
            }).join(', ')
        },
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: GUID в меню чипса): id строк виджета-селектора без тайтла в словаре сегмента — одним запросом by_ids за проход подбора
        // (повторы, ожидающие и отсутствующие id отсекает get_itemsSource; ответ меняет длину словаря — подпись подбора, список пересчитывается). Выключатель fe.ux.dictAllTruncated
        chipPick_needTitle(seg, id) {
            if (!seg || id == null || id === '' || this.store.setting('fe.ux.dictAllTruncated', true) === false) return
            if (!this._chipPickNeed) this._chipPickNeed = {}
            const pend = this._chipPickNeed[seg] || (this._chipPickNeed[seg] = new Set())
            pend.add(String(id))
            if (this._chipPickNeedTimer) return
            this._chipPickNeedTimer = setTimeout(() => {
                this._chipPickNeedTimer = null
                const all = this._chipPickNeed; this._chipPickNeed = {}
                for (const sg in all) {
                    try { this.store.get_itemsSource(this, {}, { attr_type: 'AttrLink', relation_segment: sg }, { fetchMode: 'by_ids', ids: [...all[sg]] }) } catch (e) { /* без словаря — id */ }
                }
            }, 0)
        },
        chipHistory_requestTitle(seg, id) {
            // словарь сегмента по id — один запрос на значение за жизнь компонента
            const key = `${seg}:${id}`
            if (!this._chipHistoryReq) this._chipHistoryReq = new Set()
            if (this._chipHistoryReq.has(key)) return
            this._chipHistoryReq.add(key)
            try {
                this.store.get_itemsSource(this, {}, { attr_type: 'AttrLink', relation_segment: seg }, { fetchMode: 'by_ids', ids: [id] })
            } catch (e) { /* без словаря — показываем id */ }
        },
        chipHistoryIsCurrent(chip, h) {
            const raw = chip._raw || chip
            if (!raw) return false
            const values = []
            if (raw.rightValue?.value || raw.rightValue?.valueStr) values.push(raw.rightValue)
            for (const r of raw.attrs || []) {
                if (r?.rightValue?.value || r?.rightValue?.valueStr) values.push(r.rightValue)
            }
            return JSON.stringify(values.map(v => v.value || v.valueStr)) === h.sig
        },
        applyChipHistory(index, h) {
            const chip = this.visibleChips[index]
            const raw = chip?._raw || chip
            if (!raw) return
            // селекторный чипс: эталон — расставить флажки в виджете-селекторе
            // и применить его командой (пересчёт множеств + зависимые действия)
            if (this.chipPick_isSelectorForm(raw)) {
                const values = this.chipPickKeeps_on() ? chipRow_values(h) : (h.attrs || []).map(a => a?.rightValue?.value).filter(v => v != null)   // IE 2026 10 13 / ODOO-5800: и значение записи в форме rightValue (было — только h.attrs: пусто → фолбэк копировал «в»-строку)
                if (values.length) this.chipPick_clearRv(raw)   // IE 2026 10 13 / ODOO-5800
                if (values.length && this.chipSelector_applyViaWidget(raw, values)) {
                    this.hoverIndex = null
                    // стандартное событие применения чипсов — как при включении/
                    // выключении чипса: одного onApplySelector__Click виджета
                    // недостаточно, зависимые виджеты страницы данные не обновляли
                    // (ИЕ 2026-08-12: выбор из Recent не перезапрашивал отчёт)
                    this.emitChips()
                    return
                }
            }
            const _wasSel = this.chipPick_isSelectorForm(raw)   // IE 2026 10 13 / ODOO-5800
            raw.rightValue = JSON.parse(JSON.stringify(h.rightValue || {}))
            raw.attrs = JSON.parse(JSON.stringify(h.attrs || []))
            if (h.condition && !(_wasSel && this.chipPickKeeps_on())) raw.condition = h.condition   // IE 2026 10 13 / ODOO-5800: у селекторного чипса условие строки стола не меняется записью истории
            if (this.chipPickKeeps_on()) chipRow_inToList(raw)   // IE 2026 10 13 / ODOO-5800: значение записи в форме rightValue → список attrs (бэк читает «in» только из attrs)
            raw.use = true
            // селекторный чипс (фолбэк): снять метку — переоткрытие пересеет
            // флажки виджета-селектора по восстановленным значениям
            if (this.chipPick_isSelectorForm(raw)) {
                const params = this.store.activeWORKSPACE?.record?.doc?.params
                if (params) delete params.calc_selectors_mark
            }
            this.hoverIndex = null
            this.emitChips()
        },
        // ---- подбор значения чипса (Pick value): поиск по словарю сегмента,
        // клик — заменить текущее значение (тот же путь применения, что у Recent) ----
        // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): бэк без вариантов (dev02 до слияния origin) — прежние группы Filters / Page / Widgets
        // IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U4: ключи справки для якорей, общих на несколько видов чипсов/пунктов/кнопок
        // (utils/uiHelp/chips.js, meta.state_expr_scope) -- вид чипса (тело/⊗/иконка/текст полосы), вкладка и обычная строка ⌄, кнопка группы Filters/Page
        uiHelp_chipKind(chip) {
            if (!chip) return 'other'
            if (chip._mirror) return 'metric-mirror'
            if (chip._global) return 'metric-global'
            const raw = chip._raw || chip
            if (this.mode === 'Page' && chip.segment === 'CHIP-Selector') return this.chipPeriod(chip) ? 'period' : 'selector'
            const seg = raw.segment || chip.segment || (raw.attr_type === 'CHIP-Tag' ? 'CHIP-Tag' : '')
            switch (seg) {
                case 'CHIP-WidgetsPage': return 'widgets-page'
                case 'CHIP-Limit': return 'limit'
                case 'CHIP-ProcessModel': return 'process-model'
                case 'CHIP-CompareModel': return 'compare'
                case 'CHIP-Info': return chip.doc?._corr ? 'corrections' : (chip.doc?._dd ? 'unsaved-edits' : (chip.doc?._schedule ? 'schedule-paused' : 'info'))   // IE 2026 10 10 / ODOO-345813: + чипс окон остановки регламента
                case 'CHIP-Metric': return 'metric'
                case 'CHIP-Format': return 'format'
                case 'CHIP-Search': return String(raw.title || '').startsWith('style:') ? 'style' : 'search'
                case 'CHIP-Where': return 'filter'
                case 'CHIP-Selector': return 'selector'
                case 'CHIP-Tree': return 'tree'
                case 'CHIP-Order': return 'order'
                case 'CHIP-Group': return 'group'
                case 'CHIP-GridFilter': return 'grid-filter'
                case 'CHIP-TreeRoot': return 'tree-root'
                case 'CHIP-Tag': return 'tag'
                case 'CHIP-Rows': case 'CHIP-Cell': case 'CHIP-CellFilters': case 'CHIP-CellAndWidgetsFilters': case 'CHIP-CellValues': case 'CHIP-Records': return 'copied'
                case 'CHIP-Widget': case 'CHIP-Record': case 'CHIP-JSON': case 'CHIP-Image': case 'CHIP-File': return 'object'
                default: return 'other'
            }
        },
        // ключ справки ТЕЛА чипса: uiHelp_chipKind() отдаёт 'other' запасным вариантом -- ему соответствует голый ключ 'chips.chip' (без суффикса, uiHelp/chips.js)
        uiHelp_chipHelpKey(chip) {
            const k = this.uiHelp_chipKind(chip)
            return k === 'other' ? 'chips.chip' : 'chips.chip.' + k
        },
        // IE 2026 10 29 / ODOO-5800 (ie-dm7 #114897: плашка «Порівняння з» — _compare_info.compare_model): переменные объяснения чипса сравнения — с чем сравнивается и почему (reason бэка)
        uiHelp_chipVars(chip) {
            return chip?.segment === 'CHIP-CompareModel' && chip.doc?.cmpInfo ? compareModelBadge_helpVars(chip.doc.cmpInfo, this.t) : {}
        },
        uiHelp_ddTabKey(tab) {
            const n = tab?.meta?.name
            if (n === 'filters') return this.mode === 'Page' ? 'chips.dd.tab.selectors' : 'chips.dd.tab.filters'
            return 'chips.dd.tab.' + (n || 'chips')   // params | commands | add | chips (у Embedded вкладка Chips без name)
        },
        uiHelp_ddItemKey(item) {
            if (item?._template) return 'chips.dd.template'
            if (item?._global) return 'chips.dd.item.metric'
            if (item?.segment === 'CHIP-Selector' || item?.segment === 'CHIP-Where') return 'chips.dd.item.filter'
            if (item?.segment === 'CHIP-Param') return 'chips.dd.param'
            return 'chips.dd.item.other'
        },
        uiHelp_cmdKey(b) {
            const a = b?.action || {}, bc = a.backend_command
            // IE 2026 09 26 / ODOO-… (замечание Игоря: «+» на новой странице): «+» / «✓» временной страницы — объяснения своих кнопок в состоянии temp
            if (a._tempPage === 'plus') return 'chips.cmd.btn.page-new'
            if (a._tempPage === 'check') return 'chips.cmd.btn.page-save'
            if (a.command === 'variants_saveFilters') return 'chips.cmd.btn.filters-new'
            if (a.command === 'variants_saveWidgets') return 'chips.cmd.btn.page-new'
            if (a.command === 'variants_save') return a.kind === 'filters' ? 'chips.cmd.btn.filters-save' : 'chips.cmd.btn.page-save'
            if (a.command === 'widget_open') return 'chips.cmd.btn.page-settings'
            if (bc === 'page_save_as_new') return 'chips.cmd.btn.page-copy'
            if (bc === 'user_defaults_save' && a.default_workspace) return 'chips.cmd.btn.start-workspace'
            if (bc === 'user_defaults_save' && a.withWhere) return 'chips.cmd.btn.legacy-filters-my'
            if (bc === 'workspace_defaults_save') return 'chips.cmd.btn.legacy-filters-all'
            if (bc === 'user_defaults_save' && a.reset_where) return 'chips.cmd.btn.legacy-filters-reset'
            if (bc === 'user_defaults_save' && a.reset_widgets) return 'chips.cmd.btn.legacy-page-reset'
            if (bc === 'user_defaults_save' && a.withWidgets) return 'chips.cmd.btn.legacy-page-my'
            if (bc === 'page_defaults_save') return 'chips.cmd.btn.legacy-page-all'
            return 'chips.cmd.btn'
        },
        // IE 2026 09 19 / ODOO-… (замечание Игоря): «ленивая» подсказка кнопки-команды (v-wjTooltipFn: зажатый Alt либо включённый Show tooltips) —
        // тот же вид, что у кнопок командных панелей (store.tooltip_html); функция кэшируется на объекте команды
        // IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U4: строки объяснения (what/why/click/affects/stored) дописываются в конец --
        // способ 2 ядра, кнопка (aria-label + v-wjTooltipFn) не трогается
        cmdTooltipFn(b) {
            // IE 2026 09 26 / ODOO-… (замечание Игоря: «+» на новой странице): состояние temp (кнопки группы Page временной страницы) — свои тексты и строка Now
            if (!b._tipFn) b._tipFn = () => this.store.tooltip_html({ [this.t('Action')]: b.title, [this.t('Command')]: b.action?.backend_command || b.action?.command || '' }, b.tooltip || '')   // IE 2026 09 23 / ODOO-341407: i18n
                + (uiHelp_enabled() ? uiHelp_rowsHtml(this.uiHelp_cmdKey(b), { state: this.uiHelp_cmdState(b) }, this.uiHelp_cmdState(b) ? ['what', 'why', 'click', 'now', 'affects', 'stored'] : ['what', 'why', 'click', 'affects', 'stored']) : '')   // IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U4
            return b._tipFn
        },
        // IE 2026 09 26 / ODOO-… (замечание Игоря: «+» на новой странице): состояние объяснения кнопки-команды — 'temp' у кнопок группы Page временной страницы
        uiHelp_cmdState(b) { return b?.action?._tempPage ? 'temp' : null },
        // IE 2026 09 29 / ODOO-342781: роль «только чтение» — группы Filters / Page панели Commands без записи: кнопки Save / «Save as new …» / «Make this my start workspace» убраны
        // (store.readonly_allowsAction по action кнопки), у строк вариантов нет флагов All · Me и удаления; применение варианта (клик по строке — на сеанс, без записи) остаётся
        roCommandGroups(groups) {
            const store = this.store
            return (groups || []).map(g => {
                let buttons = g.buttons
                if (Array.isArray(buttons)) {
                    buttons = buttons.filter(b => !b || !b.action || store.readonly_allowsAction(b.action))
                    buttons = buttons.filter((b, i) => !(b && b._gap) || (i > 0 && !(buttons[i - 1] && buttons[i - 1]._gap) && i < buttons.length - 1))   // зазор — только между кнопками
                }
                const attrs = Array.isArray(g.attrs) ? g.attrs.map(a => (a && a.segment === 'CHIP-VariantRow') ? { ...a, flags: false, onDelete: null, onAll: null, onMe: null } : a) : g.attrs
                return { ...g, cols: false, buttons, attrs }
            })
        },
        pageCommandGroups_legacy(admin, page) {
            const cmd = (title, pi, action, tooltip) => ({ segment: 'CHIP-Command', title, tooltip, meta: { pi: 'pi ' + pi }, action })
            // IE 2026 09 19 / ODOO-… (замечание Игоря): и здесь команды — кнопками-пиктограммами в заголовке группы, Page и Widgets — одна группа
            // IE 2026 09 23 / ODOO-341407: i18n
            return [
                { segment: 'AttrTab', title: this.t('Filters'), attrs: [], buttons: [
                    cmd(this.t('Save as my'), 'pi-save', { command: 'defaults_save', backend_command: 'user_defaults_save', withWhere: true }, this.t('Save the current chips as my defaults of this workspace')),
                    ...(admin ? [cmd(this.t('Save for all'), 'pi-users', { command: 'defaults_save', backend_command: 'workspace_defaults_save', withWhere: true }, this.t('Save the current chips as the defaults of this workspace for everyone'))] : []),
                    cmd(this.t('Use defaults'), 'pi-filter-slash', { command: 'defaults_save', backend_command: 'user_defaults_save', reset_where: true }, this.t('Reset to workspace default filters')),
                ]},
                { segment: 'AttrTab', title: this.t('Page'), attrs: [], buttons: [
                    cmd(this.t('Save as my'), 'pi-save', { command: 'defaults_save', backend_command: 'user_defaults_save', withPage: true, withWidgets: true }, this.t('Save the open widgets and their layout as my defaults of this page')),
                    ...(admin ? [cmd(this.t('Save for all'), 'pi-users', { command: 'defaults_save', backend_command: 'page_defaults_save', withPage: true, withWidgets: true }, this.t('Save the open widgets and their layout as the defaults of this page for everyone'))] : []),
                    cmd(this.t('Use defaults'), 'pi-replay', { command: 'defaults_save', backend_command: 'user_defaults_save', withPage: true, reset_widgets: true }, this.t('Reset to page default widgets')),
                    { _gap: true },
                    ...(page ? [cmd(this.t('Settings'), 'pi-cog', { command: 'widget_open', widget: page, _activate: true }, this.t('Open the settings of the current page (its record): title, icon, widget list and layout'))] : []),
                    cmd(this.t('Save as new page'), 'pi-clone', { command: 'defaults_save', backend_command: 'page_save_as_new', withPage: true, withWidgets: true, ask_title: true },
                        this.t('Save what is open on this page as a new page of the workspace (widgets and their layout as is); the new page is added to the page list')),
                    cmd(this.t('Make this my start workspace'), 'pi-bookmark-fill', { command: 'defaults_save', backend_command: 'user_defaults_save', default_workspace: true },
                        this.t('Open this workspace first after login (my start workspace)')),   // IE 2026 09 22 / ODOO-… (предложение Игоря, шаг 2)
                ]},
            ]
        },
        variantsPanel_load() {
            // IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): списки панели — одним методом бэка; при загрузке прежние списки остаются
            if (this.variantsPanelLoading) return
            this.variantsPanelLoading = true
            this.store.variants_panel((answer) => {
                this.variantsPanelLoading = false
                if (answer && answer.status !== 'error' && answer.pages) { this.variantsPanel = answer; this.variantsUnavailable = false }
                else if (!answer || answer.status === 'error' || !answer.pages) this.variantsUnavailable = true   // бэк не знает метод — прежние группы команд
            })
        },
        chipPick_segment(chip) {
            const raw = chip?._raw || chip
            if (!raw) return null
            const seg = raw.rightValue?.relation_segment
                || raw.attrs?.find?.(a => a?.rightValue?.relation_segment)?.rightValue?.relation_segment
            // подбор осмыслен только для ссылочных значений (словарь сегмента)
            return (seg && typeof seg === 'string') ? seg : null
        },
        // IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): иконка сегмента подбираемых элементов (словарь SEGMENTS: doc.pi), по умолчанию pi-file
        chipPick_segmentPi(chip, segment = null) {
            const seg = segment || this.chipPick_segment(chip)
            const IS = this.store.itemsSource?.SEGMENTS
            const byId = (id) => IS?.ids?.[id] || (IS?.data || []).find(x => x?.id === id)
            // у подсегмента без своей иконки — иконка родителя (OBJECT → segment-NNNN)
            let rec = seg ? byId(seg) : null, guard = 0
            while (rec && !(rec.pi || rec.doc?.pi) && rec.parent && guard++ < 6) rec = byId(rec.parent)
            const pi = String(rec?.pi || rec?.doc?.pi || '').trim()
            return pi ? pi.replace(/^pi\s+/, '') : 'pi-file'
        },
        // сегмент самой записи строки: словарь чипса может быть источником данных (DS::OBJECTS) — тогда строки несут
        // segment записи (segment-NNNN); иначе сегмент словаря
        chipPick_itemSegment(chip, it, segment = null) {
            const seg = segment || this.chipPick_segment(chip)
            const id = it?.id ?? it?.value
            if (!seg || id == null) return seg
            const IS = this.store.itemsSource?.[seg]
            const row = IS?.ids?.[id] || (IS?.data || []).find(x => x && String(x.id) === String(id))
            return (row && typeof row.segment === 'string' && row.segment) ? row.segment : seg
        },
        chipPick_itemPi(chip, it, segment = null) {
            return this.chipPick_segmentPi(chip, this.chipPick_itemSegment(chip, it, segment))
        },
        // IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): открыть элемент списка (подбор / выбранные / история) виджетом записи, форма чипса закрывается;
        // та же команда, что у иконки-ссылки в ячейке грида и у строки меню
        chipPick_openItem(chip, it, segment = null) {
            const seg = this.chipPick_itemSegment(chip, it, segment)
            const id = it?.id ?? it?.value
            if (!seg || id == null || id === '') return
            const vueObj = this.store.activeWIDGET?.vueObj || this
            this.chipPick_close?.()
            this.store.executeStoreMethod(vueObj, { command: 'widget_open', widget: 'widget-record', _activate: true, item: { id: String(id), segment: seg } })
        },
        // IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): единственное ссылочное значение чипса ({ id, title, attr }) — строка с иконкой открытия в шапке формы
        chipSingleValue(chip) {
            if (!this.chipPick_segment(chip)) return null
            const list = this._chipValues_list(chip?._raw || chip)
            return list.length === 1 && list[0].id != null && list[0].id !== '' ? list[0] : null
        },
        // IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): единственное значение записи истории ({ value, relation_segment, title }) или null
        chipHistory_single(h) {
            const values = []
            if (h?.rightValue?.value) values.push(h.rightValue)
            for (const r of h?.attrs || []) if (r?.rightValue?.value) values.push(r.rightValue)
            if (values.length !== 1 || !values[0].relation_segment) return null
            const v = values[0]
            return { id: v.value, value: v.value, relation_segment: v.relation_segment, title: h.title }
        },
        chipPick_load(chip) {
            this.chipPickFocused = true
            const seg = this.chipPick_segment(chip)
            if (!seg) return
            // штатный словарь платформы: закэширован в store.itemsSource, повторные
            // открытия в сеть не ходят
            this.store.get_itemsSource_bySegment(seg)
            this.chipPick_ensureWidget(chip)   // IE 2026 09 22 / ODOO-5800
        },
        // IE 2026 09 22 / ODOO-5800 (замечание Игоря, скрин Purchase Balance: подбор чипса Country показывал словарь субъектов — Farmalider S.A., Хорватия…,
        // а виджет-селектор «Contries::Balance by horizon» — девять стран): список подбора селекторного чипса — ТОЛЬКО строки виджета-селектора (правило
        // 07.08). Виджет берётся из вкладки (ALL_WIDGETS); на странице отчёта его нет, пока пользователь не открыл — раньше это молча давало словарь, а
        // после открытия виджета список не пересчитывался (подпись результата не знала о данных виджета). Теперь: при фокусе в поле подбора виджет
        // селектора открывается, как по шестерёнке чипса, без активации (фокус остаётся в поле); пока его данных нет — «Loading the selector widget...»;
        // подпись результата несёт состояние виджета — список пересчитывается, когда данные пришли. Выключатель fe.ux.chipPickOpenWidget
        // IE 2026 09 22 / ODOO-5800 (замечание Игоря, скрин: при подборе всплывал сам виджет «Contries::Balance by horizon» — «не нужно показывать
        // сам виджет, возможно делать его скрытым — как по Esc»): виджет-селектор открывается СКРЫТЫМ (parentAction._hidden → _softClosed с
        // создания, как после Esc: живёт в ALL_WIDGETS, данные грузятся, v-show прячет, стол его не сохраняет; в «последние закрытые» и историю
        // не попадает). Скрытый после Esc виджет — такой же источник; его устаревшие после смены чипсов данные (_selector_refresh_needed)
        // перечитываются при фокусе в поле подбора без показа. Пустой список виджета — тоже ответ (словарь не подставляем); ждём данных не дольше
        // CHIP_PICK_WIDGET_WAIT_MS после запроса (ошибка бэка / закрытый виджет — не «Loading…» навсегда). Выключатель fe.ux.chipPickHiddenWidget
        chipPick_widget(raw) {
            const ws = this.store.activeWORKSPACE
            if (!ws || !raw?.widget) return null
            const all = ws.ALL_WIDGETS || []
            // IE 2026 10 08 / ODOO-5800: окно той же основной, открытое подменой «Я»/«Всі» (record.id ≠ widget), — не источник (utils/chipWidgetOpen)
            const explicit = chipWidgetExplicit_on(this.store)
            const match = (w) => chipWidget_isSource(w, raw.widget, explicit)
            return all.find(w => match(w) && !w._softClosed) || all.find(match) || null
        },
        chipPick_widgetState(raw) {
            if (!this.chipPick_isSelectorForm(raw) || this.store.fastEntry.chipPickFromWidget === false || !raw?.widget) return 'off'
            const selW = this.chipPick_widget(raw)
            if (!selW) return 'none'
            if (!selW.record) return 'loading'   // ответа widget_open ещё нет
            if (!(selW.record.doc?.params?.fieldSelectors || []).length) return 'off'   // не селектор — словарь сегмента
            if (!selW.vueObj || selW._firstLoading || !Array.isArray(selW.record._doc?.data)) return 'loading'
            return 'ready'
        },
        chipPick_widgetKey(raw) { return `${raw?.widget || ''}|${this.store.activeWORKSPACE?.workspace || ''}` },
        chipPick_widgetLoading(chip) {
            const raw = chip?._raw || chip
            const st = this.chipPick_widgetState(raw)
            if (st !== 'loading' && st !== 'none') return false
            const op = this._chipPickWidgetOpened
            const fresh = !!op && op.key === this.chipPick_widgetKey(raw) && Date.now() - op.at < CHIP_PICK_WIDGET_WAIT_MS
            return fresh || !!this.chipPick_widget(raw)?.loading   // окно после запроса открытия / запрос виджета в полёте; иначе — словарь
        },
        chipPick_ensureWidget(chip) {
            const raw = chip?._raw || chip
            if (this.store.setting('fe.ux.chipPickOpenWidget', true) === false) return
            const st = this.chipPick_widgetState(raw)
            if (st === 'off') return
            if (st !== 'none') {
                // скрытый виджет с устаревшими данными (смена чипсов / возврат на страницу помечают _selector_refresh_needed) — перечитать, не показывая
                const selW = this.chipPick_widget(raw)
                if (selW?._softClosed && selW._selector_refresh_needed && selW.vueObj && !selW.loading) {
                    delete selW._selector_refresh_needed
                    const PAGE = this.store.WORKSPACES[selW.workspace]?.PAGES?.[selW.page]
                    this.store.executeStoreMethod(selW.vueObj, {
                        command: 'executeWidgetMethod', method: 'select', record_sendToBackend_exclude: ['_doc'],
                        record_sendToFrontend_exclude: PAGE?.refresh_needed ? [] : ['view'],
                    })
                }
                return
            }
            const key = this.chipPick_widgetKey(raw)
            const op = this._chipPickWidgetOpened
            if (op && op.key === key && Date.now() - op.at < CHIP_PICK_WIDGET_WAIT_MS) return   // уже просили — не дёргать повторно
            this._chipPickWidgetOpened = { key, at: Date.now() }
            try {
                const hidden = this.store.setting('fe.ux.chipPickHiddenWidget', true) !== false
                chipWidget_open(this.store, raw.widget, { parentAction: { _noActivate: true, ...(hidden ? { _hidden: true } : {}) } })   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
            } catch (e) { console.warn('chipPick_ensureWidget', e) }
        },
        chipPick_dataToken(data) {
            // идентичность массива данных виджета для подписи результата: ответ select заменяет запись целиком — новый массив = новые данные,
            // даже при том же числе строк
            if (!Array.isArray(data)) return 0
            if (!this._chipPickDataTok) { this._chipPickDataTok = new WeakMap(); this._chipPickDataN = 0 }
            let n = this._chipPickDataTok.get(data)
            if (!n) { n = ++this._chipPickDataN; this._chipPickDataTok.set(data, n) }
            return n
        },
        chipPickLoading(chip) {
            const raw = chip?._raw || chip
            // виджет-источник: список доступен сразу (словарь нужен лишь для титулов)
            if (this.chipPick_isSelectorForm(raw) && this.store.fastEntry.chipPickFromWidget !== false
                && this.chipPick_widgetSource(raw)) return false
            const seg = this.chipPick_segment(chip)
            const IS = seg && this.store.itemsSource[seg]
            return !!seg && !(IS?.data || IS?.ids)
        },
        chipPick_currentValues(chip) {
            const raw = chip?._raw || chip
            const vals = new Set()
            if (raw?.rightValue?.value != null) vals.add(String(raw.rightValue.value))
            for (const a of raw?.attrs || []) {
                if (a?.rightValue?.value != null) vals.add(String(a.rightValue.value))
            }
            return vals
        },
        chipPick_isCurrent(chip, it) {
            return this.chipPick_currentValues(chip).has(String(it.id))
        },
        chipPick_widgetSource(raw) {
            // чипс привязан к виджету-селектору? → его данные и биндинг поля
            const selW = this.chipPick_widget(raw)   // IE 2026 09 22 / ODOO-5800: и скрытый (после Esc / скрытого открытия)
            const fieldSelectors = selW?.record?.doc?.params?.fieldSelectors
            const data = selW?.record?._doc?.data
            if (!selW?.vueObj || !fieldSelectors?.length || !Array.isArray(data)) return null   // IE 2026 09 22 / ODOO-5800: пустой список виджета — тоже источник
            const binding = raw.selector_binding || raw.leftValue?.value
            const fs = fieldSelectors.find(f => f.binding === binding) || fieldSelectors[0]
            return { selW, fs, data }
        },
        // IE 2026 09 10 / ODOO-… (заказ Игоря): текст подбора набран не в той раскладке — при пустом результате пробуем
        // конвертацию (EN↔RU, RU↔UK, смешанную); нашли — показываем эти значения и подсказку «Matches for …»
        // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): подбор — ПОРЦИОННЫЙ проход по словарю (кусками ≈30 мс, между ними интерфейс живёт), результат в
        // chipPickResult; новый ввод / другой чипс отменяют прежний проход (run). На словаре в 94 тыс. названий синхронный проход
        // со всеми ступенями занимал 1–3 с и замораживал страницу. Точные совпадения появляются с первых порций
        chipPickItems(chip) {
            const seg = this.chipPick_segment(chip)
            const txt = (this.chipPickQuery || '').trim().toLowerCase()   // с задержкой (watch chipPickText)
            const IS = seg ? this.store.itemsSource[seg] : null
            const dictLen = IS?.data?.length ?? (IS?.ids ? 1 : 0)
            // IE 2026 09 22 / ODOO-5800: состояние виджета-селектора и число его строк — в подписи: данные виджета пришли → список считается заново
            const rawW = chip?._raw || chip
            const wst = this.chipPick_widgetState(rawW)
            const wsrc = wst === 'ready' ? this.chipPick_widgetSource(rawW) : null
            const sig = `${seg}|${txt}|${dictLen}|${this.chipPickFocused ? 1 : 0}|${wst}:${wsrc ? this.chipPick_dataToken(wsrc.data) : 0}`   // IE 2026 09 22: идентичность данных, не число строк
            const R = this.chipPickResult
            if (R.key !== sig && this._chipPickSched !== sig) {
                this._chipPickSched = sig
                setTimeout(() => { if (this._chipPickSched === sig) this.chipPick_recompute(chip, sig) }, 0)
            }
            return R.key === sig || R.seg === seg ? R.out : []
        },
        chipPick_sets(txt) {
            const terms = searchTerms(txt)
            const sets = [{ terms, conv: '' }, ...(txt ? layoutCandidates(txt).map(c => ({ terms: searchTerms(c), conv: c })) : [])]
            for (const S of sets) S.prep = prepareTerms(S.terms)   // термины готовятся один раз на проход
            // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): наборы последней ступени — отдельным списком (проход ступеней 0…6 их не видит)
            const _extra = (txt && this.chipPick_extraOn()) ? searchExtraSets(txt) : []
            sets.extra = _extra.length ? _extra : null
            sets.extraExact = _extra.some(S => S.step === EXTRA_TIER_SYMBOL)   // есть очищенный текст (шаг 7) — у последней ступени есть дешёвый первый проход
            return sets
        },
        chipPick_recompute(chip, sig) {
            const run = (this._chipPickRun = (this._chipPickRun || 0) + 1)
            const seg = this.chipPick_segment(chip)
            const txt = (this.chipPickQuery || '').trim().toLowerCase()
            const sets = this.chipPick_sets(txt)
            let gen = this.chipPick_candidates(chip)   // IE 2026 09 30 / ODOO-5800: let — второй проход (подсегмент) заменяет источник и оценку
            let sc = this.chipPick_scorer(sets)
            let wide = null, first = null, firstSc = null   // IE 2026 09 30 / ODOO-5800: область { segs, seg, label } и итог первого прохода (строки виджета), когда идёт проход по подсегменту
            // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): последняя ступень — на ступенях 0…6 пусто и в строках виджета, и в подсегменте: те же проходы ещё раз,
            // оценкой chipPick_extraScorer по наборам sets.extra (до тех пор результат и цена прежних проходов — как были).
            // extraPass: 0 — прежние ступени; 1 — очищенный текст точно (дёшево); 2 — очищенный текст по ступеням опечаток и один лишний знак
            let extraPass = 0
            const scorer = () => (extraPass ? this.chipPick_extraScorer(sets.extra, extraPass === 1) : this.chipPick_scorer(sets))
            const extraNext = () => {
                if (!sets.extra || extraPass >= 2 || sc.result().out.length || (first && first.out.length)) return false
                extraPass = (extraPass === 0 && sets.extraExact) ? 1 : 2
                wide = null; first = null; firstSc = null
                gen = this.chipPick_candidates(chip); sc = scorer()
                return true
            }
            const publish = (pending, S = sc, W = null) => {   // IE 2026 09 30 / ODOO-5800: + чей результат (S) и область расширения (W)
                const res = S.result()
                this.chipPickResult = { ...res, pending, key: sig, seg, wide: W && res.out.length ? W : null }   // IE 2026 09 30 / ODOO-5800: + wide
                if (!pending) {
                    if (this.chipPickConv !== res.conv) this.chipPickConv = res.conv
                    if (this.chipPickFuzzy !== res.tier) this.chipPickFuzzy = res.tier
                    if (this.chipPickInner !== (res.inner || 0)) this.chipPickInner = res.inner || 0   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе)
                }
            }
            const step = () => {
                if (run !== this._chipPickRun) return
                const t0 = performance.now()
                for (;;) {   // IE 2026 09 30 / ODOO-5800: проход по строкам виджета, затем (нет точных совпадений) — по подсегменту грани
                    let n
                    while (!(n = gen.next()).done) {
                        sc.consider(n.value.id, n.value.title, n.value.alt)   // IE 2026 09 25 / ODOO-341407: + названия на других языках
                        if (sc.full()) break
                        if (performance.now() - t0 > 30) {
                            if (wide && first.out.length) publish(true, firstSc)   // IE 2026 09 30 / ODOO-5800: пока ищем в каталоге — приближённые из виджета на месте
                            else publish(true, sc, wide)   // IE 2026 09 30 / ODOO-5800
                            setTimeout(step, 0); return
                        }
                    }
                    if (wide) { if (extraNext()) continue; break }   // IE 2026 09 30 / ODOO-5800; IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): пусто везде — проходы последней ступени
                    const r0 = sc.result()
                    if (r0.out.length && !(r0.tier > 0)) break   // IE 2026 09 30 / ODOO-5800: в строках виджета есть точные (и в другой раскладке) — не расширяем
                    if (extraPass && r0.out.length && r0.rank === EXTRA_TIER_SYMBOL) break   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): очищенный текст найден в строках виджета точно — не расширяем (как при точных на ступени 0)
                    const scope = this.chipPick_wideScopeIf(chip, txt)   // IE 2026 09 30 / ODOO-5800
                    if (!scope) { if (extraNext()) continue; break }   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе)
                    wide = scope; first = r0; firstSc = sc   // IE 2026 09 30 / ODOO-5800
                    gen = this.chipPick_wideCandidates(chip, scope); sc = scorer()   // IE 2026 09 30 / ODOO-5800: та же лестница поиска; IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): scorer() — оценка текущего прохода
                }
                // IE 2026 09 30 / ODOO-5800: каталог показывается, если в виджете пусто или там только приближённые, а в каталоге ступень лучше (точные при опечатках
                // в виджете); иначе — прежний результат виджета
                if (wide && !this.chipPick_wideBetter(first, sc.result())) { publish(false, firstSc); return }
                publish(false, sc, wide)
            }
            step()
        },
        // IE 2026 09 30 / ODOO-5800: результат по подсегменту заменяет результат строк виджета — там пусто, либо там только приближённые, а здесь ступень ниже
        chipPick_wideBetter(first, res) {
            return !!res?.out?.length && (!first?.out?.length || (res.rank ?? res.tier) < (first.rank ?? first.tier))   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): rank — только у последней ступени (различает шаг 7 по ступени опечаток внутри него); у ступеней 0…6 — tier, как было
        },
        // синхронный полный проход (Enter — применить первый)
        // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): на ступенях 0…6 пусто и в строках виджета, и в подсегменте — те же проходы наборами последней ступени
        chipPick_filterSync(chip) {
            const res = this.chipPick_filterFlow(chip, 0)
            if (res.out.length) return res
            for (const phase of [1, 2]) { const r = this.chipPick_filterFlow(chip, phase); if (r && r.out.length) return r }
            return res
        },
        // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): прежнее тело chipPick_filterSync; extraPass — проход последней ступени (1 — очищенный текст точно, 2 — ступени опечаток и
        // один лишний знак; нет наборов этого прохода — null), 0 — прежние ступени
        chipPick_filterFlow(chip, extraPass = 0) {
            const txt = (this.chipPickQuery || '').trim().toLowerCase()   // IE 2026 09 30 / ODOO-5800
            const sets = this.chipPick_sets(txt)
            if (extraPass && (!sets.extra || (extraPass === 1 && !sets.extraExact))) return null
            const pass = (gen) => {   // IE 2026 09 30 / ODOO-5800: общий проход — строки виджета, затем при пустом результате подсегмент грани
                const sc = extraPass ? this.chipPick_extraScorer(sets.extra, extraPass === 1) : this.chipPick_scorer(sets)   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе)
                let n
                while (!(n = gen.next()).done) { sc.consider(n.value.id, n.value.title, n.value.alt); if (sc.full()) break }   // IE 2026 09 25 / ODOO-341407: + alt
                return sc.result()
            }
            const res = pass(this.chipPick_candidates(chip))
            if (res.out.length && !(res.tier > 0)) return res   // IE 2026 09 30 / ODOO-5800: точные в строках виджета
            if (extraPass && res.out.length && res.rank === EXTRA_TIER_SYMBOL) return res   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): очищенный текст найден в строках виджета точно — не расширяем
            const scope = this.chipPick_wideScopeIf(chip, txt)   // IE 2026 09 30 / ODOO-5800: Enter — первое совпадение того же списка, что показан (подсегмент или виджет)
            if (!scope) return res
            const res2 = pass(this.chipPick_wideCandidates(chip, scope))   // IE 2026 09 30 / ODOO-5800
            return this.chipPick_wideBetter(res, res2) ? { ...res2, wide: scope } : res   // IE 2026 09 30 / ODOO-5800
        },
        // ---- IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе) ----
        // Последняя ступень подбора — отдельными проходами ПОСЛЕ прежних: пока ступени 0…6 (chipPick_scorer — не менялся) хоть что-то находят в строках
        // виджета или в подсегменте, её нет вовсе — ни в результате, ни в цене. Наборы — utils/searchMatch.searchExtraSets (sets.extra): шаг 7 — текст без
        // лишних символов по обычным ступеням, шаг 8 — один лишний знак. Проходов два (каждый — строки виджета, затем подсегмент): 1) очищенный текст ТОЧНО —
        // дешёвый (indexOf), он и закрывает случай Игоря «… ©»; 2) только при пустом первом — очищенный текст по ступеням опечаток и шаг 8 (цена — как у
        // прежнего прохода без результата). Выключатель fe.search.extraSymbolTier (дефолт вкл.; false — прежнее «No matches»)
        chipPick_extraOn() {
            return this.store.setting('fe.search.extraSymbolTier', true) !== false
        },
        chipPick_isExtra(tier) { return tier >= EXTRA_TIER_SYMBOL },
        // подсветка пунктов списка на последней ступени: всё найденное — приближённое (chip-pick-fuzzy)
        chipPick_approx(ranges) {
            return (ranges && this.chipPick_isExtra(this.chipPickFuzzy)) ? ranges.map(r => ({ ...r, fuzzy: true })) : ranges
        },
        // оценка прохода последней ступени — та же форма, что у chipPick_scorer: лучший ранг (меньше — главнее), out — до 15 значений этого ранга;
        // exactOnly — первый проход: только шаг 7 и только точные совпадения очищенного текста
        chipPick_extraScorer(extra, exactOnly = false) {
            const out = []
            const best = { rank: Infinity, si: Infinity, conv: '' }
            const top = extra[0].step   // лучший возможный ранг — первый набор списка, найденный точно
            return {
                consider: (id, title, alt) => {
                    const x = this.chipPick_extraMatch(extra, best.rank, id, title, alt, exactOnly)
                    if (!x) return
                    if (x.rank < best.rank || (x.rank === best.rank && x.si < best.si)) { best.rank = x.rank; best.si = x.si; best.conv = x.conv; out.length = 0 }
                    if (x.rank === best.rank && x.si === best.si && out.length < 15) out.push(x.altHit ? { id, title, altHit: x.altHit } : { id, title })
                },
                full: () => best.rank === top && best.si === 0 && out.length >= 15,
                // tier — номер шага (7 | 8) для подписи; rank — для сравнения с результатом подсегмента (chipPick_wideBetter): у шага 8 варианты равны;
                // inner — ступень опечаток внутри шага 7; conv — текст, по которому найдено
                result: () => {
                    const none = best.rank === Infinity, step = none ? 0 : Math.floor(best.rank)
                    return { out: out.slice(), tier: step, conv: none ? '' : best.conv, rank: none ? 0 : Math.min(best.rank, EXTRA_TIER_CHAR),
                        inner: step === EXTRA_TIER_SYMBOL ? Math.round((best.rank - EXTRA_TIER_SYMBOL) * 10) : 0 }
                },
            }
        },
        // оценка значения: { rank, si, altHit, conv } либо null. Шаг 7 — очищенный текст по обычным ступеням (ранг 7 + ступень / 10: точное после очистки
        // главнее опечаток после очистки; ступени хуже уже найденной не считаются); шаг 8 — один лишний знак (ранг 8 + (набор, вариант) / 1000), только
        // пока шаг 7 не дал ничего
        chipPick_extraMatch(extra, bestRank, id, title, alt, exactOnly = false) {
            let res = null
            const alts = (Array.isArray(alt) && alt.length) ? alt : null
            const cap = exactOnly ? 0 : (bestRank < EXTRA_TIER_CHAR ? Math.round((bestRank - EXTRA_TIER_SYMBOL) * 10) : FUZZY_TIERS)
            for (let si = 0; si < extra.length; si++) {
                const S = extra[si]
                let rank = Infinity, altHit = '', conv = ''
                if (S.step === EXTRA_TIER_SYMBOL) {
                    let r = matchTermsBest(title, S.prep, cap)
                    if (!r && matchTerms(String(id), S.terms, false)) r = { tier: 0 }
                    if (alts && (!r || r.tier > 0)) {
                        for (const v of alts) {
                            const ra = v ? matchTermsBest(String(v), S.prep, cap) : null
                            if (ra && (!r || ra.tier < r.tier)) { r = { tier: ra.tier }; altHit = String(v); if (ra.tier === 0) break }
                        }
                    }
                    if (r) { rank = EXTRA_TIER_SYMBOL + r.tier / 10; conv = S.conv }
                } else {
                    if (exactOnly || bestRank < EXTRA_TIER_CHAR || res) continue
                    let k = matchExtraChar(lowerCached(title), S.P)
                    if (alts) for (const v of alts) { const ka = v ? matchExtraChar(lowerCached(String(v)), S.P) : -1; if (ka >= 0 && (k < 0 || ka < k)) { k = ka; altHit = String(v) } }
                    if (k >= 0) { rank = EXTRA_TIER_CHAR + (S.ord * 25 + k) / 1000; conv = S.P.vars[k].text }
                }
                if (rank !== Infinity && (!res || rank < res.rank)) res = { rank, si, altHit, conv }
                if (res && res.rank === EXTRA_TIER_SYMBOL) break
            }
            return res
        },
        // чипс поиска таблицы, найденный последней ступенью (решение грида — AttrGrid.searchExtra_decide: _extra = 7 | 8, _extraFor = текст): номер шага либо 0
        chipExtraOn(chip) {
            const raw = chip?._raw || chip
            return (!!raw && raw.segment === 'CHIP-Search' && !!raw._extra && raw._extraFor === String(raw.title || '').trim()) ? raw._extra : 0
        },
        // IE 2026 09 30 / ODOO-5800 (Igor 30.09, скрин 2W1_001: чипсы Об'єкт «Дип Хит крем 67г» + Країна «Білорусь», подбор Об'єкт «фай» → «Немає збігів», без чипса страны
        // «файб» находит 4 «Файберика …»: «если при подборе не найдены строки, то попробовать расширить поиск, ища под подсегментом, а не под виджетом селектора; при
        // этом сообщай об этом»). Кандидаты селекторного чипса — строки виджета-селектора, а он режется прочими чипсами стола. Нет совпадений среди них — тот же
        // поиск (части в любом порядке, раскладка, опечатки, названия на других языках) по ПОДСЕГМЕНТУ грани: записи словаря сегмента чипса (уже загружен при
        // фокусе — chipPick_load), чей segment — сегмент записей виджета / значений чипса (DS::OBJECTS → 0Ag_001 «СводнаяНоменклатура»; у DS::SUBJECTS страны —
        // segment-5, а не весь словарь субъектов с поставщиками и складами — его Игорь 22.09 в подборе Country не хотел). Над списком — строка «поиск расширен».
        // Приближённые совпадения виджета (ступени опечаток) — тоже «не найдено»: каталог заменяет их, если там ступень лучше (проба «файбер мульти» под Беларусью:
        // виджет давал ступень опечаток с чужими названиями, каталог — точную «Файберика Мульти капс №60»); точные и найденные в другой раскладке — не расширяются.
        // Выбор ставит чипс обычным путём (значения нет в данных виджета → значения пишутся в attrs чипса, бэк пересчитывает наборы). Выключатель
        // fe.ux.chipPickSubsegmentFallback (по умолчанию вкл.; false — прежнее «No matches»)
        chipPick_wideOn() {
            return this.store.setting('fe.ux.chipPickSubsegmentFallback', true) !== false
        },
        // область расширенного поиска или null: только когда кандидаты — строки виджета-селектора (у прочих чипсов подбор и так идёт по словарю сегмента) и текст набран
        chipPick_wideScopeIf(chip, txt) {
            if (!txt || !this.chipPick_wideOn()) return null
            const raw = chip?._raw || chip
            if (!this.chipPick_isSelectorForm(raw) || this.store.fastEntry.chipPickFromWidget === false) return null
            const src = this.chipPick_widgetSource(raw)
            return src ? this.chipPick_wideScope(chip, src) : null
        },
        // подсегмент грани: сегменты записей (поле segment строк словаря) по строкам виджета и значениям чипса; у словаря без segment в строках весь словарь и есть
        // каталог подсегмента (segs = null). Сегмент не определить (виджет пуст, чипс без значений, в словаре несколько сегментов) / словарь не загружен — null
        chipPick_wideScope(chip, src) {
            const seg = this.chipPick_segment(chip)
            const IS = seg ? this.store.itemsSource[seg] : null
            const data = IS?.data
            if (!Array.isArray(data) || !data.length) return null
            const raw = chip?._raw || chip
            const label = String(raw?.leftValue?.valueStr || raw?.leftValue?.value || seg)   // подпись грани, пока нет названия подсегмента
            const segOf = (r) => (r && typeof r.segment === 'string' && r.segment) ? r.segment : null
            let segs = null
            if (data.some(segOf)) {
                const byId = IS.ids || Object.fromEntries(data.filter(r => r?.id != null).map(r => [String(r.id), r]))
                segs = new Set()
                const add = (v) => { const s = v == null || v === '' ? null : segOf(byId[v] || byId[String(v)]); if (s) segs.add(s) }
                const walk = (rows) => { for (const r of rows || []) { add(this.store.get_value(r, src.fs.binding)); if (Array.isArray(r?.attrs) && r.attrs.length) walk(r.attrs) } }
                walk(src.data)
                for (const v of this.chipPick_currentValues(chip)) add(v)
                if (!segs.size) {
                    const all = new Set(data.map(segOf).filter(Boolean))
                    if (all.size !== 1) return null
                    segs.add([...all][0])
                }
            }
            // названия подсегментов: входящих сегментов 1С (segment-5 «БиТ_Регионы (5-2-1)») в словаре SEGMENTS нет — дозапрос by_ids из SEGMENTS-UNION
            const missing = (segs ? [...segs] : [seg]).filter(s => !this.chipPick_segTitle(s))
            if (missing.length) {
                try { this.store.get_itemsSource({}, {}, { attr_type: 'AttrLink', relation_segment: 'SEGMENTS-UNION' }, { fetchMode: 'by_ids', ids: missing }) } catch (e) { /* подпись — название грани */ }
            }
            return { segs, seg, label }
        },
        // IE 2026 09 30 / ODOO-5800: название сегмента по словарям SEGMENTS / SEGMENTS-UNION ('' — нет)
        chipPick_segTitle(s) {
            const IS = this.store.itemsSource
            return String(IS?.SEGMENTS?.ids?.[s]?.title || IS?.['SEGMENTS-UNION']?.ids?.[s]?.title || '')
        },
        // IE 2026 09 30 / ODOO-5800: подпись каталога в строке «поиск расширен» — названия подсегментов; пока дозапрос не ответил — подпись грани (реактивно: ответ бампает itemsSource_change_index)
        chipPick_wideTitle(wide) {
            if (!wide) return ''
            void this.store.itemsSource_change_index
            const titles = (wide.segs ? [...wide.segs] : [wide.seg]).map(s => this.chipPick_segTitle(s))
            return titles.every(Boolean) ? titles.join(', ') : wide.label
        },
        // кандидаты расширенного поиска { id, title, alt } — записи словаря сегмента чипса из подсегмента грани, в порядке словаря
        *chipPick_wideCandidates(chip, scope) {
            const seg = this.chipPick_segment(chip)
            const data = (seg && this.store.itemsSource[seg]?.data) || []
            for (const r of data) {
                const id = r?.id ?? r?.value
                if (id == null || id === '') continue
                if (scope?.segs && !scope.segs.has(r.segment)) continue
                yield { id, title: String(r.title ?? r.valueStr ?? id), alt: r.titles_alt }
            }
        },
        // оценка кандидата: лучший (ступень, набор) по названию; id — только точно; out пополняется по текущему лучшему,
        // сбрасывается при улучшении; хватит — 15 точных как набрано (иначе проход до конца: может найтись ступень ниже)
        // ---- IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл) ----
        // Поле поиска в полосе чипсов стола (mode Page, десктоп; мобильная ветка и полосы таблиц — как были). Набранный текст (от 2 знаков, пауза 250 мс) ищется
        // СРАЗУ по словарям всех селекторов стола (строки WHERE со ссылочным сегментом): та же лестница, что у подбора в форме чипса (chipPick_sets →
        // chipPick_scorer: части в любом порядке, раскладка, ступени опечаток, названия на других языках; ничего на ступенях 0…6 — последняя ступень
        // chipPick_extraScorer, «лишний символ»). Проход порциями по 30 мс, новый ввод отменяет прежний. Показываются группы лучшей ступени: есть точные —
        // только они («если не удался полностью — с учётом ошибки»), до fe.ux.chipsBarSearchLimit (5) значений на селектор. Выбор — applyChipPick
        // (виджет-селектор либо значения в attrs строки, emitChips → workspace_applySelector), как из формы чипса.
        // // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів): на фокус ничего не грузится; словари — только при наборе и с потолком (utils/chipsBarLoad: local / load / server)
        // Флаг fe.ux.chipsBarInput (дефолт вкл.; false — полоса без поля, как было)
        barInput_on() {
            return this.mode === 'Page' && !this.inputOff && !this.store.mobileUi?.on && this.store.setting('fe.ux.chipsBarInput', true) !== false
        },
        // IE 2026 11 04 / ODOO-350449 (заказ Игоря: верхняя полоса в один ряд — иконки вместо подписей, «⋯» вместо поиска): поле поиска полосы — «⋯» вместо подписи, когда в полосе есть чипсы (без чипсов — прежняя подпись)
        barDots_on() {
            return this.barInput_on() && (this.visibleChips || []).length > 0 && this.store.setting('fe.ux.topBarCompact', true) !== false
        },
        barSearch_rows() {
            const out = [], seen = new Set()
            for (const chip of this.chips || []) {
                if (!chip || chip._global) continue
                const raw = chip._raw || chip
                const seg = this.chipPick_segment(chip)
                if (!seg || !raw) continue
                const binding = String(raw.selector_binding || raw.leftValue?.value || '')
                const key = binding + '|' + seg
                if (seen.has(key)) continue
                seen.add(key)
                out.push({ key, chip, raw, seg, title: String(raw.leftValue?.valueStr || raw.title || binding || seg) })
            }
            return out
        },
        barSearch_focus() {
            this.barOpen = true   // // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів): фокус (и восстановленный после перезагрузки) словари не грузит
            this.barSearch_place()
            if (this.barQuery.length >= 2 && !this.barGroups.length) this.barSearch_run(this.barQuery)
        },
        // // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів)
        barSearch_cfg() {
            const st = (k, d) => this.store.setting(k, d)
            return {
                maxRows: chipsBar_num(st('fe.ux.chipsBarMaxRows', CHIPS_BAR_MAX_ROWS), CHIPS_BAR_MAX_ROWS),
                maxSegments: chipsBar_num(st('fe.ux.chipsBarMaxSegments', CHIPS_BAR_MAX_SEGMENTS), CHIPS_BAR_MAX_SEGMENTS, 0),
                srvLimit: chipsBar_num(st('fe.ux.chipsBarServerLimit', CHIPS_BAR_SERVER_LIMIT), CHIPS_BAR_SERVER_LIMIT),
                maxBytes: chipsBar_num(st('fe.ux.chipsBarMaxBytes', CHIPS_BAR_MAX_BYTES), CHIPS_BAR_MAX_BYTES),   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search)
            }
        },
        // один «набор» — от первого запроса до очистки поля / Esc / выбора: бюджет словарей, загрузки в пути, серверные ответы по сегментам
        barSearch_session() {
            return this._barSet || (this._barSet = { used: 0, usedBytes: 0, inFlight: new Map(), srv: Object.create(null) })   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): + usedBytes
        },
        barSearch_cancel() {
            const S = this._barSet
            this._barSet = null
            this._barMissing = 0
            this.barLoading = false
            if (S) for (const k in S.srv) { try { S.srv[k].xhr?.abort?.() } catch (e) { /* уже завершён */ } }
            if (this._barUnwatch) { this._barUnwatch(); this._barUnwatch = null }
        },
        barSearch_state(r, cfg) {
            const { kind, hint } = this.barSearch_kind(r.seg)   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): вид источника и размер — из словаря SEGMENTS
            return chipsBar_segState(r.seg, this.store.itemsSource[r.seg], { kind, hint, sizes: CHIPS_BAR_SIZES, maxRows: cfg.maxRows, inFlight: this._barSet?.inFlight })
        },
        barSearch_kind(seg) { return chipsBar_segKind(seg, this.store.itemsSource?.SEGMENTS?.ids?.[seg]) },   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search)
        // план прохода: загрузки словарей в бюджет набора (малые первыми), серверный поиск больших; возвращает план (utils/chipsBarLoad)
        barSearch_prepare(rows, q) {
            const S = this.barSearch_session(), cfg = this.barSearch_cfg()
            const hints = Object.create(null)   // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): rows_hint строк SEGMENTS — порядок загрузок и оценка байт
            for (const r of rows) { const h = this.barSearch_kind(r.seg).hint; if (h != null) hints[r.seg] = h }
            const plan = chipsBar_plan(rows, r => this.barSearch_state(r, cfg), { used: S.used, maxSegments: cfg.maxSegments, sizes: CHIPS_BAR_SIZES, hints, bytes: CHIPS_BAR_BYTES, usedBytes: S.usedBytes, maxBytes: cfg.maxBytes })
            for (const r of plan.load) {
                S.used++
                S.inFlight.set(r.seg, Date.now())
                this.store.get_itemsSource_bySegment(r.seg)
            }
            if (plan.load.length) this.barSearch_watch()
            // сверх бюджета набора — тоже серверным поиском (лёгкий запрос с потолком строк), а не «не искали»
            for (const r of [...plan.server, ...plan.skipped]) this.barSearch_server(r.seg, q, cfg.srvLimit)
            return plan
        },
        barSearch_watch() {
            if (!this._barUnwatch) this._barUnwatch = this.$watch(() => this.store.itemsSource_change_index, () => this.barSearch_settle())
            window.$cg.setTimeout(this, 'AttrChips.barSettle', () => this.barSearch_settle(), 30000)   // ответа нет (ошибка бэка) — не «Loading…» навсегда
        },
        // ответы загрузок: словарь пришёл — размер в CHIPS_BAR_SIZES (больше потолка / обрезан — дальше серверный поиск), проход заново
        barSearch_settle() {
            const S = this._barSet
            if (!S) return
            let changed = false
            for (const [seg, t0] of S.inFlight) {
                const IS = this.store.itemsSource[seg]
                const busy = this.store._itemsSource_queue?.[seg] || this.store._itemsSource_active?.[seg]
                if (Array.isArray(IS?.data) && !busy) {
                    CHIPS_BAR_SIZES[seg] = IS._all_truncated ? Infinity : IS.data.length
                    // IE 2026 11 02 / ODOO-350449 (p213b: вид джерела і розмір сегмента у словнику SEGMENTS, LIMIT у name_search): байты словаря — в бюджет набора; больше бюджета — большой по факту (дальше серверный поиск)
                    const b = CHIPS_BAR_BYTES[seg] = chipsBar_dataBytes(IS.data)
                    S.usedBytes += b
                    if (b > this.barSearch_cfg().maxBytes) CHIPS_BAR_SIZES[seg] = Infinity
                    S.inFlight.delete(seg); changed = true
                } else if (Date.now() - t0 >= 30000) {
                    S.inFlight.delete(seg); changed = true
                }
            }
            if (changed && this.barOpen && this.barQuery.length >= 2) this.barSearch_run(this.barQuery)
        },
        barSearch_server(seg, q, limit) {
            const S = this._barSet
            const cur = S.srv[seg]
            if (cur && cur.q === q) return
            try { cur?.xhr?.abort?.() } catch (e) { /* уже завершён */ }
            const ent = S.srv[seg] = { q, items: null, xhr: null }
            const t0 = performance.now()
            ent.xhr = this.store.chipsBar_nameSearch(this, seg, q, limit, (items) => {
                if (this._barSet !== S || S.srv[seg] !== ent) return
                ent.items = items; ent.xhr = null
                window.chipsBarServer = { seg, q, n: items.length, ms: Math.round(performance.now() - t0) }   // замер пробы
                if (this.barOpen && this.barQuery === q) this.barSearch_run(q)
            })
        },
        // строки, по которым ищет проход: словарь целиком (local) или ответ сервера на текущий текст (server); иначе — пока ничего
        barSearch_data(r, cfg) {
            const st = this.barSearch_state(r, cfg)
            if (st === 'local') return this.store.itemsSource[r.seg]?.data || []
            if (st === 'loading') return []
            const e = this._barSet?.srv?.[r.seg]   // 'server' и сверх бюджета ('none') — ответ сервера на этот текст
            return (e && e.q === this.barQuery && e.items) || []
        },
        barSearch_blur() {
            this.barOpen = false
            this.barActive = -1
        },
        barSearch_place() {
            const el = this.$refs.barInput
            if (!el) return
            const r = el.getBoundingClientRect(), w = Math.min(560, window.innerWidth - 16)
            this.barListStyle = { top: (r.bottom + 4) + 'px', left: Math.max(8, Math.min(r.left - 6, window.innerWidth - w - 8)) + 'px', width: w + 'px' }
        },
        barSearch_input(v) {
            clearTimeout(this._barT)
            this._barT = null
            const q = String(v || '').trim()
            if (q.length < 2) { this._barRun++; this.barQuery = q; this.barGroups = []; this.barPending = false; this.barActive = -1; this.barSearch_cancel(); return }   // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів): очистка — отмена загрузок набора
            const ms = Math.max(0, Number(this.store.setting('fe.ux.chipsBarSearchDebounceMs', 250)) || 0)
            this._barT = window.$cg.setTimeout(this, 'AttrChips.barSearch', () => { this._barT = null; this.barOpen = true; this.barSearch_place(); this.barSearch_run(q) }, ms)
        },
        // кандидаты одного селектора — словарь его сегмента (без скрытого открытия виджетов-селекторов: N селекторов — N widget_open); у словаря с записями
        // разных сегментов — только подсегмент значений чипса / строк его виджета (как расширенный подбор формы, chipPick_wideScope)
        *barSearch_candidates(r, cfg) {
            const data = this.barSearch_data(r, cfg)   // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів)
            const segs = this.barSearch_segs(r, data)
            for (const x of data) {
                const id = x?.id ?? x?.value
                if (id == null || id === '') continue
                if (segs && !segs.has(x.segment)) continue
                yield { id, title: String(x.title ?? x.valueStr ?? id), alt: x.titles_alt }
            }
        },
        barSearch_segs(r, data) {
            if (!data.length || !data.some(x => x && typeof x.segment === 'string' && x.segment)) return null
            const src = this.chipPick_isSelectorForm(r.raw) ? this.chipPick_widgetSource(r.raw) : null
            if (src) return this.chipPick_wideScope(r.chip, src)?.segs || null
            const IS = this.store.itemsSource[r.seg], segs = new Set()
            for (const v of this.chipPick_currentValues(r.chip)) { const s = IS?.ids?.[v]?.segment; if (s) segs.add(s) }
            return segs.size ? segs : null
        },
        barSearch_run(txt) {
            const run = (this._barRun = (this._barRun || 0) + 1)
            const q = String(txt || '').trim().toLowerCase()
            this.barQuery = String(txt || '').trim()
            if (q.length < 2) { this.barGroups = []; this.barPending = false; return }
            const sets = this.chipPick_sets(q)
            const lim = Math.max(1, Number(this.store.setting('fe.ux.chipsBarSearchLimit', 5)) || 5)
            const rows = this.barSearch_rows()
            // // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів): загрузки и серверный поиск — по плану; ждём словари в пути и ответы сервера на этот текст
            const plan = this.barSearch_prepare(rows, this.barQuery), cfg = this.barSearch_cfg()
            const srvRows = [...plan.server, ...plan.skipped]
            const srvWait = srvRows.filter(r => !this._barSet?.srv?.[r.seg]?.items).length
            this._barMissing = plan.load.length + plan.loading.length + srvWait
            this.barLoading = this._barMissing > 0
            const live = [...plan.local, ...srvRows]
            let extraPass = 0, ji = 0
            const mk = () => live.map(r => ({ r, gen: this.barSearch_candidates(r, cfg), sc: extraPass ? this.chipPick_extraScorer(sets.extra, extraPass === 1) : this.chipPick_scorer(sets) }))
            let jobs = mk()
            const publish = (pending) => {
                const groups = []
                for (const j of jobs) {
                    const res = j.sc.result()
                    if (!res.out.length) continue
                    groups.push({ key: j.r.key, chip: j.r.chip, title: j.r.title, tier: res.tier, conv: res.conv, inner: res.inner || 0, rank: Math.floor(res.rank ?? res.tier), items: res.out.slice(0, lim) })
                }
                // есть точные (и найденные в другой раскладке) — только они; точных нет нигде — все селекторы с приближёнными, ближняя ступень выше
                // (у каждого селектора своя лучшая ступень: «магнитофер» — Країна «Магнитогорск» ступенью 3 и Об'єкт «Мальтофер» ступенью 6)
                const exact = groups.some(g => g.rank === 0)
                let off = 0
                const shown = groups.filter(g => !exact || g.rank === 0).sort((a, b) => a.rank - b.rank).map(g => { const x = { ...g, offset: off }; off += g.items.length; return x })
                this.barGroups = shown
                this.barPending = pending
                if (this.barActive >= off) this.barActive = off ? 0 : -1
                if (!pending && this._barPickFirst === run) { this._barPickFirst = 0; this.barSearch_enter() }
            }
            this.barPending = true
            const step = () => {
                if (run !== this._barRun) return
                const t0 = performance.now()
                while (ji < jobs.length) {
                    const j = jobs[ji]
                    let n
                    while (!(n = j.gen.next()).done) {
                        j.sc.consider(n.value.id, n.value.title, n.value.alt)
                        if (j.sc.full()) break
                        if (performance.now() - t0 > 30) { publish(true); setTimeout(step, 0); return }
                    }
                    ji++
                }
                // ступени 0…6 пусты во всех селекторах — последняя ступень (очищенный текст точно, затем опечатки и один лишний знак)
                if (sets.extra && extraPass < 2 && !jobs.some(j => j.sc.result().out.length)) {
                    extraPass = (extraPass === 0 && sets.extraExact) ? 1 : 2
                    jobs = mk(); ji = 0
                    setTimeout(step, 0)
                    return
                }
                publish(false)
            }
            step()
        },
        barSearch_flat() {
            const out = []
            for (const g of this.barGroups) for (const it of g.items) out.push({ g, it })
            return out
        },
        barSearch_move(d) {
            const n = this.barSearch_flat().length
            if (!this.barOpen) { this.barOpen = true; this.barSearch_place() }
            if (!n) return
            this.barActive = this.barActive < 0 ? (d > 0 ? 0 : n - 1) : (this.barActive + d + n) % n
            this.$nextTick(() => this.$el?.ownerDocument?.querySelector?.('.chips-bar-list .chips-bar-active')?.scrollIntoView?.({ block: 'nearest' }))
        },
        barSearch_enter() {
            const q = String(this.barText || '').trim()
            if (q.length < 2) return
            if (this._barT || q !== this.barQuery) {   // Enter до паузы — поиск сразу, выбор первого по готовности
                clearTimeout(this._barT); this._barT = null
                this.barOpen = true; this.barSearch_place()
                this.barSearch_run(q)
                this._barPickFirst = this._barRun
                if (!this.barPending) { this._barPickFirst = 0; this.barSearch_enter() }
                return
            }
            if (this.barPending) { this._barPickFirst = this._barRun; return }
            const flat = this.barSearch_flat()
            const x = flat[this.barActive >= 0 ? this.barActive : 0]
            if (x) this.barSearch_pick(x.g, x.it)
        },
        barSearch_esc() {
            if (this.barText) { this.barText = ''; this.barGroups = []; this.barQuery = ''; this.barSearch_cancel(); return }   // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів)
            this.$refs.barInput?.blur()
        },
        barSearch_pick(g, it) {
            const index = this.visibleChips.indexOf(g.chip)
            this.applyChipPick(index, g.chip, it)
            window.chipsBarPicks = (window.chipsBarPicks || 0) + 1   // счётчик выборов из поля полосы
            this._barRun++
            this.barText = ''; this.barQuery = ''; this.barGroups = []; this.barActive = -1; this.barPending = false
            this.barSearch_cancel()   // IE 2026 11 02 / ODOO-350449 (дефект боя webscm #125565 по p194: p213 — словники лише при наборі, ліміт, серверний пошук для великих сегментів)
            this.barOpen = false
        },
        barSearch_tierText(g) {
            const fz = !g.tier ? '' : (this.chipPick_isExtra(g.tier) ? extraTierText(g.tier, g.inner) : (FUZZY_TIER_TEXT[g.tier] || this.t('typos allowed')))
            const head = g.conv ? (g.tier ? this.t('Approximate matches for “{conv}”', { conv: g.conv }) : this.t('Matches for “{conv}”', { conv: g.conv })) : this.t('Approximate matches')
            return fz ? head + ' (' + fz + ')' : head
        },
        // подсветка совпадения в пункте — как chipPick_titleHTML, но по ступени и тексту своей группы
        barSearch_titleHTML(g, it) {
            const t = this.store.truncateText(it.title, 70)
            const q = (g.conv || this.barQuery || '').trim()
            const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            if (!q) return esc(t)
            const approx = (ranges) => (ranges && this.chipPick_isExtra(g.tier)) ? ranges.map(r => ({ ...r, fuzzy: true })) : ranges
            const opts = { tag: 'b', cls: 'chip-pick-hit', fuzzyCls: 'chip-pick-fuzzy', escape: true }
            if (it.altHit) {
                const a = this.store.truncateText(it.altHit, 70)
                const ar = approx(matchTerms(a, searchTerms(q), g.tier))
                return esc(t) + '<span class="chip-pick-alt">' + (ar ? highlightRanges(a, ar, opts) : esc(a)) + '</span>'
            }
            const ranges = approx(matchTerms(t, searchTerms(q), g.tier))
            return ranges ? highlightRanges(t, ranges, opts) : esc(t)
        },
        // ---- IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): копирование чипса стола и вставка в другой стол ----
        chipsBar_flag(key, dflt = true) { return chipsBarFlag_on((k, d) => this.store?.setting?.(k, d), key, dflt) },   // IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою): дефолт флага задаётся
        // IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою)
        // буфер доступен: navigator.clipboard.writeText есть и контекст безопасный (http без TLS — API нет / запрещён)
        chipsBar_clipboardOk() {
            try { return typeof window !== 'undefined' && window.isSecureContext !== false && typeof navigator?.clipboard?.writeText === 'function' } catch (e) { return false }
        },
        // строка WHERE чипса → `webscm-chip:{…}` в буфер, тост «Chip copied» при КАЖДОЙ записи (клик, Ctrl+C, кнопка Copy); false — чипс не переносится
        // (нет binding / значений). Период — обе границы (chipRow_period). IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою):
        // буфера нет — пропуск без тоста и без ошибки; отказ записи — тост-предупреждение только на явное копирование (Ctrl+C / Copy), клик (opts.silent) — молча
        chipsBar_copy(chip, opts = {}) {
            const text = chipRow_portable(chip?._raw || chip)
            if (!text) return false
            if (!this.chipsBar_clipboardOk()) return true
            const ok = () => { window.chipsBarCopies = (window.chipsBarCopies || 0) + 1; this.store.show_notification({ text: this.t('Chip copied'), status: 'success', timeout: 2000 }) }
            const fail = () => { if (!opts.silent) this.store.show_notification({ text: this.t('The chip could not be copied to the clipboard'), status: 'warning', timeout: 4000 }) }
            try { navigator.clipboard.writeText(text).then(ok, fail) } catch (e) { fail() }
            return true
        },
        // вставка в поле поиска полосы: переносимый чипс — включить его селектор в этом столе; обычный текст — в поле (вставка по умолчанию)
        barPaste(e) {
            if (!this.chipsBar_flag('fe.ux.chipsBarCopy')) return
            const chip = chipPortable_parse(e?.clipboardData?.getData('text') || '')
            if (!chip) return
            e.preventDefault()
            this.barPaste_apply(chip)
        },
        // строка WHERE с тем же binding: виджет-селектор (эталонный путь) либо значения в attrs строки (pageGotoChip_applyMany без создания строк);
        // строки нет — тост «нет селектора», стол не трогается
        barPaste_apply(chip) {
            const W = this.store.activeWORKSPACE
            const params = this.record?.doc?.params || W?.record?.doc?.params
            if (!params) return false
            const ids = chip.values.map(v => v.value)
            const row = chipPortable_findRow(params.WHERE, chip)
            const done = () => { window.chipsBarPastes = (window.chipsBarPastes || 0) + 1; this.barText = ''; this.barQuery = ''; this.barGroups = []; this.emitChips(); return true }
            // IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою):
            // период — обе границы через store.periodSel_set (как форма чипса «С … По»: потребители периода, без общего applySelector); строки периода в столе нет — тост
            if (chip.period) {
                if (!row || String(row.condition || '').toLowerCase() !== 'between') {
                    this.store.show_notification({ text: this.t('This workspace has no selector “{title}”', { title: chip.title }), status: 'warning', timeout: 5000 })
                    return false
                }
                this.store.periodSel_set(null, chip.period.from, chip.period.to, W?.workspace)
                window.chipsBarPastes = (window.chipsBarPastes || 0) + 1; this.barText = ''; this.barQuery = ''; this.barGroups = []
                return true
            }
            if (row) {
                this.chipPick_clearRv(row)
                if (this.chipSelector_applyViaWidget(row, ids)) return done()
            }
            const res = pageGotoChip_applyMany(params.WHERE, [chip], { create: false })
            if (res.missing.length || !res.set.length) {
                this.store.show_notification({ text: this.t('This workspace has no selector “{title}”', { title: chip.title }), status: 'warning', timeout: 5000 })
                return false
            }
            params.WHERE = res.WHERE
            delete params.calc_selectors_mark   // метка расчётных селекторов — пересчитает бэк на applySelector
            try { this.store.update_CHIPS_from_WHERE?.(params) } catch (e) { /* чипсы шапки — best effort */ }
            if (W) W.selector_version = (W.selector_version ?? 0) + 1
            return done()
        },
        chipPick_scorer(sets) {
            const out = []
            const best = { tier: Infinity, si: Infinity }
            const noTerms = !sets?.[0]?.terms?.length
            return {
                // IE 2026 09 25 / ODOO-341407 (этап 2 переводов данных 1С, поиск по всем языкам): alt — названия значения на других языках (titles_alt строки
                // справочника, бэк get_itemsSource); совпадение по ним — та же ступень, что по показанному названию; вариант, где нашлось, — в
                // пункте списка (altHit) подписью справа. Показанное название при равной ступени главнее
                consider: (id, title, alt) => {
                    if (noTerms) { if (out.length < 15) out.push({ id, title }); return }
                    let bt = Infinity, bs = Infinity, bAlt = ''
                    for (let si = 0; si < sets.length; si++) {
                        const S = sets[si]
                        if (!S.terms.length) continue
                        let r = matchTermsBest(title, S.prep || S.terms, FUZZY_TIERS)
                        if (!r && matchTerms(String(id), S.terms, false)) r = { tier: 0 }
                        let altHit = ''
                        if (Array.isArray(alt) && alt.length && (!r || r.tier > 0)) {
                            for (const v of alt) {
                                const ra = v ? matchTermsBest(String(v), S.prep || S.terms, FUZZY_TIERS) : null
                                if (ra && (!r || ra.tier < r.tier)) { r = { tier: ra.tier }; altHit = String(v); if (ra.tier === 0) break }
                            }
                        }
                        if (r && r.tier < bt) { bt = r.tier; bs = si; bAlt = altHit; if (bt === 0) break }
                    }
                    if (bt === Infinity) return
                    if (bt < best.tier || (bt === best.tier && bs < best.si)) { best.tier = bt; best.si = bs; out.length = 0 }
                    if (bt === best.tier && bs === best.si && out.length < 15) out.push(bAlt ? { id, title, altHit: bAlt } : { id, title })
                },
                full: () => noTerms ? out.length >= 15 : (best.tier === 0 && best.si === 0 && out.length >= 15),
                result: () => ({ out: out.slice(), tier: best.tier === Infinity ? 0 : best.tier, conv: (best.si > 0 && best.si !== Infinity) ? sets[best.si].conv : '' }),
            }
        },
        // кандидаты подбора { id, title }: чипс виджета-селектора — ТОЛЬКО строки, видимые сейчас в виджете (его отборы/поиск/порядок —
        // «как если открыть и увидеть»; общий словарь предлагал значения, которые селектор применить не может — просьба Igor 2026-08-07;
        // опция chipPickFromWidget, дефолт ВКЛ: false — весь словарь сегмента, window.fastEntry.set('chipPickFromWidget', false));
        // иначе — словарь сегмента (store.itemsSource)
        *chipPick_candidates(chip) {
            const seg = this.chipPick_segment(chip)
            if (!seg) return
            const raw = chip?._raw || chip
            const IS = this.store.itemsSource[seg]
            const src = this.chipPick_isSelectorForm(raw) && this.store.fastEntry.chipPickFromWidget !== false
                ? this.chipPick_widgetSource(raw) : null
            if (!src && this.chipPick_widgetLoading(chip)) return   // IE 2026 09 22 / ODOO-5800: виджет-селектор ещё грузится — словарь не показываем
            if (src) {
                const seen = new Set()
                const rowItem = (r) => {
                    const v = this.store.get_value(r, src.fs.binding)
                    if (v == null || v === '' || seen.has(String(v))) return null
                    seen.add(String(v))
                    const title = IS?.ids?.[v]?.title
                        || this.store.get_value(r, `${src.fs.binding}.title`)
                        || String(r.title ?? v)
                    if (!IS?.ids?.[v]) this.chipPick_needTitle(seg, v)   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: GUID в меню чипса): значения строк виджета-селектора, которых нет в словаре сегмента (за потолком 'all'), — спросить by_ids
                    return { id: v, title: String(title), alt: IS?.ids?.[v]?.titles_alt }   // IE 2026 09 25 / ODOO-341407: названия на других языках (titles_alt словаря)
                }
                // живой грид виджета: видимые строки в порядке отображения
                const gridV = src.selW.vueObj?.get_vueObj?.('_doc.data')
                if (gridV?.flex?.rows?.length) {
                    for (const row of gridV.flex.rows) {
                        if (!row?.dataItem || row.visible === false) continue
                        if (row.dataItem._fe?.visible === false) continue
                        const it = rowItem(row.dataItem)
                        if (it) yield it
                    }
                    return
                }
                // грид не смонтирован / без привязки данных (скрытый виджет: хост шириной 0, itemsSource не ставится) — по данным виджета
                // (уже с его отборами) в порядке его чипсов Order — как в видимом гриде (IE 2026 09 22 / ODOO-5800)
                const order = this.chipPick_order(src)
                const stack = this.chipPick_sortRows(src.data || [], order, src.fs.binding, IS).reverse()
                while (stack.length) {
                    const r = stack.pop()
                    const it = rowItem(r)
                    if (it) yield it
                    if (Array.isArray(r?.attrs) && r.attrs.length) { const kids = this.chipPick_sortRows(r.attrs, order, src.fs.binding, IS); for (let i = kids.length - 1; i >= 0; i--) stack.push(kids[i]) }
                }
                return
            }
            const data = IS?.data || (IS?.ids ? Object.values(IS.ids) : [])
            for (const r of data) {
                const id = r?.id ?? r?.value
                if (id == null) continue
                yield { id, title: String(r.title ?? r.valueStr ?? id), alt: r.titles_alt }   // IE 2026 09 25 / ODOO-341407: + названия на других языках
            }
        },
        // IE 2026 09 22 / ODOO-5800 (проба скрытого виджета: список шёл в порядке ответа бэка — «Украина» раньше «Литвы», а видимый грид сортирует по чипсу
        // Order «country»): порядок строк виджета без привязанного грида — по его чипсам Order (AttrGrid.set_itemsSource берёт те же getChipOrder());
        // ключ сравнения — как sortConverter грида: поле <binding>_title → титул словаря → число из строки → сырое значение
        chipPick_order(src) {
            try { return src.selW.vueObj?.get_vueObj?.('_doc.data')?.$refs?.chips?.getChipOrder?.() || [] } catch (e) { return [] }
        },
        chipPick_sortRows(rows, order, binding, IS) {
            const out = [...(rows || [])]
            if (!order.length || out.length < 2) return out
            const key = (r, prop) => {
                const t = r?.[`${prop}_title`]
                if (t !== null && t !== undefined && t !== '') return String(t)
                const v = this.store.get_value(r, prop)
                if (v === null || v === undefined || v === '') return null
                if (prop === binding && IS?.ids?.[v]?.title) return String(IS.ids[v].title)
                const vt = this.store.get_value(r, `${prop}.title`)
                if (vt !== null && vt !== undefined && vt !== '' && typeof vt !== 'object') return String(vt)
                if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v)
                return typeof v === 'object' ? String(v.title ?? v.value ?? '') : v
            }
            const cmp = (a, b) => {
                if (a === null && b === null) return 0
                if (a === null) return 1
                if (b === null) return -1
                if (typeof a === 'number' && typeof b === 'number') return a - b
                const sa = String(a).toLowerCase(), sb = String(b).toLowerCase()   // как wijmo CollectionView: строки без учёта регистра, по кодам
                return sa < sb ? -1 : sa > sb ? 1 : 0
            }
            const keys = new Map(out.map(r => [r, order.map(sd => key(r, sd.property))]))
            out.sort((x, y) => { const kx = keys.get(x), ky = keys.get(y); for (let i = 0; i < order.length; i++) { const c = cmp(kx[i], ky[i]); if (c) return order[i].ascending === false ? -c : c } return 0 })
            return out
        },
        chipPick_titleHTML(it) {
            // подсветка совпадения, как в поисковых списках
            const t = this.store.truncateText(it.title, 60)
            const q = (this.chipPickConv || this.chipPickQuery || '').trim()   // IE 2026 09 10 / ODOO-…: подсветка по сконвертированному тексту
            const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            if (!q) return esc(t)
            // IE 2026 09 25 / ODOO-341407 (поиск по всем языкам): нашлось по названию на другом языке — показанное название и серым тот вариант с подсветкой
            if (it.altHit) {
                const a = this.store.truncateText(it.altHit, 60)   // IE 2026 09 25 / ODOO-341407: вариант на другом языке — второй строкой пункта (в строку не помещался)
                const ar = this.chipPick_approx(matchTerms(a, searchTerms(q), this.chipPickFuzzy))   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе)
                const aHTML = ar ? highlightRanges(a, ar, { tag: 'b', cls: 'chip-pick-hit', fuzzyCls: 'chip-pick-fuzzy', escape: true }) : esc(a)
                return esc(t) + '<span class="chip-pick-alt">' + aHTML + '</span>'
            }
            // IE 2026 09 12 / ODOO-…: подсветка каждой части; приближённые (опечатки) — классом chip-pick-fuzzy
            const ranges = this.chipPick_approx(matchTerms(t, searchTerms(q), this.chipPickFuzzy))   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): на последней ступени всё найденное — приближённое
            if (!ranges) return esc(t)
            return highlightRanges(t, ranges, { tag: 'b', cls: 'chip-pick-hit', fuzzyCls: 'chip-pick-fuzzy', escape: true })
        },
        chipPick_fuzzyText(tier) {   // IE 2026 09 12 / ODOO-…: подпись ступени опечаток
            if (this.chipPick_isExtra(tier)) return extraTierText(tier, this.chipPickInner)   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): шаг последней ступени и ступень опечаток внутри него
            return FUZZY_TIER_TEXT[tier] || this.t('typos allowed')   // IE 2026 09 23 / ODOO-341407: i18n (как в MenuPanel)
        },
        // IE 2026 09 25 / ODOO-… (замечание Игоря: поиск в таблице по названиям на других языках): чипс поиска таблицы в режиме «на другом языке» (решение грида — AttrGrid.searchAlt_decide: на показанных названиях ничего,
        // по названиям записей ссылочных ячеек на других языках — есть; _alt = язык совпавших названий en | uk | ru | other, _altFor = текст): значок и подпись
        chipAltOn(chip) {
            const raw = chip?._raw || chip
            return !!raw && raw.segment === 'CHIP-Search' && !!raw._alt && raw._altFor === String(raw.title || '').trim() && !this.chipPlace_input(chip)
        },
        chipAltBadge(chip) { const l = gridSearchAlt_shownLang((chip?._raw || chip)?._alt); return (l === 'en' || l === 'uk') ? l.toUpperCase() : '' },   // IE 2026 09 28 / ODOO-341407: без значка RU — только значок языка
        chipAltLabel(chip) { return gridSearchAlt_label((chip?._raw || chip)?._alt) },
        chipAltTitle(chip) { return gridSearchAlt_hint((chip?._raw || chip)?._alt) },
        chipPhraseOn(chip) {   // IE 2026 09 21 / ODOO-… (замечание Игоря: фраза поиска целиком главнее частей)
            const raw = chip?._raw || chip
            return !!raw && raw.segment === 'CHIP-Search' && raw._phrase === true && raw._phraseFor === String(raw.title || '').trim()
        },
        chipFuzzyTitle(chip) {   // IE 2026 09 12 / ODOO-…: подсказка знака ≈ на чипсе поиска таблицы
            const t = (chip?._raw || chip)?._fuzzy
            const ex = this.chipExtraOn(chip)   // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): чипс найден последней ступенью — что набрано и что не учтено
            if (ex) {
                const typed = String((chip?._raw || chip)?._extraFrom || ''), what = extraTierText(ex, t || 0)
                return typed ? this.t('Nothing matches “{typed}” as typed — {what}', { typed, what }) : this.t('Nothing matches the text as typed — {what}', { what })
            }
            return this.t('Nothing matches exactly — rows where {where} (typos allowed)', { where: FUZZY_TIER_TEXT[t] || this.t('one or two letters differ') })   // IE 2026 09 23 / ODOO-341407: i18n (тексты ступеней — utils/searchMatch)
        },
        chipPick_isSelectorForm(raw) {
            // селекторный чипс: значения живут СПИСКОМ в attrs (как их пишет
            // onApplySelector__Click из виджета-селектора), rightValue — плейсхолдер
            return !!raw?.selector_binding
                || /selector/.test(String(raw?.condition || ''))
                || (!!raw?.attrs?.length && !raw?.rightValue?.value)
                || (this.chipPickKeeps_on() && this.mode === 'Page' && chipRow_isWidgetSelector(raw))   // IE 2026 10 13 / ODOO-5800: строка стола с виджетом-селектором и «in» — селекторная и со значением в rightValue
        },
        chipPickKeeps_on() { return chipPickKeepsSelector_on((k, d) => this.store?.setting?.(k, d)) },   // IE 2026 10 13 / ODOO-5800
        // селекторная строка «in» со значением в rightValue (снимок «в»-строки): значение — плейсхолдер, отбор ставится списком attrs
        chipPick_clearRv(raw) { if (this.chipPickKeeps_on() && raw?.rightValue?.value && /^!?in$/.test(String(raw.condition || ''))) raw.rightValue = { ...raw.rightValue, value: '', valueStr: '' } },   // IE 2026 10 13 / ODOO-5800
        chipSelector_applyViaWidget(raw, values) {
            // ЭТАЛОННЫЙ путь установки селектора: пометить строки в
            // виджете-селекторе (row.selector) и применить его же командой —
            // бэкенд пересчитает множества и сработают все зависимые действия,
            // ровно как при клике по чекбоксу. Возвращает false, если путь
            // невозможен (виджет не смонтирован / значений нет в его данных)
            const store = this.store
            const ws = store.activeWORKSPACE
            // TEMP [chip-pick] diagnostics (remove after Igor confirms dev02) —
            // English console output per project rule
            const diag = (reason, extra = {}) => debugLog('%c[chip-pick] widget-path: ' + reason,
                'color:#7b1fa2', { chip_widget: raw?.widget || null, ...extra })
            if (!ws || !raw?.widget) { diag('SKIP: chip has no widget ref'); return false }
            const selW = (ws.ALL_WIDGETS || []).find(w => w.widget === raw.widget || w.record?.id === raw.widget)
            const fieldSelectors = selW?.record?.doc?.params?.fieldSelectors
            const data = selW?.record?._doc?.data
            if (!selW?.vueObj || !fieldSelectors?.length || !data?.length) {
                diag('SKIP: widget not usable', {
                    widget_found: !!selW, mounted: !!selW?.vueObj,
                    fieldSelectors: (fieldSelectors || []).map(f => f.binding),
                    data_rows: data?.length ?? null,
                    page_widgets: (ws.ALL_WIDGETS || []).map(w => `${w.id}:${w.widget || ''}`),
                })
                return false
            }
            const binding = raw.selector_binding || raw.leftValue?.value
            const fs = fieldSelectors.find(f => f.binding === binding) || fieldSelectors[0]
            const want = new Set(values.filter(v => v != null).map(String))
            if (!want.size) { diag('SKIP: no values'); return false }
            // 1-й проход: значения вообще есть в данных виджета?
            let found = 0
            const foundV = new Set()   // IE 2026 09 30 / ODOO-5800: какие именно значения нашлись
            const scan = (rows) => {
                for (const r of rows || []) {
                    const v = store.get_value(r, fs.binding)
                    if (v != null && want.has(String(v))) { found++; foundV.add(String(v)) }   // IE 2026 09 30 / ODOO-5800: + foundV
                    if (Array.isArray(r.attrs) && r.attrs.length) scan(r.attrs)
                }
            }
            scan(data)
            if (!found) {
                diag('SKIP: values not present in widget data', { fs_binding: fs.binding, values: [...want], data_rows: data.length })
                return false
            }
            // IE 2026 09 30 / ODOO-5800: часть значений вне строк виджета (добавлена из расширенного поиска по подсегменту, из Recent) — флажки виджета их не
            // передадут, путь виджета молча потерял бы их; значения пишутся в attrs чипса (фолбэк вызывающего). Выключатель fe.ux.chipPickSubsegmentFallback
            if (foundV.size < want.size && this.chipPick_wideOn()) {
                diag('SKIP: some values are not in widget data', { fs_binding: fs.binding, values: [...want], found: [...foundV] })
                return false
            }
            diag('APPLY via selector widget', { widget_id: selW.id, fs_binding: fs.binding, values: [...want], matched_rows: found })
            // 2-й проход: расставить флажки
            const mark = (rows) => {
                for (const r of rows || []) {
                    const v = store.get_value(r, fs.binding)
                    const hit = v != null && want.has(String(v))
                    if (r.selector !== hit) r.selector = hit
                    if (Array.isArray(r.attrs) && r.attrs.length) mark(r.attrs)
                }
            }
            mark(data)
            try { selW.vueObj.get_flex?.('_doc.data')?.refresh?.() } catch (e) { /* виджет мог быть скрыт */ }
            store.executeStoreMethod(selW.vueObj, { command: 'onApplySelector__Click', binding: '_doc.data' }, null)
            return true
        },
        applyChipPick(index, chip, it) {
            const raw = chip?._raw || chip
            const seg = this.chipPick_segment(chip)
            if (!raw || !seg) return
            if (this.chipPick_isSelectorForm(raw)) {
                this.chipPickText = ''
                this.chipPickFocused = false
                this.hoverIndex = null
                // TEMP [chip-pick]: снимок строки чипса ДО применения (диагностика dev02)
                try { debugLog('%c[chip-pick] selector chip row BEFORE:', 'color:#7b1fa2', JSON.stringify(raw).slice(0, 1500)) } catch (e) { /* circular row is fine to skip */ }
                // эталон: через виджет-селектор (он сам применит и пересчитает);
                // плюс стандартное событие применения чипсов — см. applyChipHistory
                this.chipPick_clearRv(raw)   // IE 2026 10 13 / ODOO-5800
                if (this.chipSelector_applyViaWidget(raw, [it.id])) { this.emitChips(); return }
                // фолбэк (виджет не смонтирован / значения нет в его данных):
                // форма onApplySelector__Click — values в attrs, rightValue/condition
                // не трогаем; метка селекторов снимается, переоткрытие пересеет
                // флажки виджета по чипсам (механика открытия по ссылке where=)
                debugLog('%c[chip-pick] FALLBACK: writing value into chip attrs + drop calc_selectors_mark', 'color:#7b1fa2', { value: it.id })
                raw.attrs = [{
                    use: true,
                    rightValue: { value: it.id, valueStr: it.title, attr_type: 'AttrLink', relation_segment: seg },
                }]
                raw.use = true
                const params = this.store.activeWORKSPACE?.record?.doc?.params
                if (params) delete params.calc_selectors_mark
                this.emitChips()
                return
            }
            // одно выбранное значение: список значений (attrs) заменяется целиком
            if (this.chipPickKeeps_on()) chipRow_setSingle(raw, it.id, it.title, seg)   // IE 2026 10 13 / ODOO-5800: у строки «in» — в attrs (rightValue «in» бэк не читает)
            else { raw.rightValue = { value: it.id, valueStr: it.title, attr_type: 'AttrLink', relation_segment: seg }; raw.attrs = [] }   // IE 2026 10 13 / ODOO-5800
            if (!raw.condition || ['and', 'or'].includes(raw.condition)) raw.condition = '='
            raw.use = true
            this.chipPickText = ''
            this.chipPickFocused = false
            this.hoverIndex = null
            this.emitChips()
        },
        chipPick_applyFirst(index, chip) {
            clearTimeout(this._chipPickTimer); this.chipPickQuery = this.chipPickText   // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): Enter — без ожидания паузы
            const items = this.chipPick_filterSync(chip).out   // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): полный проход синхронно
            if (items.length) this.applyChipPick(index, chip, items[0])
        },
        getTooltipHTML(mappedChip, opts) {
            if (this.formatChip_is(mappedChip)) return ''   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): своя форма
            if (this.metricChip_is(mappedChip)) return ''   // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): форма чипса метрик — свой блок шаблона
            // не разворачиваем _raw: в Page-режиме это голая строка WHERE без segment/doc,
            // расшифровка строится по замапленному чипу (segment + doc.WHERE)
            return this._resolveTooltip(mappedChip, opts)
        },
        // IE 2026 09 09 / ODOO-5800: снимок значений чипса для строк с чекбоксами (только плоский список attrs)
        _buildChipValueSnap(index) {
            const chip = this.visibleChips?.[index]
            const raw = chip?._raw || chip
            const attrs = raw?.attrs
            if (!Array.isArray(attrs) || attrs.length < 2) return null
            if (!attrs.every(a => a && a.use !== false && a.rightValue && !a.attrs?.length)) return null
            const IS = this.store.itemsSource
            const items = attrs.map((a, i) => {
                const V = a.rightValue || {}
                const title = (V.attr_type === 'AttrLink' && V.relation_segment && V.value && IS[V.relation_segment]?.ids?.[V.value]?.title)
                    || V.valueStr || V.value || ''
                return { key: `${String(V.value ?? '')}-${i}`, id: V.value, title: String(title), attr: a }
            })
            return { index, items }
        },
        // IE 2026 09 15 / ODOO-… (заказ Игоря): чипс периода — строка стола condition 'between' по date (mainStore.periodSel_*)
        chipPeriod(chip) {
            const raw = chip?._raw || chip
            if (!raw || raw.condition !== 'between' || (raw.leftValue?.value ?? raw.leftValue) !== 'date') return null
            const rv = raw.rightValue || {}
            return { from: rv.value || '', to: rv.valueTo || '' }
        },
        chipPeriod_change(chip, which, value) {
            const p = this.chipPeriod(chip); if (!p) return
            const from = which === 'from' ? value : p.from, to = which === 'to' ? value : p.to
            this.store.periodSel_set(null, from, to, this.store.activeWORKSPACE?.workspace)
        },
        chipValueRows(chip, index) {
            const snap = this.chipValueSnap
            if (!snap || snap.index !== index) return []
            // флажок — по ТЕКУЩЕМУ отбору чипса (реактивно), список — снимок на время открытого поповера:
            // иначе отбор, применённый асинхронно (путь через виджет-селектор, Recent), расходился с флажками
            const cur = this.chipPick_currentValues(chip)
            return snap.items.map(it => ({ ...it, on: cur.has(String(it.id)) }))
        },
        toggleChipValue(index, chip, v, ev) {
            const snap = this.chipValueSnap
            if (!snap || snap.index !== index) return
            if (ev?.altKey) { this.chipValues_apply(index, chip, [v]); return }
            const raw = chip?._raw || chip
            const cur = this._chipValues_list(raw)
            const has = cur.some(x => String(x.id) === String(v.id))
            const items = has ? cur.filter(x => String(x.id) !== String(v.id)) : [...cur, { id: v.id, title: v.title, attr: v.attr }]
            this.chipValues_apply(index, chip, items)
        },
        onChipValueTitleClick(index, chip, v, ev) {
            // название значения — оставить выбранным только его (как в подборе и виджетах-селекторах)
            this.chipValues_apply(index, chip, [v])
        },
        // IE 2026 09 09 / ODOO-5800: текущий набор значений чипса как список {id, title, attr}
        _chipValues_list(raw) {
            const out = []
            if (Array.isArray(raw?.attrs) && raw.attrs.length) {
                for (const a of this._flatWhereAttrs(raw.attrs)) {
                    const V = a.rightValue || {}
                    if (V.value == null || V.value === '') continue
                    out.push({ id: V.value, title: V.valueStr || String(V.value), attr: a })
                }
            } else if (raw?.rightValue?.value != null && raw.rightValue.value !== '') {
                const V = raw.rightValue
                out.push({ id: V.value, title: V.valueStr || String(V.value), attr: null })
            }
            return out
        },
        _chipValues_attr(v, seg) {
            return v.attr || { use: true, rightValue: { value: v.id, valueStr: v.title, attr_type: 'AttrLink', relation_segment: seg } }
        },
        // снимок верхнего списка (чекбоксы значений) — синхронизировать с новым набором, не удаляя строк
        _chipValueSnap_sync(index, items) {
            let snap = this.chipValueSnap
            if ((!snap || snap.index !== index) && this.hoverIndex === index && items.length > 1) {
                // IE 2026 09 09 / ODOO-5800: поповер открыт при одном значении, добавили ещё — верхний список появляется сразу
                snap = this.chipValueSnap = { index, items: [] }
            }
            if (!snap || snap.index !== index) return
            for (const x of items) {
                if (!snap.items.some(it => String(it.id) === String(x.id))) {
                    snap.items.push({ key: `${String(x.id)}-${snap.items.length}`, id: x.id, title: x.title, attr: x.attr })
                }
            }
        },
        // применить набор значений чипса (замена набора): селекторный чипс — через виджет-селектор, иначе attrs/rightValue;
        // пустой набор = снять чипс (как ⊗); одно значение у чипса одиночной формы остаётся в rightValue
        chipValues_apply(index, chip, items) {
            const raw = chip?._raw || chip
            if (!raw) return
            if (!items.length) { this.removeChip(index); return }
            const seg = this.chipPick_segment(chip) || ''
            if (this.chipPick_isSelectorForm(raw)) {
                this.chipPick_clearRv(raw)   // IE 2026 10 13 / ODOO-5800
                if (this.chipSelector_applyViaWidget(raw, items.map(x => x.id))) { this._chipValueSnap_sync(index, items); this.emitChips(); return }
                raw.attrs = items.map(x => this._chipValues_attr(x, seg))
                raw.use = true
                const params = this.store.activeWORKSPACE?.record?.doc?.params
                if (params) delete params.calc_selectors_mark
                this._chipValueSnap_sync(index, items)
                this.emitChips()
                return
            }
            const listForm = (Array.isArray(raw.attrs) && raw.attrs.length > 0) || items.length > 1
            if (listForm) {
                raw.attrs = items.map(x => this._chipValues_attr(x, seg))
                raw.rightValue = { ...(raw.rightValue || {}), value: '', valueStr: '', attr_type: 'AttrLink', relation_segment: seg }
                if (!['in', 'and', 'or'].includes(raw.condition)) raw.condition = 'in'
            } else {
                raw.rightValue = { ...(raw.rightValue || {}), value: items[0].id, valueStr: items[0].title, attr_type: 'AttrLink', relation_segment: seg }
                raw.attrs = []
                if (!raw.condition || ['and', 'or', 'in'].includes(raw.condition)) raw.condition = '='
            }
            raw.use = true
            this._chipValueSnap_sync(index, items)
            this.emitChips()
        },
        // подбор: тайтл (и Alt+щелчок) — только это значение; чекбокс — добавить/убрать к текущему отбору, поповер остаётся
        onChipPickTitleClick(index, chip, it, ev) {
            this.applyChipPick(index, chip, it)
        },
        onChipPickCheckClick(index, chip, it, ev) {
            if (ev?.altKey) { this.applyChipPick(index, chip, it); return }
            const raw = chip?._raw || chip
            const cur = this._chipValues_list(raw)
            const has = cur.some(x => String(x.id) === String(it.id))
            const items = has ? cur.filter(x => String(x.id) !== String(it.id)) : [...cur, { id: it.id, title: it.title, attr: null }]
            this.chipValues_apply(index, chip, items)
        },
        // история: тайтл (и Alt+щелчок) — применить запись целиком; чекбокс — добавить её значения к текущему отбору
        onChipHistoryTitleClick(index, h, ev) {
            this.applyChipHistory(index, h)
        },
        onChipHistoryCheckClick(index, chip, h, ev) {
            if (ev?.altKey) { this.applyChipHistory(index, h); return }
            const raw = chip?._raw || chip
            const cur = this._chipValues_list(raw)
            const seen = new Set(cur.map(x => String(x.id)))
            const add = []
            const push = (V) => {
                if (!V || V.value == null || V.value === '' || seen.has(String(V.value))) return
                seen.add(String(V.value))
                const title = (V.relation_segment && this.store.itemsSource[V.relation_segment]?.ids?.[V.value]?.title) || V.valueStr || String(V.value)
                add.push({ id: V.value, title, attr: null })
            }
            push(h.rightValue)
            for (const a of h.attrs || []) push(a?.rightValue)
            if (!add.length) return
            this.chipValues_apply(index, chip, [...cur, ...add])
        },
        _resolveTooltip(chip, opts = {}) {
            if (!chip) return ''
            if (chip.segment === 'CHIP-Where' || chip.segment === 'CHIP-Selector') {
                const resolveTitle = (V) => {
                    if (V?.attr_type === 'AttrLink' && V.relation_segment && V.value) {
                        return this.store.itemsSource[V.relation_segment]?.ids?.[V.value]?.title || V.valueStr || V.value || ''
                    }
                    return V?.valueStr || V?.value || ''
                }
                const renderWhereRow = (row) => {
                    const l = this.chipRow_isPeriod(row) ? this.t('Period') : resolveTitle(row.leftValue)   // IE 2026 09 23 / ODOO-341407: i18n — заголовок окна чипса периода
                    const c = row.condition || ''
                    if (['and', 'or'].includes(c) && row.attrs?.length) {
                        const usedAttrs = row.attrs
                        const subRows = usedAttrs.map(r => {
                            const sl = r.leftValue?.raw_value || resolveTitle(r.leftValue)
                            const sc = r.condition || ''
                            const sr = resolveTitle(r.rightValue)
                            return `<div class="chip-tooltip-row">${sl} <span class="chip-tooltip-condition">${this.conditionLabel(sc)}</span> ${sr}</div>`   // IE 2026 09 23 / ODOO-341407: i18n — подпись оператора
                        }).join('')
                        return `<div class="chip-tooltip-header">${l} <span class="chip-tooltip-condition">${c.toUpperCase()}</span> <span class="chip-tooltip-count">(${usedAttrs.length})</span></div>${subRows}`
                    }
                    if (row.attrs?.length) {
                        const values = this._flatWhereAttrs(row.attrs)
                            .map(r => r.rightValue?.valueStr || resolveTitle(r.rightValue)).filter(Boolean)
                        const subRows = opts.headerOnly ? '' : values.map(v => `<div class="chip-tooltip-row">${v}</div>`).join('')   // IE 2026 09 09 / ODOO-5800: строки значений рендерит шаблон (чекбоксы)
                        return `<div class="chip-tooltip-header">${l} <span class="chip-tooltip-condition">${this.conditionLabel(c)}</span> <span class="chip-tooltip-count">(${values.length})</span></div>${subRows}`   // IE 2026 09 23 / ODOO-341407: i18n
                    }
                    const r = resolveTitle(row.rightValue)
                    return r
                        ? `<div class="chip-tooltip-row">${l} <span class="chip-tooltip-condition">${this.conditionLabel(c)}</span> ${r}</div>`   // IE 2026 09 23 / ODOO-341407: i18n — «Період між 2026-09-01»
                        : (l ? `<div class="chip-tooltip-row">${l}</div>` : '')
                }

                const WHERE = chip.doc.WHERE
                if (WHERE?.length) {
                    return WHERE.map(renderWhereRow).join('')
                }
                // fallback: old flat structure
                return renderWhereRow(chip.doc)
            }
            if (chip.segment === 'CHIP-Info') {
                // тексты приходят из записей (титул метрики, название чипса) —
                // экранируем: «Stock < 0» иначе съедает остаток подсказки
                const esc = (v) => this.store.escapeHtml(String(v ?? ''))
                const _lk = approvalLinksOn((k, d) => this.store.setting(k, d))   // IE 2026 10 10 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE1-krows FE1 (A-03): Segment / Version — название ссылкой на запись (chip.links бэка)
                const rows = (chip.doc?.tooltip || []).map(([label, value]) => {
                    const ln = _lk ? approvalChipLink(chip.doc, label) : null
                    const v = ln ? `<a class="appr-chip-link" data-appr-seg="${esc(ln.segment)}" data-appr-id="${esc(ln.id)}" title="${esc(ln.id)}">${esc(ln.title || value)}</a>` : esc(value)
                    return `<div class="chip-tooltip-row">${esc(approvalChipLabel(chip.doc, label))} <span class="chip-tooltip-condition">:</span> ${v}</div>`
                }   // IE 2026 10 09 / ODOO-345618 (F2): подписи подсказки чипса согласования — переводятся
                ).join('')
                return `<div class="chip-tooltip-header">${esc(approvalChipTitle(chip.doc))}</div>${rows}`
            }
            if (chip.segment === 'CHIP-Param') {
                const title = chip.doc.attr?.title || ''
                const valueStr = chip.doc.attr?.valueStr || ''
                if (valueStr) {
                    return `<div class="chip-tooltip-header">${title}</div><div class="chip-tooltip-row">${valueStr}</div>`
                }
            }
            if (chip.segment === 'CHIP-Node') {
                const valueStr = chip.doc.node?.valueStr || ''
                if (valueStr) {
                    return `<div class="chip-tooltip-header">${valueStr}</div>`
                }
            }
            if (chip.segment === 'CHIP-Widget') {
                const valueStr = chip.doc.widget?.title || ''
                if (valueStr) return `<div class="chip-tooltip-header">${valueStr}</div>`
            }
            if (chip.segment === 'CHIP-Record') {
                const title = chip.doc.record?.title || ''
                return `<div class="chip-tooltip-header">${title}</div>`
            }
            if (chip.segment === 'CHIP-Rows' || chip.segment === 'CHIP-Cell' || chip.segment === 'CHIP-CellFilters') {
                const cells = chip.doc.cells || chip.doc.meta?.source?.cells || []
                if (!cells.length) return ''
                const rows = cells.map(c =>
                    `<div class="chip-tooltip-row">${c.title || c.binding}: ${c.value}</div>`
                ).join('')
                return `<div class="chip-tooltip-header">${this.tn(cells.length, '{n} cell', '{n} cells')}</div>${rows}`   // IE 2026 09 23 / ODOO-341407: i18n
            }
            // у чипса с юзерфрендли-заголовком (meta.titleHTML) в tooltip показываем реальное условие
            if (chip.meta?.titleHTML && chip.title) {
                return `<div class="chip-tooltip-row">${this.store.escapeHtml(chip.title)}</div>`
            }
            return ''
        },
        _realIndex(visibleIndex) {
            const chip = this.visibleChips[visibleIndex]
            return chip?._index != null ? chip._index : visibleIndex
        },
        pmTitle(id) {   // IE 2026 09 23 / ODOO-341407: i18n
            if (id === 'current-pm') return this.t('Current process model')
            if (id === 'previous-pm') return this.t('Previous process model')
            const item = (this.store.process_model_menu || []).find(x => x.process_model === id)
            return item?.title || id
        },
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): строки меню модели (kind 'pm' — CHIP-ProcessModel, 'cmp' — выбор модели сравнения в CHIP-CompareModel)
        pmChipRows(kind) {
            const params = this.WIDGET?.record?.doc?.params || {}
            const lang = getLanguage() || 'en'
            const o = {
                t: this.t, fmtDate: (s) => pmMenu_fmtDate(s, lang),
                archiveSubmenu: this.store.setting('fe.ux.pmArchiveSubmenu', true) !== false,
                archOpen: this.pmArchOpen[kind],
            }
            if (kind !== 'cmp') {
                return pmMenu_chipRows(this.store.process_model_menu, { ...o,
                    head: [{ id: 'current-pm', title: this.t('Current process model') }, { id: 'previous-pm', title: this.t('Previous process model') }],
                    activeId: params.process_model_override || '' })
            }
            const pick = this.WIDGET?._cmpPmPick || null
            const cmpInfo = this.WIDGET?.record?.doc?._compare_info || null
            // «From settings»: справа — во что разрешилась настройка (до выбора — модель последнего сравнения бэка; после — прежнее значение отчёта, пусто — каскад стола / ПМ)
            const setTitle = pick ? (pick.has && pick.v ? this.pmTitle(pick.v) : '') : (cmpInfo?.prev_title || (cmpInfo?.prev_process_model ? this.pmTitle(cmpInfo.prev_process_model) : (params.prev_process_model ? this.pmTitle(params.prev_process_model) : '')))
            const openId = this.store.process_model_record?.id || ''
            return pmMenu_chipRows(this.store.process_model_menu, { ...o,
                head: [{ id: '', title: this.t('From settings'), aside: setTitle, icon: 'pi-cog' }, { id: 'previous-pm', title: this.t('Previous process model') }],
                exclude: [openId, 'current-pm', 'previous-pm'],
                activeId: pick ? String(params.prev_process_model || '') : '' })
        },
        pmChipRow_click(kind, r) {
            if (!r) return
            if (r.type === 'folder') { this.pmArchOpen = { ...this.pmArchOpen, [kind]: !r.open }; return }
            if (kind === 'cmp') this.selectCmpModel(r.id)
            else this.selectPmChip(r.id)
        },
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): выбор модели сравнения в чипсе «Δ vs model» — правка параметров отчёта (utils/pmMenuGroups.pmMenu_cmpApply;
        // состояние до первого выбора — WIDGET._cmpPmPick, в памяти виджета), перечит — как у чипса модели (_reload_widget); запись стола / настроек не сохраняется
        selectCmpModel(id) {
            const W = this.WIDGET, rec = W?.record, params = rec?.doc?.params
            if (!params) return
            rec._meta = rec._meta || {}
            const r = pmMenu_cmpApply(params, rec._meta, W._cmpPmPick || null, id)
            if (r.pick) W._cmpPmPick = r.pick
            else delete W._cmpPmPick
            this.hoverIndex = null
            if (!r.changed) return
            try { window._cmpModelPicker = { picks: (window._cmpModelPicker?.picks || 0) + 1, last: id } } catch (e) { /* счётчик пробы */ }
            this._reload_widget()
        },
        selectPmChip(process_model) {
            const rec = this.WIDGET?.record
            const params = rec?.doc?.params
            if (!params) return
            params.process_model_override = process_model
            rec._meta = rec._meta || {}
            rec._meta.modified = true
            this._reload_widget()
            this.hoverIndex = null
        },
        removeChip(index) {
            const chip = this.visibleChips[index]
            if (!chip) return
            if (chip.doc?.static) return   // IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): статичный чипс («Regimen paused until …») не снимается ни ⊗, ни Ctrl+X
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): ⊗ у чипса метрик шапки — строка стола выключается на фронте, как у прочих чипсов стола (отбор остаётся в строке:
            // чипс возвращается флажком в списке ⌄), и селекторы применяются заново; черновик просто убирается
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): ⊗ на зеркале над таблицей прячет его до следующей смены отбора в шапке (чипс стола не трогается)
            if (chip._mirror) { chip.dismissedSig = chip.sig; this.hoverIndex = null; this.emitChips(); return }
            if (chip._global) {
                this.hoverIndex = null
                this.metricChipG_localDrop()
                if (chip._draft || !chip._raw) return
                chip._raw.use = false
                this.resolveIndex++
                this.emitChips()
                return
            }
            if (chip.segment === 'CHIP-WidgetsPage') return   // IE 2026 09 15 / ODOO-…: чипс пейджинга не снимается
            if (chip.segment === 'CHIP-ProcessModel' || chip.segment === 'CHIP-CompareModel') {
                // удаление PM-чипа = ручная очистка настройки: параметр чистится,
                // форма помечается изменённой, отчёт перечитывается
                const rec = this.WIDGET?.record
                const params = rec?.doc?.params
                if (params) {
                    if (chip.segment === 'CHIP-ProcessModel') {
                        params.process_model_override = ''
                        rec._meta = rec._meta || {}
                        rec._meta.modified = true
                    } else if (params.compare_with_prev) {
                        params.compare_with_prev = false
                        rec._meta = rec._meta || {}
                        rec._meta.modified = true
                    }
                    // IE 2026 08 28 / ODOO-5800: снятие чипа сравнения с эталоном — копия становится обычным отчётом
                    if (params.compare_reference) {
                        delete params.compare_reference
                        rec._meta = rec._meta || {}
                        rec._meta.modified = true
                    }
                    delete this.WIDGET._cmpInfo_keep
                    // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): ⊗ после выбора модели в чипсе — модель сравнения возвращается к настройке (выбор забывается)
                    const _pick = chip.segment === 'CHIP-CompareModel' ? this.WIDGET._cmpPmPick : null
                    if (_pick) { if (_pick.has) params.prev_process_model = _pick.v; else delete params.prev_process_model; delete this.WIDGET._cmpPmPick }
                    // runtime-сравнение (кнопка Compare): маркер уходит сразу,
                    // обычный перечит без compare вернёт отчёт без Δ-строк
                    if (rec?.doc?._compare_info) delete rec.doc._compare_info
                }
                this._reload_widget()
                this.hoverIndex = null
                return
            }
            if ((chip._raw || chip).segment === 'CHIP-TreeRoot') {
                // IE 2026 08 20 (заказ Игоря): снятие чипса «текущее дерево» —
                // отбор серверный, перечитываем список (покажутся все деревья)
                const raw0 = chip._raw || chip
                const arr0 = this.chipsRaw || []
                const j0 = arr0.indexOf(raw0)
                if (j0 >= 0) arr0.splice(j0, 1)
                this.hoverIndex = null
                this._reload_widget()
                return
            }
            if ((chip._raw || chip).segment === 'CHIP-Tag' || (chip._raw || chip).attr_type === 'CHIP-Tag') {
                // IE 2026 08 19 / ODOO-5512: снятие тег-чипса — убрать из CHIPS и
                // перестроить (локальный список — колбэком хоста, widget-list — reload)
                const raw = chip._raw || chip
                const arr = this.chipsRaw || []
                const i2 = arr.indexOf(raw)
                if (i2 >= 0) arr.splice(i2, 1)
                this.hoverIndex = null
                if (typeof this.attr?.tags_onToggle === 'function') this.attr.tags_onToggle()
                else this._reload_widget()
                return
            }
            if (chip.segment === 'CHIP-Info' && typeof chip.doc?.onClear === 'function') {
                // IE 2026 08 19 / ODOO-5512: чипс корректировок — ⊗ = откат всех
                // несохранённых корректировок (чипс снимет сам грид)
                chip.doc.onClear()
                this.hoverIndex = null
                return
            }
            if (chip.segment === 'CHIP-Info') {
                // информационный чипс: снятие гасит породившую его настройку
                // отчёта (если она указана), иначе чипс просто убирается
                const rec = this.WIDGET?.record
                const param = chip.doc?.param
                if (param && rec?.doc?.params) {
                    rec.doc.params[param] = false
                    rec._meta = rec._meta || {}
                    rec._meta.modified = true
                }
                const list = rec?.doc?._info_chips
                if (list?.includes(chip.doc)) list.splice(list.indexOf(chip.doc), 1)
                const list2 = this.WIDGET?._dd_info_chips
                if (list2?.includes(chip.doc)) list2.splice(list2.indexOf(chip.doc), 1)
                this.hoverIndex = null
                if (param) this._reload_widget()
                return
            }
            if (chip.segment === 'CHIP-Limit') {
                if (this.WIDGET?.record?.doc) {
                    const doc = this.WIDGET.record.doc
                    const defaults = this.attr?.['CHIP-Limit_defaults']
                    if (defaults) { // сброс на дефолт (глобальный поиск), а не на null как в widget-list
                        doc.limit = defaults.limit ?? null
                        doc.offset = defaults.offset ?? 0
                    } else {
                        doc.limit = null
                        doc.offset = null
                        doc.dataLimit = null
                        doc._limit_total_count = null
                    }
                }
                this._reload_widget()
                this.hoverIndex = null
                return
            }
            const ri = chip._index != null ? chip._index : index
            if (chip?.segment === 'CHIP-Where') {
                chip.meta.use = false
                this.resolveIndex++
            } else if (chip?.segment === 'CHIP-Selector') {
                chip.meta.use = false
                if (chip._raw) chip._raw.use = false

                this.resolveIndex++
            } else {
                this.chipsRaw.splice(ri, 1);
                // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): Legacy — modelValue хоста может быть ненаблюдаемым (MenuPanel.chipModel),
                // computed chips после splice не пересчитан; отдаём список без снятого чипса явно (utils/menuSearchChips.chipsLegacy_afterRemove)
                if (this.mode === 'Legacy') { this.emitChips(undefined, chipsLegacy_afterRemove(this.chips, chip)); this.hoverIndex = null; return }
            }
            this.emitChips();
            this.hoverIndex = null;
        },

        // IE 2026 09 24 / ODOO-… (заказ Игоря: объяснение элементов по Alt) · U4, дефект Backspace / Corrections (без выключателя — починка индекса):
        // removeChip(index) берёт visibleChips[index], а виртуальные чипсы (Corrections / Info / сравнение / процессная модель / пейджинг) стоят в
        // visibleChips ВПЕРЕДИ настоящих (unshift) — прежний индекс this.chips.length - 1 попадал в них: Backspace в пустом поле поиска таблицы
        // снимал чипс Corrections, то есть без вопроса откатывал ВСЕ несохранённые корректировки (doc.onClear). Теперь снимается последний
        // НАСТОЯЩИЙ (не _isVirtual) чипс полосы; настоящих не осталось — ничего (проба tools/probes/backspacecorr_check.mjs, до / после).
        removeLastChip(event) {
            if (event.key === "Backspace" && this.newChipText === "" && this.chips.length > 0) {
                const vis = this.visibleChips
                let vi = -1
                for (let i = vis.length - 1; i >= 0; i--) { if (!vis[i]?._isVirtual) { vi = i; break } }
                if (vi >= 0) this.removeChip(vi);
                event.preventDefault();
            }
        },

        // IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): МЕСТО ДЕЙСТВИЯ чипса — Table | Input (utils/chipPlace.js; бэк be-ie
        // a7577d044 читает chip.meta.place у чипсов грида dataSheet). Только полоса грида данных виджета; выключатель fe.ux.chipPlace
        chipPlace_on() {
            return this.mode === 'Embedded' && this.attr?.name === 'dataSheet' && !!this.WIDGET?.record
                && this.store.setting('fe.ux.chipPlace', true) !== false
        },
        chipPlace_places() { return CHIP_PLACES },
        chipPlace_show(chip) { return this.chipPlace_on() && chipPlace_supported(chip) },
        chipPlace_get(chip) { return chipPlace_of(chip) },
        chipPlace_input(chip) { return this.chipPlace_on() && chipPlace_isInput(chip) },
        chipPlace_set(index, place) {
            const chip = this.visibleChips[index]
            if (!chip || !this.chipPlace_show(chip) || chipPlace_of(chip) === place) return
            chipPlace_write(chip, place)   // пишет в _raw, если чипс — проекция; значение по умолчанию в запись не кладётся
            this.emitChips()               // перезапрос — по отпечатку входных чипсов (chipPlace_afterEmit)
        },
        // сведения бэка о входном чипсе (record._doc.chips_input [{index, applied, notes}]): предупреждения и «не исполнен»
        chipPlace_notes(chip) {
            if (!this.chipPlace_input(chip)) return []
            const c = chip._raw || chip
            const info = chipPlace_info(this.WIDGET?.record?._doc?.chips_input, c._index != null ? c._index : chip._index)
            if (!info) return []
            const notes = (info.notes || []).filter(Boolean).map(String)
            if (info.applied === false) notes.unshift(this.t('Not applied on the server - the rows are not restricted by this chip'))   // IE 2026 09 23 / ODOO-341407: i18n
            return notes
        },
        chipPlace_sigNow() { return this.chipPlace_on() ? chipPlace_sig(this.chipsRaw) : '' },
        // любая смена ВХОДНОГО чипса (добавление, текст / условия, use, удаление, смена места — в обе стороны) меняет выборку:
        // перезапрос виджета с паузой (текст печатают). Гард несохранённых правок — в воронке select (store.unsavedGuard_check).
        // Заодно закрыт прежний дефект: правка чипса-фильтра списка перезапроса не вызывала вовсе
        chipPlace_afterEmit() {
            if (!this.chipPlace_on()) return
            const sig = this.chipPlace_sigNow()
            if (this._chipPlaceSig === undefined) { this._chipPlaceSig = sig; return }
            if (sig === this._chipPlaceSig) return
            this._chipPlaceSig = sig
            clearTimeout(this._chipPlaceTimer)
            const pause = Number(this.store.setting('fe.data.chipPlaceReloadMs', 450)) || 0
            const run = (left) => {
                this._chipPlaceTimer = null
                if (!this.WIDGET?.vueObj || this.WIDGET.close) return
                // идёт загрузка — дождаться её конца: запрос, ушедший раньше правки, вёз прежние чипсы
                if (this.WIDGET.loading && left > 0) { this._chipPlaceTimer = setTimeout(() => run(left - 1), 300); return }
                this._reload_widget()
            }
            this._chipPlaceTimer = setTimeout(() => run(40), pause)
        },
        // IE 2026 09 19 / ODOO-5800 (Igor: флаг «Show other formatting» чипса метрик; нет поля = включён): снят — в таблице остаётся оформление только
        // метрик, выбранных чипсом (AttrGrid.metricFmt_hides). У зеркала глобального чипса флаг живёт на объекте зеркала; у виджета с оформлением на бэке
        // зеркало флага не предлагает — бэк объекта зеркала не видит
        metricFmtFlag_show(chip) {
            const raw = chip._raw || chip
            // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format): у чипса оформления флаг действует там, где оформляет фронт;
            // у виджета с оформлением на бэке — когда бэк объявил, что читает флаг (attr.format_other_formats)
            if (raw.segment === 'CHIP-Format') return this.chipNav_on(chip) && (!this.WIDGET?.record?.doc?.params?.conditional_format_backend || !!this.attr?.format_other_formats)
            if (raw.segment !== 'CHIP-Metric' || chip._global || !this.chipNav_on(chip) || !this.store.metricSelector_on()) return false
            return !(raw._mirror && this.WIDGET?.record?.doc?.params?.conditional_format_backend)
        },
        // IE 2026 10 07 / ODOO-5800: флаг «Dim other formatting» — у чипса сигналов над таблицей (свой и зеркало глобального), там же, где «Show other formatting»; выключатель fe.ux.metricChipDimFlag
        // (дефолт ВКЛ; false — флага нет, ячейки вне отбора приглушаются всегда, как до 07.10)
        metricDimFlag_show(chip) {
            const raw = chip._raw || chip
            return raw.segment === 'CHIP-Metric' && this.metricFmtFlag_show(chip) && this.store.setting('fe.ux.metricChipDimFlag', true) !== false
        },
        toggleDimOtherFormats(index) {   // IE 2026 10 07 / ODOO-5800
            const chip = this.visibleChips[index]
            if (!chip) return
            const c = chip._raw || chip
            c.dimOtherFormats = c.dimOtherFormats !== true
            this.emitChips()
        },
        toggleShowOtherFormats(index) {
            const chip = this.visibleChips[index]
            if (!chip) return
            const c = chip._raw || chip
            c.showOtherFormats = c.showOtherFormats === false
            this.emitChips()
        },
        toggleShowAllLines(index) {
            // задача 14 ч.2: чекбокс «показать строки без найденных» — снимает
            // fold/фильтр строк без совпадений, подсветку оставляет. Флаг живёт на
            // объекте search-чипса; хосты (редакторы/грид) читают chip.showAllLines
            const chip = this.visibleChips[index]
            if (!chip) return
            const c = chip._raw || chip
            c.showAllLines = !c.showAllLines
            this.emitChips()
        },
        chipLimit_canPrev(chip) {
            return (chip.doc?.offset || 0) > 0
        },
        chipLimit_canNext(chip) {
            const limit = chip.doc?.limit
            const offset = chip.doc?.offset || 0
            const total = chip.doc?.totalCount
            if (!limit) return false
            if (total != null) return offset + limit < total
            return true
        },
        chipLimit_prev(index) {
            const chip = this.visibleChips[index]
            if (!chip || chip.segment !== 'CHIP-Limit') return
            const doc = this.WIDGET?.record?.doc
            if (!doc?.limit) return
            const newOffset = Math.max(0, (doc.offset || 0) - doc.limit)
            doc.offset = newOffset
            this._reload_widget()
        },
        chipLimit_next(index) {
            const chip = this.visibleChips[index]
            if (!chip || chip.segment !== 'CHIP-Limit') return
            const doc = this.WIDGET?.record?.doc
            if (!doc?.limit) return
            const total = doc._limit_total_count ?? null
            const newOffset = (doc.offset || 0) + doc.limit
            if (total != null && newOffset >= total) return
            doc.offset = newOffset
            this._reload_widget()
        },
        _reload_widget() {
            if (this.mode === 'Legacy') { // глобальный поиск: перезапрос через update:modelValue (apply_globalSearch)
                this.emitChips({ keepOffset: true })
                return
            }
            if (!this.WIDGET?.vueObj) return
            // IE 2026 09 15 / ODOO-… (критичное замечание Игоря: чипс пейджинга «1 / N ›» возвращался на первую страницу): с 06.09
            // (ODOO-336658) select с ключом серверного кэша виджета НЕ шлёт запись — бэк берёт её из redis-копии, и новый doc.offset
            // терялся. Смена страницы меняет запись → ключ сбрасываем, запись с offset уходит целиком; свежий ключ вернёт ответ
            delete this.WIDGET.widget_cache_key
            this.store.executeStoreMethod(this.WIDGET.vueObj, {
                command: 'executeWidgetMethod',
                method: 'select',
                record_sendToBackend_exclude: ['_doc'],
            })
        },

        // кнопка Open в тултипе чипса-селектора: та же команда, что dblclick
        // (editChip); без своего виджета у чипса — общий widget-selectors
        openChipSelector(index) {
            const chip = this.visibleChips[index]
            const c = chip._raw || chip
            chipWidget_open(this.store, chip.doc?.widget || c.doc?.widget || 'widget-selectors')   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
            this.hoverIndex = null
        },
        editChip(index) {
            const chip = this.visibleChips[index]
            if (chip?._global) return   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): у чипса метрик шапки текст не правится — только форма
            if (chip.segment === "CHIP-Selector") {
                if (chip.doc.widget) {
                    chipWidget_open(this.store, chip.doc.widget)   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
                }
                return
            }
            if (chip.segment === "CHIP-Search") {
                chip._title_beforeEdit = chip.title
                chip.isEditing = true;
                nextTick(() => {
                    const inputs = this.$refs.chipInputs
                    if (inputs?.[0]) {
                        inputs[0].focus();
                        inputs[0].select();
                    }
                });
            }
        },
        saveChip(index) {
            const chip = this.visibleChips[index]
            chip.isEditing = false;
            if ('_title_beforeEdit' in chip) {
                // условие изменили — юзерфрендли-заголовок (titleHTML) ему больше не соответствует
                if (chip.meta?.titleHTML && chip.title !== chip._title_beforeEdit) {
                    delete chip.meta.titleHTML
                }
                delete chip._title_beforeEdit
            }
            this.emitChips();

            const WRow = this.store.get_childrenWidget(this.WIDGET, 'widget-row')
            if (WRow && !WRow._softClosed) {
                this.store.widget_close(WRow.id)
            }
        },

        onDragOver(event) {
            if (this.inputOff) return   // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): в полосу без поля ввода не бросают (иначе бросок создал бы чипс чужого вида)
            const dt = this.store.dataTransfer
            if (dt?.type === 'grid-column' || dt?.type === 'chip') {
                event.preventDefault();
                this.isDragging = true;
            }
        },
        onDragLeave(event) {
            if (!this.$el.contains(event.relatedTarget)) {
                this.isDragging = false;
                this.dragOverChipIndex = null;
            }
        },
        onDrop(event) {
            event.preventDefault();
            this.isDragging = false;

            const dt = this.store.dataTransfer
            if (dt?.type === 'chip') {
                this._dropChip(this.chipsRaw.length)
                return
            }
            if (dt?.type === 'grid-column') {
                this.setChipGroup(dt.attr_col)
                this.emitChips();
            }
        },
        selectChip(index) {
            this.selectedIndex = this.selectedIndex === index ? null : index
            // IE 2026 09 12 / ODOO-… (заказ Игоря, набор под диктовку): клик по чипсу селектора — форма открыта сразу (без задержки ховера),
            // фокус в поле «Pick another value…», курсор в конце текста: набранное и надиктованное попадает туда
            const chip = this.visibleChips[index]
            // %s: клик по телу чипса стола — копия в буфер (тост), форма открывается как раньше. Флаг fe.ux.chipsBarCopyOnClick
            // IE 2026 11 02 / ODOO-350449 (ревью webscm #125265 по p194: p200 — перенос періоду повністю, копіювання чіпа лише на десктопі і з підказкою):
            // на телефоне клик по чипсу открывает панель / форму — без копирования; дефолт fe.ux.chipsBarCopyOnClick — вкл на десктопе, выкл на телефоне
            const mobile = !!this.store.mobileUi?.on
            if (this.mode === 'Page' && !mobile && chip && !chip._global && this.selectedIndex === index && this.chipsBar_flag('fe.ux.chipsBarCopy') && this.chipsBar_flag('fe.ux.chipsBarCopyOnClick', !mobile)) this.chipsBar_copy(chip, { silent: true })
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): клик по чипсу метрик шапки — форма сразу, фокус в поле поиска метрик
            if (chip?._global) {
                clearTimeout(this._chipOpenTimer); clearTimeout(this._chipHoverTimer)
                if (this.hoverIndex !== index) { this.metricPickText = ''; if (this.showDropdown) this.showDropdown = false; this.hoverIndex = index }
                this.$nextTick(() => this.chipPick_focusInput())
                return
            }
            if (this.mode === 'Page' && chip && this.chipPick_segment(chip)) {
                clearTimeout(this._chipOpenTimer); clearTimeout(this._chipHoverTimer)
                if (this.hoverIndex !== index) {
                    this.chipPickText = ''
                    this.chipPickFocused = false
                    if (this.showDropdown) this.showDropdown = false
                    this.hoverIndex = index
                    this.chipValueSnap = this._buildChipValueSnap(index)
                }
                this.$nextTick(() => this.chipPick_focusInput())
            }
        },
        // IE 2026 10 12 / ODOO-343342 (заказ Игоря: правая кнопка на остальных элементах — рейл / чипс / стол)
        // правая кнопка по чипсу (шапка стола и таблицы) — форма этого чипса сразу, без задержки наведения: ровно то, что открывает наведение и клик по чипсу селектора (значения отбора с отметками,
        // «Pick another value…», Recent, Copy / Download / Open selector, значок ⊗ остаётся на своём месте). Селектор — фокус в поле подбора, как при клике; глобальный чипс метрик — поле поиска метрик.
        // Не перехватываем: чипс в правке (имя), ⊗, переключатели ‹ ›, уже открытая форма, поля ввода, выделенный текст. Выключатель fe.ux.contextMenuActions (дефолт ВКЛ); телефон — нет.
        // Закрывается так же, как форма по наведению: уход мыши / Esc в поле подбора / клик вне. Счётчик window.ctxMenuActions.chip
        chipContextMenu(index, e) {
            try {
                const chip = this.visibleChips[index]
                if (!chip || chip.isEditing || chip.segment === 'CHIP-WidgetsPage') return
                if (e.target?.closest?.('.chip-hover-popup, .chip-remove, .chip-nav, .chip-period, .chip-input')) return
                if (!ctxMenu_allowed(this.store, e, { links: true })) return
                e.preventDefault()
                clearTimeout(this._chipOpenTimer); clearTimeout(this._chipHoverTimer)
                this._chipMouseIn = index
                if (this.hoverIndex !== index) {
                    if (this.showDropdown) this.showDropdown = false
                    if (chip._global) this.metricPickText = ''
                    else { this.chipPickText = ''; this.chipPickFocused = false }
                    this.hoverIndex = index
                    this.chipValueSnap = this._buildChipValueSnap(index)
                }
                if (chip._global || (this.mode === 'Page' && this.chipPick_segment(chip))) this.$nextTick(() => this.chipPick_focusInput())
                ctxMenu_count('chip')
            } catch (err) { /* форма не готова — меню браузера уже подавлено, ничего не открываем */ }
        },
        chipPick_focusInput() {   // IE 2026 09 12 / ODOO-…: фокус в поле подбора открытой формы чипса, курсор в конце
            const el = this.$el?.querySelector?.('.chip-hover-popup .chip-pick-input')
            if (!el) return
            el.focus()
            const n = (el.value || '').length
            try { el.setSelectionRange(n, n) } catch (e) { /* не текстовый инпут */ }
        },
        chipPick_inputFocused() {   // IE 2026 09 12 / ODOO-…: поле подбора этой полосы в фокусе (форму не прячем по уходу мыши)
            const ae = document.activeElement
            return !!(ae && ae.classList?.contains('chip-pick-input') && this.$el?.contains?.(ae))
        },
        chipPick_close() {
            this._chipPickRun = (this._chipPickRun || 0) + 1   // IE 2026 09 14 / ODOO-… (заказ Игоря: ступени 5–6 поиска): отменить незавершённый проход подбора   // IE 2026 09 12 / ODOO-…: закрыть форму чипса (Esc в пустом поле, уход фокуса при мыши вне чипса)
            this.hoverIndex = null
            this.chipPickFocused = false
        },
        onChipPickBlur() {   // IE 2026 09 12 / ODOO-…: фокус ушёл из поля — если мышь не над чипсом, форма закрывается как по уходу мыши
            if (this._chipMouseIn === null || this._chipMouseIn === undefined) {
                clearTimeout(this._chipHoverTimer)
                this._chipHoverTimer = setTimeout(() => { if (!this.chipPick_inputFocused()) this.hoverIndex = null }, this.store?.setting('fe.ux.hoverHideMs', 300) ?? 300)
            }
        },
        _dataURLtoBlob(dataURL) {
            const [header, data] = dataURL.split(',')
            const mime = header.match(/:(.*?);/)[1]
            const binary = atob(data)
            const arr = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i)
            return new Blob([arr], { type: mime })
        },
        cutChip(index) {
            this.copyChip(index)
            this.removeChip(index)
            this.selectedIndex = null
        },
        onChipDragStart(index, event) {
            const ri = this._realIndex(index)
            this.store.dataTransfer = {
                type: 'chip',
                chip: JSON.parse(JSON.stringify(this.chipsRaw[ri])),
                sourceIndex: ri,
                sourceAttrChips: this,
            }
            event.dataTransfer.effectAllowed = 'move'
            this.selectedIndex = null
            this.hoverIndex = null

            const chipEl = event.currentTarget
            const clone = chipEl.cloneNode(true)
            clone.querySelector('.chip-hover-popup')?.remove()
            clone.style.cssText = 'position:fixed;top:-1000px;left:-1000px;pointer-events:none;'
            document.body.appendChild(clone)
            const rect = chipEl.getBoundingClientRect()
            event.dataTransfer.setDragImage(clone, event.clientX - rect.left, event.clientY - rect.top)
            setTimeout(() => clone.remove(), 0)
        },
        onChipDragOver(index) {
            if (this.store.dataTransfer?.type === 'chip') {
                this.dragOverChipIndex = index
            }
        },
        onChipDragLeave(index) {
            if (this.dragOverChipIndex === index) this.dragOverChipIndex = null
        },
        onChipDrop(index, event) {
            event.preventDefault()
            this.isDragging = false
            const dt = this.store.dataTransfer
            if (!dt || dt.type !== 'chip') return
            this._dropChip(this._realIndex(index))
        },
        onChipDragEnd() {
            this.dragOverChipIndex = null
            this.isDragging = false
            if (this.store.dataTransfer?.type === 'chip') this.store.dataTransfer = null
        },
        _dropChip(targetRealIndex) {
            const dt = this.store.dataTransfer
            if (!dt || dt.type !== 'chip') return
            const isSame = dt.sourceAttrChips === this
            if (isSame) {
                const src = dt.sourceIndex
                if (src === targetRealIndex) { this.store.dataTransfer = null; return }
                const chip = this.chipsRaw.splice(src, 1)[0]
                const adjusted = src < targetRealIndex ? targetRealIndex - 1 : targetRealIndex
                this.chipsRaw.splice(adjusted, 0, chip)
            } else {
                dt.sourceAttrChips.chipsRaw.splice(dt.sourceIndex, 1)
                dt.sourceAttrChips.emitChips()
                const target = Math.min(targetRealIndex, this.chipsRaw.length)
                this.chipsRaw.splice(target, 0, dt.chip)
            }
            this.dragOverChipIndex = null
            this.store.dataTransfer = null
            this.emitChips()
        },

        setChipGroup(attr_col) {
            let chip = this.chips.find(chip => chip.segment === "CHIP-Group");
            if (!chip) {
                chip = {
                    segment: "CHIP-Group",
                    meta: {
                        use: true,
                        pi: "pi-play",
                    },
                    doc: {
                        groupDescriptions: [],
                    },
                }
                this.chipsRaw.push(chip)
            }

            let binding = attr_col.binding
            if (attr_col.attr_type === 'AttrLinkOdoo') {
                binding += '[1]'
            }

            chip.doc.groupDescriptions.push({binding:binding, title:attr_col.title || binding});
            chip.title = chip.doc.groupDescriptions.map(group => group.title).join(", ");

            return chip
        },
        getChipGroup(forCompare=false) {
            let groupDescriptions = []
            let chip = this.chips.find(chip => chip.segment === "CHIP-Group");
            if (chip) {
                groupDescriptions = chip.doc.groupDescriptions.map(group => group.binding)
            }
            return groupDescriptions
        },
        compareChipGroup(vueGrid) {
            let groupDescriptions = this.getChipGroup(true)
            const current_groupDescriptions = vueGrid.flex.itemsSource.groupDescriptions.map(group => group.propertyName);

            return JSON.stringify(groupDescriptions) === JSON.stringify(current_groupDescriptions)
        },

        getChipTree() {
            // CHIP-Tree (Hierarchy): биндинги parent-полей — дерево строит грид (set_itemsSource).
            // doc.groupDescriptions переиспользуется как есть: чип переключаем с CHIP-Group без потери настройки
            let chip = this.chips.find(chip => chip.segment === "CHIP-Tree" && chip.meta?.use !== false);
            if (!chip) return []
            return (chip.doc?.groupDescriptions || []).map(group => group.binding)
        },
        compareChipTree(vueGrid) {
            return JSON.stringify(this.getChipTree()) === JSON.stringify(vueGrid.applied_chipTree || [])
        },
        // IE 2026 11 02 / ODOO-349799 (group-header-value): опция чипа группировки «Field and count» — полный заголовок строки-группы; в самом чипе (meta.fieldAndCount), как Group ↔ Hierarchy.
        // Грид читает её в groupHeader_apply (flex.groupHeaderFormat); пункт меню — только у чипа CHIP-Group и при включённом fe.ux.gridGroupHeaderValue
        getChipGroupFieldAndCount() {
            const chip = this.chips.find(chip => chip.segment === 'CHIP-Group')
            return groupHeader_fieldAndCount(chip)
        },
        groupHeaderOption_show(chip) {
            return (chip._raw || chip).segment === 'CHIP-Group' && groupHeaderValue_on(this.store)
        },
        groupHeader_fieldAndCount(chip) {
            return groupHeader_fieldAndCount(chip)
        },
        groupHeaderOption_toggle(index) {
            const chip = this.visibleChips[index]
            if (!chip) return
            const raw = chip._raw || chip
            raw.meta ||= {}
            raw.meta[GROUP_HEADER_OPTION] = !groupHeader_fieldAndCount(raw)
            this.emitChips()
        },

        setChipOrder(vueGrid) {
            let chip = this.chips.find(chip => chip.segment === "CHIP-Order");
            if (!chip) {
                chip = {
                    segment: "CHIP-Order",
                    meta: {
                        use: true,
                        pi: "pi-sort",
                    },
                    doc:{
                        sortDescriptions: [],
                    },
                }
                this.chipsRaw.push(chip)
            }

            chip.doc.sortDescriptions = vueGrid.flex.collectionView?.sortDescriptions.map(sortDesc => ({
                binding: sortDesc.property,
                title: vueGrid.attr_by_binding[sortDesc.property]?.title || sortDesc.property,
                direction: sortDesc.ascending ? 'ASC' : 'DESC'
            }));
            chip.title = chip.doc.sortDescriptions.map(row => row.title).join(", ");

            if (chip.doc.sortDescriptions.length === 0) {
                const index = this.chips.indexOf(chip);
                this.chipsRaw.splice(index, 1);
            }

            this.emitChips({fast:true});

            return chip
        },
        getChipOrder(forCompare=false) {
            let sortDescriptions = []
            let chip = this.chips.find(chip => chip.segment === "CHIP-Order");
            if (chip) {
                if (!forCompare) {
                    sortDescriptions = chip.doc.sortDescriptions.map(row => new wjcCore.SortDescription(row.binding, row.direction === 'ASC'))
                } else {
                    sortDescriptions = chip.doc.sortDescriptions.map(group => [group.binding, group.direction])
                }
            }
            return sortDescriptions
        },
        compareChipOrder(vueGrid) {
            let sortDescriptions = this.getChipOrder(true)
            const current_sortDescriptions = vueGrid.flex.collectionView?.sortDescriptions.map(sortDesc => ([
                sortDesc.property,
                sortDesc.ascending ? 'ASC' : 'DESC'
            ]));

            return JSON.stringify(sortDescriptions) === JSON.stringify(current_sortDescriptions)
        },

        setChipWhere(title, binding, condition, value) {
            let chip = this.chips.find(chip => chip.segment === "CHIP-Where" && (chip.doc?.WHERE?.[0] || chip.doc)?.leftValue?.value === binding);
            if (!chip) {
                chip = {
                    segment: 'CHIP-Where',
                    meta:{
                        use: true,
                        pi: 'pi-filter',
                        cssClass: binding,
                    },
                    doc:{
                        WHERE: [],
                    },
                }
                this.chipsRaw.push(chip)
            }

            chip.doc.WHERE = [{
                use: true,
                leftValue: { value: binding, valueStr: binding.charAt(0).toUpperCase() + binding.slice(1) },
                condition: condition,
                rightValue: { value: value, valueStr: title || String(value) },
            }]
            chip.title = title || `${binding}=${value}`

            this.emitChips();

            return chip
        },

        setChipGridFilter(vueGrid, filter) {
            let chip = this.chips.find(chip => chip.segment === "CHIP-GridFilter");
            if (!chip) {
                chip = {
                    segment: 'CHIP-GridFilter',
                    meta:{
                        use: true,
                        pi: 'pi-filter',
                    },
                    doc: {},
                }
                this.chipsRaw.push(chip)
            }

            chip.doc.filterDefinition = filter.filterDefinition

            let filterDefinition = JSON.parse(filter.filterDefinition)
            chip.title = filterDefinition.filters.map(row =>
                vueGrid.attr_by_binding[row.binding]?.title || row.binding,
            ).join(", ");

            if (!chip.title) {
                const index = this.chips.indexOf(chip);
                this.chipsRaw.splice(index, 1);
            }

            if (this.modelValue_timeout) { clearTimeout(this.modelValue_timeout) }
            this.modelValue_timeout = window.$cg.setTimeout(this, "AttrChips.setChipGridFilter", () => {
                this.emitChips();
            }, 100)

            return chip
        },
        getChipGridFilter() {
            let chip = this.chips.find(chip => chip.segment === "CHIP-GridFilter");
            return chip?.doc.filterDefinition || '{"defaultFilterType":3,"filters":[]}'
        },
        compareGridFilter(vueGrid, filter) {
            if (!filter) return true
            let filterDefinition = this.getChipGridFilter()
            let current_filterDefinition = filter?.filterDefinition

            return filterDefinition === current_filterDefinition
        },
        onChipsInputBlur() {
            this.langBuffer = []
            // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска) (решение Q2): набранный, не оформленный текст при уходе фокуса остаётся видимым и действующим
            // (живой поиск продолжает подсвечивать); снимается стиранием или Esc
            if (this.liveSearch_on() && liveSearch_text(this.newChipText)) return
            this.editable = false
        },
        // IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): живой поиск — только у полосы над таблицей (mode Embedded, хост слушает live-search), выключатель fe.ux.gridLiveSearch
        liveSearch_on() {
            return this.mode === 'Embedded' && !this.inputOff && typeof this.$attrs?.onLiveSearch === 'function' && this.store.setting('fe.ux.gridLiveSearch', true) !== false
        },
        liveSearch_input(v) {
            if (!this.liveSearch_on()) return
            const text = liveSearch_text(v)
            clearTimeout(this._liveT)
            this._liveT = null
            if (text === (this._liveSent || '')) return
            const ms = liveSearch_delay(this.store.setting('fe.ux.gridLiveSearchDebounceMs', 250), text)
            if (!ms) { this.liveSearch_emit(text); return }
            this._liveT = window.$cg.setTimeout(this, 'AttrChips.liveSearch', () => { this._liveT = null; this.liveSearch_emit(text) }, ms)
        },
        // apply = false — хост только снимает текст, проход отбора сделает следующий emitChips (Enter)
        liveSearch_emit(text, apply = true) {
            clearTimeout(this._liveT)
            this._liveT = null
            this._liveSent = text
            this.$emit('live-search', text, { apply })
        },
        _convertChar(code, key, toLang) {
            const entry = LAYOUT_MAP[code]
            if (!entry || !entry[toLang]) return key
            const upper = key === key.toUpperCase() && key !== key.toLowerCase()
            return upper ? entry[toLang].toUpperCase() : entry[toLang]
        },
        _convertBuffer(newLang) {
            if (!this.langBuffer.length) return
            const converted = this.langBuffer.map(({code: c, key: k}) => ({
                code: c, key: this._convertChar(c, k, newLang),
            }))
            const convertedStr = converted.map(x => x.key).join('')
            const textBefore = this.newChipText.length >= this.langBuffer.length
                ? this.newChipText.slice(0, this.newChipText.length - this.langBuffer.length)
                : ''
            this.newChipText = textBefore + convertedStr
            this.langBuffer = converted
        },
        onChipsInputKeydown(event) {
            const {code, key} = event

            const isLangSwitch = (
                (code === 'Space'       && event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey) || // macOS Ctrl+Space
                (code === 'CapsLock'    && !event.ctrlKey && !event.altKey && !event.metaKey) ||                   // macOS Caps Lock layout switch
                (code === 'Fn'          || code === 'FnLock') ||                                                   // macOS Globe/Fn key
                /^F\d+$/.test(code) ||                                                                             // F1–F24 function key
                ((code === 'ShiftLeft'  || code === 'ShiftRight') && event.altKey  && !event.ctrlKey && !event.metaKey) || // Win Alt+Shift
                ((code === 'ShiftLeft'  || code === 'ShiftRight') && event.ctrlKey && !event.altKey  && !event.metaKey)   // Win Ctrl+Shift
            )
            if (isLangSwitch && this.langBuffer.length > 0) {
                const bufLang = this._getLangBufferLang()
                if (bufLang) {
                    this._convertBuffer(bufLang === 'ru' ? 'en' : 'ru')
                }
                return
            }

            if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(key)) {
                this.langBuffer = []
                return
            }
            if (key === ' ' || key === 'Enter') {
                this.langBuffer = []
                return
            }
            if (key === 'Backspace') {
                this.langBuffer.pop()
                return
            }
            if (key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return

            const isRU = /[а-яёА-ЯЁ]/.test(key)
            const isEN = /[a-zA-Z]/.test(key)
            if (!isRU && !isEN) {
                this.langBuffer.push({code, key})
                return
            }

            const newLang = isRU ? 'ru' : 'en'
            const bufLang = this._getLangBufferLang()

            if (bufLang && bufLang !== newLang && this.langBuffer.length > 0) {
                event.preventDefault()
                this._convertBuffer(newLang)
                const newChar = this._convertChar(code, key, newLang)
                this.newChipText += newChar
                this.langBuffer.push({code, key: newChar})
            } else {
                this.langBuffer.push({code, key})
            }
        },
        _getLangBufferLang() {
            for (const {key} of this.langBuffer) {
                if (/[а-яёА-ЯЁ]/.test(key)) return 'ru'
                if (/[a-zA-Z]/.test(key)) return 'en'
            }
            return null
        },
        onInputContainerMouseDown(event) {
            if (event.target.tagName === 'INPUT') return
            if (event.target.closest('.chip')) return
            event.preventDefault()
        },
        focusInput(event) {
            if (event.target === this.$refs.input || event.target.closest('.chip')) return;
            if (this.$refs.barInput && event.target === this.$refs.barInput) return   // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл)
            // IE 2026 09 04 / ODOO-336291 (mobile UX): B-42 topbarSearch — в режиме Page инпут не рендерится вовсе
            // (v-if="editable && mode !== 'Page'"), и «поле поиска» в шапке было мёртвым: 3-4 тапа = ноль мутаций,
            // фокуса нет, единственный вход — шеврон. На телефоне тап по полосе открывает ту же панель чипсов.
            // Выкл: window.mobileUi.set('topbarSearch', false)
            if (this.store.mobileUi?.on && this.store.mobileUi.topbarSearch !== false && this.mode === 'Page') {
                this.toggleDropdown()
                window.mobTapDone?.('topbarChips') // IE 2026 09 04 / ODOO-336291 (mobile UX): P-31
                return
            }
            this.selectedIndex = null
            // IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): клик по пустому месту полосы стола (десктоп) — фокус в поле поиска по селекторам
            if (this.mode === 'Page' && this.barInput_on()) { nextTick(() => this.$refs.barInput?.focus()); return }
            this.focus();
        },
        focus() {
            this.editable = true;
            nextTick(() => {
                this.$refs.input?.focus();
            });
        },

        toggleChipMenu(index, event) {
            const chip = this.visibleChips[index]
            if (chip?._global) { this.metricChipG_openReport(chip); return }
            if (chip?._mirror) return   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»: приём чипса отчётом): у зеркала над таблицей своей строки настроек нет   // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): шестерёнка чипса метрик шапки — отчёт метрик
            const segment = chip?.segment || chip?._raw?.segment
            // if (segment === "CHIP-Group" || segment === "CHIP-Tree" || segment === "CHIP-Param" || segment === "CHIP-Node" || segment === "CHIP-Widget" || segment === "CHIP-Record") {
            //     this.showChipMenuIndex = this.showChipMenuIndex === index ? null : index;
            // }

            if (!this.WIDGET.vueObj) {
                if (chip.doc?.widget) {
                    chipWidget_open(this.store, chip.doc.widget)   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
                }
                return
            }

            const chipEl = event?.target
            const widgetEl = document.getElementById('WIDGET' + this.WIDGET.id)
            let x, y
            if (chipEl && widgetEl) {
                const cr = chipEl.getBoundingClientRect()
                const wr = widgetEl.getBoundingClientRect()
                x = cr.left - wr.left + cr.width / 2
                y = cr.top - wr.top + cr.height / 2
            }
            this._openWidgetRowForChip(chip, index, x, y)

        },
        _openWidgetRowForChip(chip, index, x, y) {
            if (this.mode === 'Legacy') return // глобальный поиск: WIDGET фейковый, widget-row к нему не открыть
            const vueObj = this
            const WRow = vueObj.store.get_childrenWidget(vueObj.WIDGET, 'widget-row')
            if (WRow && !WRow._softClosed && WRow.parentAction?.type === 'widget-row-chip' && WRow.record?._meta?.binding === this.attr.binding && WRow.record?._meta?.index === index) {
                vueObj.store.widget_close(WRow.id)
                return
            }
            
            const actionMenu = {
                command:'widget_open',
                'widget':'widget-row',
                'type': 'widget-row-chip',
                source_attr_type: chip.segment,

                binding: this.binding,
                source: {
                    title: chip.title || '',
                    type_title: this.chipTypes[chip.segment]?.title || chip.segment || '',
                    index: index,
                    name: this.attr.name,
                },

                'get_Parent':true,
                'open_in_children_widget':true,
                'widgetRow_params': {
                    'top_pct': 0.6,
                    'left_pct': 0.2,
                    'width_pct': 0.8,
                    'height_pct': 0.8,
                },

                selectedCog: {
                    x,
                    y,
                },
            }
            vueObj.store.executeStoreMethod(vueObj.WIDGET.vueObj, actionMenu, vueObj.attr)
        },
        onDropdownItemClick(index) {
            this.toggleChipMenu(index)
            this.showDropdown = false
        },
        onDropdownTabAction(action) {
            if (!this.store.checkAccess(action)) {
                return
            }
            if (action.command === 'widget_open') {
                this.store.widget_open({ widget: action.widget })
            }
            if (action.command === 'set_activeAttr') {
                this.store.set_activeAttr(this.WIDGET.view, { binding: action.binding }, this.WIDGET)
            }
            this.showDropdown = false
        },
        onDropdownTabAddAction(action) {
            if (action.command === 'add_chip') {
                const segment = action.segment || 'CHIP-Where'
                const chip = {
                    segment,
                    meta: { use: true, pi: this.chipTypes[segment]?.pi || 'pi pi-filter' },
                    doc: { WHERE: [] },
                }
                this.chipsRaw.push(chip)
                this.emitChips()
                nextTick(() => {
                    const index = this.visibleChips.length - 1
                    if (index >= 0) this._openWidgetRowForChip(this.chips[this.chips.length - 1], index)
                })
            }
        },
        onDropdownTabItemClick(item) {
            // IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): чипс метрик добавляется пустым и сразу открывает свою форму выбора (не форму строки)
            if (item._template && item.segment === 'CHIP-Format') { this.formatChip_add(); return }   // IE 2026 09 20 / ODOO-5800 (Igor: колонка Selector и статистика грида Conditional format, чипс условного оформления CHIP-Format)
            if (item._template && item.segment === 'CHIP-Metric') { this.metricChip_add(); return }
            // IE 2026 09 19 / ODOO-5800 (заказ Игоря «глобальный чипс-метрик»): строка чипса метрик в списке ⌄ шапки — выключенную вернуть, у действующей открыть форму
            if (item._global) {
                this.showDropdown = false
                if (item.meta?.use === false) { this.check_chip_dropdown(item); this.emitChips(); return }
                nextTick(() => { const i = this.visibleChips.findIndex(c => c._global); if (i >= 0) { this.metricPickText = ''; this.hoverIndex = i } })
                return
            }
            if (item._template) {
                const { _template, _noOpen, ...chip } = item
                chip.meta = { ...(chip.meta || {}), use: true }
                chip.doc = { ...(chip.doc || {}), WHERE: chip.doc?.WHERE ? [...chip.doc.WHERE] : [] }
                this.chipsRaw.push(chip)
                this.emitChips()
                if (_noOpen) {
                    // IE 2026 08 19 (заказ Игоря): готовый чипс (Parent) — без формы;
                    // хост перестраивает сам (tags_onToggle у списка настроек)
                    this.showDropdown = false
                    if (typeof this.attr?.tags_onToggle === 'function') this.attr.tags_onToggle()
                    else this._reload_widget()
                    return
                }
                nextTick(() => {
                    const last = this.chips[this.chips.length - 1]
                    if (last) this._openWidgetRowForChip(last, this.chips.length - 1)
                })
                return
            }

            if (item.segment === 'CHIP-Selector' && this.mode === 'Page') {
                chipWidget_open(this.store, item.doc.widget || 'widget-selectors')   // IE 2026 10 08 / ODOO-5800: виджет чипса — явно по привязанной записи, без подмены «Я»/«Всі» (utils/chipWidgetOpen)
                this.showDropdown = false
                return
            }

            // IE 2026 08 28 / ODOO-5828 (заказ Игоря): пункт-команда таба Commands —
            // диспатч как из меню пользователя (activeWIDGET, иначе сам компонент)
            if (item.segment === 'CHIP-Command') {
                if (item.action) {
                    const vueObj = this.store.activeWIDGET?.vueObj || this
                    this.store.executeStoreMethod(vueObj, { ...item.action })
                }
                this.showDropdown = false
                return
            }

            this.showDropdown = false
        },
        resolveDropdownItemTitle(chip) {
            // if (chip.title) return chip.title
            
            if (chip.segment === 'CHIP-Where') return chip.doc.WHERE?.[0]?.leftValue?.valueStr || chip.doc.leftValue?.valueStr || ''
            if (chip.segment === 'CHIP-Selector') return chip.doc.selector || chip.doc.widget?.valueStr || chip.doc.widget?.title || ''
            if (chip.segment === 'CHIP-Param') return chip.doc.attr?.title || ''
            if (chip.segment === 'CHIP-Node') return chip.doc.node?.valueStr || ''
            if (chip.segment === 'CHIP-Widget') return chip.doc.widget?.title || ''
            if (chip.segment === 'CHIP-Record') return chip.doc.record?.title || ''
            
            return chip.title || ''
        },
        resolveDropdownItemHTML(chip) {
            if (chip.meta?.titleHTML) return chip.meta.titleHTML
            const resolved = this._resolveTitle(chip)
            if (chip.title && chip.title !== resolved.text) return this.chipTitle_lang(chip)   // IE 2026 10 12 / ODOO-5800: было chip.title
            return resolved.html
        },
        toggleDropdown() {
            if (!this.showDropdown) {
                const rect = this.$el.getBoundingClientRect()
                this.dropdownStyle = {
                    top: rect.bottom + 'px',
                    left: rect.left + 'px',
                }
            }
            this.showDropdown = !this.showDropdown
        },
        onChipsInputEsc(event) {
            if (this.newChipText) {
                this.newChipText = ''
            } else {
                this.$refs.input?.blur()
                this.store.runActionForHotKey(this.store.activeWIDGET, "Esc", event)
            }
        },
        _onDocumentClick(e) {
            const container = this.$el
            if (!container.contains(e.target)) {
                this.showDropdown = false
            }
        },
        // IE 2026 09 20 / ODOO-5800 (Igor: переходы чипса метрик по ячейкам, подсветка параметра ячейки и подпадающих ячеек по наведению на чипс)
        metricChipHover_emit(index) {
            const chip = index === null || index === undefined ? null : this.visibleChips[index]
            // IE 2026 09 20 / ODOO-5800: наведение на чипс условного оформления подсвечивает ячейки его правил тем же механизмом (segment говорит гриду, чей список)
            const seg = chip ? (chip._raw || chip).segment : ''
            const on = !!chip && this.mode === 'Embedded' && !chip._global && (seg === 'CHIP-Metric' || (seg === 'CHIP-Format' && this.formatChip_on()))
            const sig = on ? seg : ''
            if (sig === (this._metricHoverOn || '')) return
            this._metricHoverOn = sig
            this.$emit('chip-metric-hover', { index, on, segment: seg })
        },
        onChipMouseOver(index) {
            this.metricChipHover_emit(index)
            clearTimeout(this._chipHoverTimer)
            clearTimeout(this._chipOpenTimer)
            this._chipMouseIn = index   // IE 2026 09 12 / ODOO-…
            if (this.hoverIndex === index) return
            const open = () => {
                if (this.hoverIndex !== index) {
                    // смена чипса — сброс состояния подбора значения
                    this.chipPickText = ''
                    this.chipPickFocused = false
                }
                // ховер по чипсу при открытом общем дропдауне (Selectors/Params):
                // закрываем общий блок — его место занимает стандартный popup чипса
                if (this.showDropdown) this.showDropdown = false
                this.hoverIndex = index
                this.chipValueSnap = this._buildChipValueSnap(index)   // IE 2026 09 09 / ODOO-5800
            }
            // ИЕ 2026-08-14: попап по НАМЕРЕНИЮ — мимолётный проезд мышкой над
            // полосой чипсов больше не выбрасывает форму. Если попап соседнего
            // чипса уже открыт, переключаемся мгновенно (как между пунктами меню).
            // Настройка без пересборки: window.fastEntry.set('hoverOpenMs', N)
            if (this.hoverIndex !== null) { open(); return }
            const openMs = this.store?.fastEntry?.hoverOpenMs ?? this.store?.setting('fe.ux.hoverOpenMs', 350) ?? 350   // IE 2026 09 05 / ODOO-336330: настройка базы; fastEntry — личный override
            this._chipOpenTimer = setTimeout(open, openMs)
        },
        // IE 2026 09 14 / ODOO-5800 (замечание Игоря): длинные названия в форме под чипсом (значения отбора, подбор Pick, Recent)
        // обрезаются многоточием по ширине формы, а не вылезают поверх соседних элементов; наведение на обрезанное название —
        // «бесшовное продолжение» текста поверх (как у обрезанной ячейки / заголовка грида: header-title-overlay в AttrGrid).
        // Оверлей — pointer-events: none, временный (автоскрытие 5 с), снимается уходом мыши, прокруткой списка и закрытием формы
        // IE 2026 10 10 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE1-kclick FE1 (A-03): щелчок по ссылке подсказки чипса согласования — форма записи (сегмент / версия)
        apprChip_is(chip) { return approvalChipIs(chip?.doc) },   // IE 2026 10 11 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE2 (UX1 S-06)
        apprLink_click(e) {
            const a = e?.target?.closest?.('.appr-chip-link')
            if (!a) return
            e.preventDefault(); e.stopPropagation()
            approvalOpenRef(this.store, this.WIDGET?.vueObj || this, { segment: a.dataset.apprSeg, id: a.dataset.apprId })
        },
        chipTip_over(e) {
            if (!this.chipClipOn) return
            const el = e.target?.closest?.('.chip-clip, .chip-tooltip-row')
            if (el && el === this._ctEl) return
            this.chipTip_hide()
            if (!el || !(el.scrollWidth > el.clientWidth + 1)) return
            this._ctEl = el
            this._ctTimer = setTimeout(() => {
                this._ctTimer = null
                if (!el.isConnected || this._ctEl !== el) return
                // IE 2026 09 18 / ODOO-… (замечание Игоря): общий помощник — текст поверх строки пиксель в пиксель (замер первой буквы),
                // фон / цвет / шрифт — самой строки, подсветка совпадений подбора переносится вместе с оформлением
                const ov = clipOverlay_show(el, { className: 'clip-overlay chip-clip-overlay' })
                if (!ov) return
                this._ctOv = ov
                this._ctTimer3 = setTimeout(() => this.chipTip_hide(), 5000)
            }, this.store?.setting?.('fe.ux.clipOverlayDelayMs', 250) ?? 250)
        },
        chipTip_hide() {
            clearTimeout(this._ctTimer); clearTimeout(this._ctTimer3); this._ctTimer = this._ctTimer3 = null
            if (this._ctOv) { this._ctOv.remove(); this._ctOv = null }
            this._ctEl = null
        },
        onChipMouseLeave() {
            this.metricChipHover_emit(null)   // IE 2026 09 20 / ODOO-5800 (Igor: переходы чипса метрик по ячейкам, подсветка параметра ячейки и подпадающих ячеек по наведению на чипс)
            clearTimeout(this._chipHoverTimer)
            clearTimeout(this._chipOpenTimer) // ушли раньше задержки — не открывать
            this._chipMouseIn = null   // IE 2026 09 12 / ODOO-…
            // IE 2026 09 12 / ODOO-…: поле подбора в фокусе (набор/диктовка) — форму по уходу мыши не прячем
            this._chipHoverTimer = setTimeout(() => { if (!this.chipPick_inputFocused()) this.hoverIndex = null }, this.store?.setting('fe.ux.hoverHideMs', 300) ?? 300)   // IE 2026 09 05 / ODOO-336330
        },
        // курсор на кнопке закрытия: попап не открываем и уже открытый убираем
        // сразу (без 300мс отсрочки onChipMouseLeave) — иначе выпадающий блок
        // перекрывает ⊗ и по нему невозможно попасть
        onChipRemoveMouseOver() {
            clearTimeout(this._chipHoverTimer)
            clearTimeout(this._chipOpenTimer)
            this.hoverIndex = null
        },
        getChipMenuTypes(chip) {
            const segment = (chip._raw || chip).segment
            const group = this.chipTypeSwitchGroups.find(g => g.includes(segment))
            if (!group) return null
            const types = {}
            for (const id of group) {
                if (this.chipTypes[id]) types[id] = this.chipTypes[id]
            }
            return types
        },
        selectChipType(index, id, type) {
            const chip = this.visibleChips[index]
            chip.segment = id;
            chip.meta ||= {};
            chip.meta.pi = type.pi;
            this.hoverIndex = null;
            this.showChipMenuIndex = null;
            this.emitChips();
        },
    },
});
</script>

<style scoped>
.chips-container {
    position: relative;
    display: flex;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.5rem;
}
.chips-dropdown {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
    display: flex;
    max-width: 90vw;
    max-height: 400px;
    overflow: auto;
}
.dropdown-tab {
    flex: none;
    white-space: nowrap;
    border-right: 1px solid var(--surface-border, #dee2e6);
}
.dropdown-tab:last-child {
    border-right: none;
}
.dropdown-tab-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid var(--surface-border, #dee2e6);
    color: var(--text-color);
}
.dropdown-tab-header-action {
    cursor: pointer;
}
.dropdown-tab-header-action:hover {
    background-color: var(--surface-hover, #e9ecef);
}
.dropdown-tab-header i {
    font-size: 14px;
}
.dropdown-tab-header-add {
    margin-left: auto;
    cursor: pointer;
    opacity: 0.5;
    border-radius: 3px;
    padding: 2px 4px;
}
.dropdown-tab-header-add:hover {
    opacity: 1;
    background-color: var(--surface-hover, #e9ecef);
}
.dropdown-tab-items {
    padding: 4px 0;
}
.dropdown-tab-item {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 13px;
}
.dropdown-tab-item-nested {
    padding-left: 24px;
}
.dropdown-tab-subtitle {
    padding: 6px 12px 2px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-secondary, #999);
    border-top: 1px solid var(--surface-border, #dee2e6);
    margin-top: 4px;
}
.dropdown-tab-item:hover {
    background-color: var(--surface-hover, #e9ecef);
}
.dropdown-tab-item-check {
    cursor: pointer;
    margin-right: 6px;
    font-size: 14px;
    color: var(--primary-color, #2196f3);
}
/* IE 2026 08 28 / ODOO-5828: пункты-команды и разделитель таба Commands */
.dropdown-tab-item-cmd-pi {
    margin-right: 6px;
    font-size: 13px;
    color: var(--text-color-secondary, #888);
}
.dropdown-tab-divider {
    border-top: 1px solid var(--surface-border, #dee2e6);
    margin: 4px 12px;
}
.dropdown-tab-item-check.pi-stop {
    color: var(--text-color-secondary, #999);
}
.dropdown-tab-item-title {
    flex: 1;
}
.chips-dropdown-item {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 13px;
    white-space: nowrap;
}
.chips-dropdown-item:hover {
    background-color: var(--surface-hover, #e9ecef);
}
.chips-dropdown-empty {
    padding: 8px 10px;
    color: var(--text-color-secondary, #999);
    font-size: 13px;
}
/* IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: живой поиск и чипсы поиска): полоса без своего поля ввода (строка поиска панели-меню) — без рамки и отступов, ширина по чипсам */
.chips-container.chips-bare { border: none; border-radius: 0; background: transparent; min-width: 0; max-width: 100%; }
.chips-container.chips-bare .input-container { padding: 0; width: auto; min-width: 0; cursor: default; }
.chips-container.drag-over {
    border-color: #2196f3;
    background-color: rgba(33, 150, 243, 0.1);
}
.input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    padding: 4px;
    width: -webkit-fill-available;
    cursor: text;
}

.chips-icon {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #ccc;
    margin-left: 8px;
}
.chips-placeholder-text {
    font-size: 14px;
    margin-left: 6px;
    color: #aaa;
}

.chip {
    position: relative;
    display: flex;
    align-items: center;
    background: rgb(222 226 230 / 75%);
    color: var(--text-color);
    border-radius: 3px;
    font-size: 14px;
    white-space: nowrap;
    max-width: 100%; /* не вылезать за контейнер: текст обрезается в .chip-text */
    min-width: 5rem; /* пол при обрезке: видно хотя бы начало текста, а не одно многоточие */
}

/* IE 2026 08 18 / ODOO-5512: чипс несохранённых правок источника у расшифровки —
   жёлтый, как правленые ячейки (lightgoldenrodyellow) */
.chip.chip-unsaved-edits {
    background: lightgoldenrodyellow;
    box-shadow: inset 0 0 0 1px rgba(200, 160, 0, .45);
}
.chip.chip-unsaved-edits .chip-icon { background: rgba(200, 160, 0, .75); }
/* IE 2026 10 10 / ODOO-345813 (заказ Игоря: окна остановок регламента): чипс шапки стола «Regimen paused until …» — янтарный (идёт окно остановки регламента) */
.chip.chip-schedule-paused {
    background: #ffe3b0;
    box-shadow: inset 0 0 0 1px rgba(214, 138, 0, .55);
}
.chip.chip-schedule-paused .chip-icon { background: rgba(214, 138, 0, .85); }
.chip.chip-schedule-paused .chip-tooltip { white-space: normal; max-width: 420px; }   /* строка окна — «до …, режим, охват»: в форме переносится целиком, а не режется многоточием */

.chip-icon {
    background: rgba(119, 136, 153, 0.8);
    padding: 4px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    color: white;
    cursor: pointer;
    flex: none;
}

.chip-text {
    margin-left: 4px;
    min-width: 0;
    overflow: hidden; /* overflow нельзя на .chip — обрежет chip-hover-popup */
    text-overflow: ellipsis;
}
.chip-text :deep(.chip-title-left) {
    font-weight: 600;
    font-stretch: ultra-condensed;
}
.chip-text :deep(.chip-title-condition) {
    opacity: 0.5;
    font-size: 0.85em;
}
.chip-text :deep(.chip-title-count) {
    opacity: 0.5;
    font-size: 0.85em;
}
.chip-text :deep(.chip-title-rest) {
    opacity: 0.5;
}

.chip-badge {
    position: absolute;
    top: -7px;
    right: -7px;
    background-color: var(--cs-accent, rgba(100, 119, 239, 0.6));
    color: #ffffff;
    border-radius: 12px;
    padding: 1px 4px;
    font-size: 7px;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;    
}
/* IE 2026 10 05 / ODOO-5800 (fe-86): хвост счётчика чипса метрик «+N» — ячейки сигнала в строках выключенных опций отчёта; в обход ‹ › они не входят.
   Доработка по ревью: белый с opacity 0.65 на акценте бейджа давал контраст около 1,7 : 1 при 7px — цвет и размер теперь как у самого счётчика
   (наследуются, при любом --cs-accent), вторичность — обычным начертанием рядом с жирным счётчиком, а не прозрачностью */
/* IE 2026 10 06 / ODOO-5800: хвост «+N» темнее (слово Игоря 06.10) — свой фон: тот же акцент бейджа, затемнённый чёрным 50%, текст белый.
   Контраст WCAG текста хвоста к его фону ≥ 4,5 : 1 во всех гаммах, светлая / тёмная (invert) тема: classic 7,09 / 8,81, самая светлая style7w 4,75 / 6,84
   (было: белый прямо на акценте — 2,14 : 1 в classic, 1,25 в style7w); начертание обычное, как было */
.chip-badge-more {
    margin-left: 2px;
    font-weight: normal;
    background-color: rgba(0, 0, 0, 0.5);   /* IE 2026 10 06 / ODOO-5800 */
    border-radius: 6px;                      /* IE 2026 10 06 / ODOO-5800 */
    padding: 0 2px;                          /* IE 2026 10 06 / ODOO-5800 */
}

.chip-remove {
    background: transparent;
    border: none;
    cursor: pointer;
    margin: 4px;
    opacity: 0.6;
    flex: none;
}

.chip-remove:hover {
    color: #d32f2f;
}

/* IE 2026 09 15 / ODOO-… (заказ Игоря): форма чипса периода — границы с … по */
.chip-period { display: flex; gap: 10px; align-items: center; padding: 6px 8px; }
.chip-period-lbl { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #556; white-space: nowrap; }
.chip-period-input { font: inherit; font-size: 12px; padding: 2px 4px; border: 1px solid #c9cfda; border-radius: 4px; background: #fff; width: auto; }
.chip-nav {
    cursor: pointer;
    padding: 0 3px;
    opacity: 0.7;
    font-size: 11px;
    flex: none;
}
.chip-nav:hover {
    opacity: 1;
    color: var(--primary-color, #2196f3);
}
.chip-nav-disabled {
    opacity: 0.25;
    pointer-events: none;
}
.chip-title-page {
    font-weight: 600;
}
.chip-title-of {
    opacity: 0.65;
    font-size: 11px;
}

.chip-input {
    border: none;
    outline: none;
    font-size: 14px;
    padding: 2px;
    min-width: 80px;
    background: transparent;
}
/* IE 2026 11 02 / ODOO-350449 (заказ Игоря: p194 глобальна форма чіпів — поле пошуку за всіма селекторами, копіювання чіпа в інший стіл): поле поиска полосы стола и его список */
.chips-input.chips-bar-input { flex: 1 1 8rem; min-width: 8rem; background: transparent; font-size: 13px; color: var(--text-color, #495057); }
/* IE 2026 11 04 / ODOO-350449 (заказ Игоря: верхняя полоса в один ряд — иконки вместо подписей, «⋯» вместо поиска): «⋯» — узкое поле (полоса чипсов остаётся в один ряд); в фокусе и с текстом — прежняя ширина. Флаг-класс — через :where() (вес селектора прежний) */
.chips-input.chips-bar-input:where(.chips-bar-dots) { flex: 0 0 auto; width: 2rem; min-width: 2rem; font-size: 15px; font-weight: 700; letter-spacing: 1px; cursor: pointer; }
.chips-input.chips-bar-input:where(.chips-bar-dots):focus, .chips-input.chips-bar-input:where(.chips-bar-typed) { flex: 1 1 8rem; width: auto; min-width: 8rem; font-size: 13px; font-weight: 400; letter-spacing: 0; cursor: text; }
.chips-dropdown.chips-bar-list { flex-direction: column; padding: 4px; max-height: 420px; }
.chips-bar-group { display: flex; align-items: center; gap: 6px; padding: 5px 6px 2px; font-size: 12px; font-weight: 600; color: var(--text-color-secondary, #6c757d); white-space: nowrap; }
.chips-bar-group .pi { font-size: 12px; }
.chips-bar-tier { font-weight: 400; font-style: italic; overflow: hidden; text-overflow: ellipsis; }
.chips-bar-item { padding-left: 14px; font-size: 13px; }
.chips-bar-item.chips-bar-active { background: #e8eefc; }
.chips-input {
    border: none;
    outline: none;
    font-size: 14px;
    flex: 1 1 50px;
    min-width: 50px;
  }

.dropdown-button {
    display: flex;
    align-items: center;
    font-size: 16px;
    padding: 8px;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
}

.dropdown-button i {
    color: #ccc;
}

.chips-container .collection {
    background: rgba(142, 160, 255, 0.75);
    color: white;
}
.chips-container .segment {
    background: rgba(142, 160, 255, 0.75);
    color: white;
}
.chips-container .widget {
    background: rgba(204, 153, 51, 0.75);
    color: white;
}
.chips-container .status {
    background: rgba(244, 67, 54, 0.75);
    color: white;
}

.chip:hover {
    background: rgb(240 240 240 / 75%);
    color: var(--text-color);
}

.chip-selected {
    background: rgb(173 210 255 / 75%);
    outline: 2px solid var(--primary-color, #2196f3);
}

.chip-drag-over {
    border-left: 3px solid var(--primary-color, #2196f3);
}

.chip-tooltip-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.chip-tooltip-index {
    font-size: 11px;
    color: var(--text-color-secondary, #999);
}
.chip-tooltip-actions.has-content {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid var(--surface-border, #dee2e6);
}
.chip-copy-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 3px;
    color: var(--text-color-secondary, #666);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
}
.chip-copy-btn:hover {
    background: var(--surface-hover, #e9ecef);
    color: var(--primary-color, #2196f3);
}

/* IE 2026 08 28 / ODOO-5828: бейдж «вне периметра расчёта» */
.chip-badge-warn {
    background: #f59e0b; color: #fff; font-weight: 600;
    border-radius: 8px; padding: 0 4px; margin-left: 3px; font-size: .85em;
}
</style>

<style>
.collection {
    background: rgba(142, 160, 255, 0.75);
    color: white;
}
.segment {
    background: rgba(142, 160, 255, 0.75);
    color: white;
}
.widget {
    background: rgba(204, 153, 51, 0.75);
    color: white;
}
.status {
    background: rgba(244, 67, 54, 0.75);
    color: white;
}

.chip-hover-popup {
    position: absolute;
    top: 100%;
    left: 0;
    padding-top: 4px; /* не margin: зазор входит в hover-зону, меню не закрывается по пути */
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
}

.chip-menu {
    background: white;
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
    padding: 4px 0;
    min-width: 150px;
}
.chip-menu-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    cursor: pointer;
}
.chip-menu-item:hover {
    background-color: var(--surface-hover);
}
.chip-menu-item-active {
    background-color: var(--surface-hover);
    font-weight: 600;
}
/* IE 2026 10 27 / ODOO-5800 (заказ Игоря 27.10: ежедневный архив ПМ): меню модели чипса — подзаголовок, папка «Archive (N)», архивы с отступом, дата справа */
.chip-menu-sub { padding: 2px 12px 4px; font-size: 11px; text-transform: uppercase; letter-spacing: .03em; color: var(--text-color-secondary, #6c757d); }
.chip-menu-folder .chip-menu-text { font-weight: 600; }
.chip-menu-folder .pi-history, .chip-menu-depth1 .pi-history { color: #8a5a00; }
.chip-menu-depth1 { padding-left: 30px; }
.chip-menu-fold { width: 10px; font-size: 11px; opacity: .75; }
.chip-menu-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 360px; }
.chip-menu-aside { margin-left: auto; padding-left: 12px; font-size: 12px; color: var(--text-color-secondary, #6c757d); font-variant-numeric: tabular-nums; white-space: nowrap; }
.chip-check-row {
    /* строка внутри общего блока .chip-tooltip (padding 6px 10px): разделитель
       во всю ширину через отрицательные margin'ы, свой фон/рамка не нужны */
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px -10px 0;
    padding: 6px 10px 0;
    border-top: 1px solid var(--surface-border);
    cursor: pointer;
    white-space: nowrap;
    font-size: 12px;
}
/* история значений чипса: продолжение того же блока тултипа, отделена линией */
.chip-history {
    border-top: 1px solid #e3e3e3;
    margin-top: 4px;
    padding-top: 4px;
}
.chip-history-title {
    font-size: 11px;
    color: #90a4ae;
    padding: 1px 6px 2px;
    text-transform: uppercase;
    letter-spacing: .04em;
}
.chip-history-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 6px;
    cursor: pointer;
    border-radius: 3px;
    white-space: nowrap;
}
.chip-history-item:hover {
    background: #f0f4f8;
}
.chip-history-item .pi-history {
    font-size: 11px;
    color: #90a4ae;
}
.chip-history-current {
    opacity: .75;
}
/* подбор значения чипса (Pick value) */
.chip-pick {
    border-top: 1px solid #e3e3e3;
    margin-top: 4px;
    padding-top: 5px;
}
.chip-pick-input-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 6px;
}
.chip-pick-input-row .pi-search {
    font-size: 11px;
    color: #90a4ae;
}
.chip-pick-input {
    flex: 1;
    border: 1px solid #dfe3e8;
    border-radius: 4px;
    padding: 3px 7px;
    font-size: 12px;
    outline: none;
    min-width: 210px;
}
.chip-pick-input:focus {
    border-color: #9da9f6;
}
.chip-pick-list {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 3px;
}
.chip-pick-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 6px;
    cursor: pointer;
    border-radius: 3px;
    white-space: nowrap;
}
.chip-pick-item:hover {
    background: #f0f4f8;
}
/* IE 2026 09 14 / ODOO-… (заказ Игоря: открыть элемент из формы чипса): иконка сегмента — открыть элемент; приглушена, на наведении — акцент */
.chip-pick-item .chip-item-open, .chip-value-row .chip-item-open, .chip-history-item .chip-item-open {
    flex: none; width: 16px; margin: 0 6px 0 2px; opacity: .55; cursor: pointer; font-size: 13px;
}
.chip-pick-item .chip-item-open:hover, .chip-value-row .chip-item-open:hover, .chip-history-item .chip-item-open:hover {
    opacity: 1; color: var(--primary-color);
}
.chip-value-single { cursor: default; padding-left: 2px; }
/* IE 2026 09 14 / ODOO-… (заказ Игоря: варианты страниц и фильтров стола): панель вариантов в табе Commands */
.dropdown-tab:has(.chip-variant-row) { min-width: 330px; }
.chip-variant-head { display: flex; justify-content: space-between; align-items: center; padding-right: 10px; }
/* IE 2026 09 19 / ODOO-… (замечание Игоря): команды группы — кнопки-пиктограммы в строке заголовка */
.chip-cmd-head { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.chip-cmd-buttons { display: inline-flex; align-items: center; gap: 1px; }
.chip-cmd-btn { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 22px; padding: 0; border: none; border-radius: 4px; background: transparent; color: var(--text-color, #495057); cursor: pointer; }
.chip-cmd-btn .pi { font-size: 13px; }
.chip-cmd-btn:hover { background: var(--surface-hover, #e9ecef); color: var(--primary-color); }
.chip-cmd-btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: -2px; }
/* IE 2026 09 26 / ODOO-… (замечание Игоря: «+» на новой странице): недоступная кнопка группы (⚙ временной страницы) — приглушена, без подсветки по наведению */
.chip-cmd-btn.is-disabled { opacity: .38; cursor: not-allowed; }
.chip-cmd-btn.is-disabled:hover { background: transparent; color: var(--text-color, #495057); }
.chip-cmd-gap { display: inline-block; width: 9px; height: 14px; margin-left: 4px; border-left: 1px solid var(--surface-border, #dee2e6); }
.chip-variant-cols { font-size: 10px; letter-spacing: .04em; opacity: .65; }
.chip-variant-row { display: flex; align-items: center; gap: 6px; }
.chip-variant-row .chip-variant-title { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chip-variant-row.chip-variant-current .chip-variant-title { font-weight: 600; }
.chip-variant-row .chip-variant-flag { flex: none; width: 18px; text-align: center; opacity: .75; cursor: pointer; }
.chip-variant-row .chip-variant-flag:hover { opacity: 1; color: var(--primary-color); }
.chip-variant-row .chip-variant-flag-off { opacity: .3; cursor: default; }
/* IE 2026 09 19 / ODOO-…: корзина строки варианта — по наведению на строку (на тач-экранах видна всегда) */
.chip-variant-row .chip-variant-del { flex: none; width: 18px; margin-right: 6px; text-align: center; opacity: 0; cursor: pointer; transition: opacity .12s; }
.chip-variant-row:hover .chip-variant-del { opacity: .55; }
.chip-variant-row .chip-variant-del:hover { opacity: 1; color: #c62828; }
@media (hover: none) { .chip-variant-row .chip-variant-del { opacity: .55; } }
.chip-pick-item .pi {
    font-size: 11px;
    color: #90a4ae;
}
.chip-pick-item .pi-check {
    color: #2e7d32;
}
/* IE 2026 09 09 / ODOO-5800: чекбокс значения в подборе и истории — кликабельная зона побольше, отмеченный в цвете */
.chip-value-row .chip-value-check,
.chip-pick-item .chip-value-check,
.chip-history-item .chip-value-check {
    font-size: 12px;
    padding: 2px 3px;
    margin: -2px -3px;
    border-radius: 3px;
    color: #90a4ae;
}
.chip-value-row .chip-value-check:hover,
.chip-pick-item .chip-value-check:hover,
.chip-history-item .chip-value-check:hover {
    background: #e3e8ee;
}
.chip-pick-item .chip-value-check.pi-check-square,
.chip-history-item .chip-value-check.pi-check-square {
    color: var(--primary-color, #7b1fa2);
}
/* выбранное значение остаётся кликабельным: чекбокс снимает его из отбора */
.chip-pick-current {
    opacity: .75;
}
.chip-pick-conv { padding: 3px 8px; font-size: 11px; color: var(--text-color-secondary, #6b7280); font-style: italic; }   /* IE 2026 09 10 / ODOO-… */
/* IE 2026 09 30 / ODOO-5800: поиск расширен на подсегмент грани — строка над списком, переносится по ширине формы */
.chip-pick-wide { padding: 3px 8px 4px; font-size: 11px; line-height: 1.35; color: #0e7490; font-style: italic; white-space: normal; cursor: help; border-bottom: 1px dashed #a5d8e4; margin-bottom: 2px; }
.chip-pick-wide .pi { font-size: 11px; margin-right: 4px; vertical-align: -1px; }
.chip-pick-alt { display: block; color: var(--text-color-secondary, #6b7280); font-size: 11px; font-style: italic; overflow: hidden; text-overflow: ellipsis; }   /* IE 2026 09 25 / ODOO-341407: найдено по названию на другом языке — второй строкой */
/* IE 2026 09 12 / ODOO-… (заказ Игоря): приближённые совпадения (опечатки) — особая подсветка и подпись */
.chip-pick-conv-fuzzy { color: #b45309; }
/* IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): подпись последней ступени длинная (текст запроса + причина), причина стоит в конце —
   строка переносится по ширине формы (как .chip-pick-wide), а не обрезается её краем */
.chip-pick-conv-extra { white-space: normal; line-height: 1.35; }
.chip-pick-item b.chip-pick-fuzzy { background: #fde68a; border-bottom: 1px dashed #b45309; border-radius: 2px; }
.chip-fuzzy { margin: 0 2px; font-weight: 700; color: #b45309; cursor: help; }
/* IE 2026 09 21 / ODOO-… (замечание Игоря: фраза поиска целиком главнее частей) */
.chip-phrase { margin: 0 2px; font-weight: 700; color: #64748b; cursor: help; letter-spacing: -1px; }
/* IE 2026 09 25 / ODOO-… (замечание Игоря: поиск в таблице по названиям на других языках): значок языка на чипсе и подпись в форме */
.chip-alt { margin: 0 2px; font-weight: 700; font-size: 10px; color: #0e7490; cursor: help; white-space: nowrap; }
.chip-alt .pi { font-size: 10px; margin-right: 1px; vertical-align: -1px; }
.chip-alt-note { padding: 3px 8px; font-size: 11px; color: #0e7490; font-style: italic; }
.chip-alt-note .pi { font-size: 11px; }
.chip-pick-empty {
    padding: 3px 8px;
    color: #90a4ae;
    font-size: 12px;
}
.chip-check-row:hover {
    background-color: var(--surface-hover);
}
.chip-check-row .pi-check-square {
    color: #2563eb;
}
/* IE 2026 09 19 / ODOO-…: бейдж «Current» строки варианта панели Commands — та же пилюля, что в списках настроек виджета
   (.cell-badge.cell-badge-current грида) */
.chip-variant-badge {
    display: inline-flex; align-items: center; flex: 0 0 auto; margin: 0 6px 0 0; padding: 0 6px; border-radius: 10px;
    font-size: 10px; font-weight: 600; letter-spacing: .2px; line-height: 14px; white-space: nowrap;
    background: #e3f1ff; color: #1d5fa8; border: 1px solid #9cc5f0;
}
/* IE 2026 09 19 / ODOO-5800 (пункт 3 Игоря: чипсы таблицы на входе выборки): переключатель места действия чипса (Table | Input), значок входного
   чипса, предупреждения бэка */
.chip-place-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 6px -10px 0;
    padding: 6px 10px 0;
    border-top: 1px solid var(--surface-border);
    white-space: nowrap;
    font-size: 12px;
}
.chip-place-label { color: #64748b; margin-right: 4px; }
.chip-place-opt {
    padding: 1px 8px;
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    cursor: pointer;
    color: #334155;
    user-select: none;
}
.chip-place-opt:hover { background-color: var(--surface-hover); }
.chip-place-opt-on, .chip-place-opt-on:hover { background-color: #2563eb; border-color: #2563eb; color: #fff; cursor: default; }
.chip-place-ico { font-size: 10px; margin: 0 2px; color: #2563eb; }
.chip-place-notes { margin-top: 6px; max-width: 360px; white-space: normal; font-size: 12px; color: #b45309; }
.chip-place-notes .pi { font-size: 11px; }

.chip-tooltip {
    background: white;
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
    padding: 6px 10px;
    min-width: 150px;
    max-width: 350px;
    font-size: 13px;
    white-space: nowrap;
}
.chip-tooltip-header {
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--text-color);
}
.chip-tooltip-condition {
    font-weight: 400;
    opacity: 0.6;
}
.chip-tooltip-count {
    font-weight: 400;
    opacity: 0.5;
}
/* IE 2026 10 10 / ODOO-345618 (заказ Игоря: согласование под ключ) — FE1-kcss FE1: ссылка в подсказке чипса согласования */
.appr-chip-link { color: #3558c7; cursor: pointer; }
.appr-chip-link:hover { text-decoration: underline; }
.chip-tooltip-row {
    padding: 1px 0;
    color: var(--text-color-secondary, #666);
}
/* IE 2026 09 09 / ODOO-5800: строка значения чипса с чекбоксом (снятие флага = убрать значение из отбора) */
.chip-value-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    cursor: pointer;
    white-space: nowrap;
    color: var(--text-color-secondary, #666);
}
.chip-value-row:hover {
    color: var(--text-color, #333);
}
.chip-value-row .pi-check-square {
    color: var(--primary-color, #7b1fa2);
}
.chip-value-row.chip-value-off {
    opacity: 0.55;
}
/* IE 2026 09 14 / ODOO-5800 (замечание Игоря): названия в форме под чипсом обрезаются многоточием по ширине формы
   (min-width: 0 — иначе flex-элемент не ужимается); полный текст — «продолжение» при наведении (chipTip_over) */
.chip-tooltip-clip .chip-clip, .chip-tooltip-clip .chip-tooltip-row { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.chip-tooltip-clip .chip-clip { flex: 1 1 auto; }
/* оформление слоя «продолжения» — .clip-overlay в App.vue (IE 2026 09 18) */
/* IE 2026 09 18 / ODOO-5800 (заказ Игоря «чипс-метрик»): форма чипса метрик */
.chip-tooltip.chip-tooltip-metric { min-width: 420px; max-width: 560px; }
.chip-metric-head { display: flex; align-items: baseline; gap: 8px; padding: 1px 6px 3px; }
.chip-metric-hint { font-size: 11px; color: #90a4ae; white-space: nowrap; }
.chip-metric-hint-row { padding: 0 6px 4px; white-space: normal; }
.chip-metric-swatch { flex: 0 0 auto; display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 5px; box-shadow: 0 0 0 1px rgba(0, 0, 0, .18); }
.chip-metric-kind { flex: 0 0 auto; margin-left: 8px; font-size: 11px; color: #90a4ae; white-space: nowrap; }
.chip-metric-rows { flex: 0 0 auto; margin-left: 8px; font-size: 11px; color: #607d8b; font-variant-numeric: tabular-nums; }
.chip-metric-list { max-height: 300px; }
.chip-metric-group { margin-top: 4px; }
.chip-metric-list .chip-pick-item b { font-weight: 700; }
</style>
